import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { runtimeWsUrl, runtimeHttpUrl } from "../lib/runtime-url.js";
import ChainBuilderTab from "./ChainBuilderTab.jsx";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  Tabs,
  StatCard,
  VersionBadge,
  StatusBadge,
  RedactedField,
  formatCost,
  formatDate,
} from "../components.jsx";
import {
  VOICE_MODEL_OPTIONS,
  TEXT_MODEL_OPTIONS,
  VOICE_NAME_OPTIONS,
  PROVIDERS,
  voicesForChain,
  SAFETY_LEVEL_OPTIONS,
  LANGUAGE_OPTIONS,
  CAPABILITY_DEFS,
  DEFAULT_LLM_CONFIG,
  DEFAULT_SYSTEM_TOOLS,
  DEFAULT_VARIABLES,
  VARIABLE_DEFINITIONS,
  CARTESIA_VOICE_OPTIONS,
  CARTESIA_TTS_MODELS,
  CARTESIA_STT_MODELS,
} from "./tenant-config-constants.js";
import { useSubTenantEnv } from "./SubTenantEnvContext.jsx";
import { computeDiff, diffStats } from "./diff-utils.js";
import AgentToolGatesEditor from "../components/AgentToolGatesEditor.jsx";
import { AuthoredView, CompiledView } from "../components/prompt-builder.jsx";
import GlobalRulesEditor from "../components/global-rules-editor.jsx";

// Tabs shown differ by environment: Draft gets the editable Revisions
// tab; Live doesn't (all content is read-only, no pending changes).
// Mirrors the library specialty page layout: Overview (metadata +
// agent list), Config, operational tabs, and Revisions (draft only).
// The separate "Agents" and "Agent Configs" tabs are gone — agents
// are listed directly in the Overview tab, each clickable into a
// detail page, exactly like the library specialty page.
// Orchestration tab removed — chain mode runs the orchestrator in code
// (configural-runtime/src/providers/chain.js); the prompt-driven orchestrator
// agent is no longer used. See chain.js:464 (`if (key === "orchestrator") continue`).
const DRAFT_TABS = [
  { id: "overview", label: "Overview" },
  { id: "config", label: "Config" },
  { id: "global-rules", label: "Global Rules" },
  { id: "post-call", label: "Post-Call Analysis" },
  { id: "testing", label: "Testing" },
  { id: "call-log", label: "Call Log" },
  { id: "outbound-campaigns", label: "Outbound", outboundOnly: true },
  { id: "cost", label: "Cost + Usage" },
  { id: "credentials", label: "LLM Credentials" },
  { id: "secrets", label: "Secrets" },
  { id: "revisions", label: "Revisions" },
];

const LIVE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "config", label: "Config" },
  { id: "global-rules", label: "Global Rules" },
  { id: "post-call", label: "Post-Call Analysis" },
  { id: "testing", label: "Testing" },
  { id: "call-log", label: "Call Log" },
  { id: "outbound-campaigns", label: "Outbound", outboundOnly: true },
  { id: "cost", label: "Cost + Usage" },
  { id: "credentials", label: "LLM Credentials" },
  { id: "secrets", label: "Secrets" },
];

export default function SubTenantDetail() {
  const { tid, sid } = useParams();
  const { env, isDraft, isLive: isLiveEnv } = useSubTenantEnv();
  const [tab, setTab] = useState("overview");
  const [sub, setSub] = useState(null);
  // GitHub-managed: editors are read-only but we stay on draft view (NOT redirected to live)
  // isLive controls read-only state for editors. isLiveEnv is the actual URL environment.
  const isGitHubManaged = sub?.github_managed && isDraft;
  const isLive = isLiveEnv || isGitHubManaged;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [providerType, setProviderType] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [subRes, draftsRes] = await Promise.all([
          api.getSubTenant(tid, sid),
          isDraft
            ? api.listSubTenantDrafts(tid, sid).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        setSub(subRes.data);
        setDraftCount((draftsRes.data || []).length);
        // Check provider type for conditional Chain tab
        try {
          const cfgRes = await api.getSubTenantConfig(tid, sid, isDraft ? "draft" : "live");
          const llm = cfgRes?.data?.content?.llm_config;
          setProviderType(llm?.provider || "gemini");
        } catch { setProviderType("gemini"); }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid, isDraft]);

  // Pick the right tab set for the environment. Draft gets Revisions
  // with a count badge; Live hides it entirely.
  // Use isLiveEnv (not isLive) for tab selection — GitHub-managed should still show draft tabs (including Revisions)
  const baseTabs = isLiveEnv ? LIVE_TABS : DRAFT_TABS;
  const outboundEnabled = Array.isArray(sub?.enabled_modes)
    ? sub.enabled_modes.includes("outbound")
    : false;
  let tabs = baseTabs
    .filter((t) => !t.outboundOnly || outboundEnabled)
    .map((t) =>
      t.id === "revisions" && draftCount > 0
        ? { ...t, label: `Revisions (${draftCount})` }
        : t
    );
  // Chain tab is now a subtab of Config — no separate top-level tab needed

  // If the user is on a tab that doesn't exist in the current env
  // (e.g. switching from Draft/Revisions to Live), reset to overview.
  const activeTab = tabs.find((t) => t.id === tab) ? tab : "overview";

  // Build the env-relative prefix for sub-tenant links inside tabs
  // (e.g. prompt editor links need the /draft or /live segment).
  const envPrefix = `/tenants/${tid}/sub-tenants/${sid}/${env}`;

  return (
    <>
      <PageHeader
        title={sub?.name || sid}
        subtitle={
          sub
            ? `${sub.specialty} sub-tenant · ${isLiveEnv ? "Live" : "Draft"}`
            : ""
        }
        breadcrumbs={[
          { label: "Tenants", to: "/tenants" },
          { label: tid, to: `/tenants/${tid}` },
          { label: sub?.display_name || sid },
          { label: isLiveEnv ? "Live" : "Draft" },
        ]}
      />

      {/* GitHub-managed banner */}
      {sub?.github_managed && !isLiveEnv && (
        <div style={{
          padding: "10px 16px", background: "#f3e8ff", borderBottom: "2px solid #d1b3ff",
          fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <strong style={{ color: "#6f42c1" }}>Managed by GitHub</strong>
            {" "}— agent prompts, orchestration, post-call actions and agent configs are read-only.
            LLM / voice / telephony / variables stay editable here.
            Edit locked content on GitHub and pull from <a href="/settings/github">Settings → GitHub Sync</a>.
          </div>
          <a href="/settings/github" className="btn btn-sm" style={{ fontSize: 11 }}>GitHub Settings</a>
        </div>
      )}

      {/* Environment banner — only for actual live env, not GitHub-managed draft */}
      {isLiveEnv && (
        <div
          className="env-banner env-banner-live"
          style={{
            padding: "10px 16px",
            background: "#f0f7ff",
            borderBottom: "2px solid #c5dcff",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          <strong>Live environment</strong> — read-only. Switch to{" "}
          <Link to={`/tenants/${tid}/sub-tenants/${sid}/draft`}>
            Draft
          </Link>{" "}
          to make changes.
        </div>
      )}

      <Tabs tabs={tabs} active={activeTab} onChange={setTab} />

      <div className="tab-content">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : !sub ? (
          <div className="empty-state">
            <h3>Not found</h3>
          </div>
        ) : (
          <>
            {activeTab === "overview" && (
              <SubOverviewTab
                sub={sub}
                tid={tid}
                sid={sid}
                mode={env}
                envPrefix={envPrefix}
                isLive={isLive}
              />
            )}
            {activeTab === "config" && (
              <SubConfigTab
                tid={tid}
                sid={sid}
                mode={env}
                isLive={false}
                enabledModes={sub.enabled_modes || ["inbound"]}
              />
            )}
            {activeTab === "global-rules" && (
              <SubGlobalRulesTab tid={tid} sid={sid} />
            )}
            {activeTab === "post-call" && (
              <PostCallActionsTab
                tid={tid}
                sid={sid}
                mode={env}
                isLive={isLive}
              />
            )}
            {activeTab === "testing" && (
              <TestingTab tid={tid} sid={sid} configMode={env} />
            )}
            {activeTab === "call-log" && (
              <CallLogTab tid={tid} sid={sid} mode={env} />
            )}
            {activeTab === "outbound-campaigns" && (
              <OutboundCampaignsTab tid={tid} sid={sid} mode={env} isLive={isLive} />
            )}
            {activeTab === "cost" && <SubCostTab tid={tid} sid={sid} />}
            {activeTab === "credentials" && (
              <SubCredentialsTab tid={tid} sid={sid} />
            )}
            {activeTab === "secrets" && (
              <SubSecretsTab tid={tid} sid={sid} />
            )}
            {activeTab === "revisions" && isDraft && (
              <SubDraftsTab tid={tid} sid={sid} />
            )}
          </>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SubGlobalRulesTab — per-sub-tenant override of the Universal +
// Framework rule banks ("global rules"). Mirrors the platform Library
// page (LibraryGlobalRules) but the resolution flows from sub-tenant →
// platform → in-code, so each scope shows where the current rules came
// from. "Reset to platform default" deletes the sub-tenant override.
// ─────────────────────────────────────────────────────────────────────
const GLOBAL_RULE_SCOPES_UI = [
  { id: "universal",          label: "Universal",          accent: "#1565c0" },
  { id: "framework-inbound",  label: "Framework — Inbound", accent: "#7b1fa2" },
  { id: "framework-outbound", label: "Framework — Outbound", accent: "#a5824a" },
];

function SubGlobalRulesTab({ tid, sid }) {
  const [activeScope, setActiveScope] = useState("universal");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getSubTenantGlobalRules(tid, sid, activeScope);
      setData(res.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid, activeScope]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (rules, commitMsg) => {
    await api.writeSubTenantGlobalRules(tid, sid, activeScope, rules, { commit_msg: commitMsg });
    await load();
  };

  const onResetToDefault = async () => {
    // For sub-tenants, "reset to default" means delete the override and
    // fall back to whatever the platform / in-code defaults provide.
    await api.resetSubTenantGlobalRules(tid, sid, activeScope);
    await load();
  };

  return (
    <div>
      <p className="muted small mb-16" style={{ lineHeight: 1.5 }}>
        Override the platform's Universal / Framework rule banks for this sub-tenant only.
        Resolution at runtime: <strong>sub-tenant override</strong> → <strong>platform live</strong> → <strong>in-code defaults</strong>.
        "Reset to platform" deletes this sub-tenant's override so the platform / in-code rules take over again.
      </p>

      {/* Scope tab strip */}
      <div className="card" style={{ padding: 0, marginBottom: 16 }}>
        <div style={{ display: "flex", borderBottom: "1px solid #e5e5e5" }}>
          {GLOBAL_RULE_SCOPES_UI.map((s) => {
            const active = s.id === activeScope;
            return (
              <button
                key={s.id}
                onClick={() => setActiveScope(s.id)}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  background: active ? "#fff" : "#f8f9fa",
                  border: "none",
                  borderBottom: active ? `3px solid ${s.accent}` : "3px solid transparent",
                  color: active ? s.accent : "#666",
                  fontWeight: active ? 700 : 500,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorBanner error={error} />
      ) : data ? (
        <GlobalRulesEditor
          scope={activeScope}
          initialRules={data.rules}
          source={data.source}
          version={data.version}
          onSave={onSave}
          onResetToDefault={data.source === "sub_tenant" ? onResetToDefault : null}
          resetButtonLabel="Reset to platform default"
          context="sub_tenant"
        />
      ) : null}
    </div>
  );
}

// Overview mirrors the library specialty page: metadata at top, then
// the agent list. Each agent row is clickable into the prompt editor.
// Agents show "Inherited" (from library live) or "Overridden" status.
function SubOverviewTab({ sub, tid, sid, mode = "draft", envPrefix, isLive }) {
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [agentConfigs, setAgentConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [libraryAgents, setLibraryAgents] = useState([]);
  // Agent contract validator: "Validate Agents" button in the Agents card.
  // Modal shows per-agent errors / warnings per docs/agent-contract.md §7.
  const [validateBusy, setValidateBusy] = useState(false);
  const [validateResult, setValidateResult] = useState(null); // { results, summary, mode } | null
  const [validateError, setValidateError] = useState(null);
  const [importBusy, setImportBusy] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [promptsRes, configsRes] = await Promise.all([
          api.listSubTenantPrompts(tid, sid, mode),
          api.listAgentConfigs(tid, sid, mode).catch(() => ({ data: [] })),
        ]);
        // Filter out the orchestrator — it's managed separately via
        // the Orchestration tab / Config, not as a regular agent. This
        // mirrors the library specialty page which also hides it.
        setAgents(
          (promptsRes.data || [])
            .filter((a) => a.key !== "orchestrator")
            .sort((a, b) => (a.key || "").localeCompare(b.key || ""))
        );
        setAgentConfigs(
          (configsRes.data || []).filter(
            (c) => c.agent_id !== "orchestrator"
          )
        );
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid, mode]);

  // Build a config lookup by agent_id for quick column access
  const cfgByAgent = {};
  for (const c of agentConfigs) {
    cfgByAgent[c.agent_id] = c.content || {};
  }

  return (
    <>
      <div className="stat-grid">
        <StatCard label="Agent Group" value={sub.specialty} />
        <StatCard label="Agents" value={agents.length} />
        <StatCard label="Created" value={formatDate(sub.created_at)} />
      </div>

      <ErrorBanner error={error} />

      <div className="card">
        <div className="card-header">
          <h3>Metadata</h3>
        </div>
        <table className="table" style={{ border: "none" }}>
          <tbody>
            <tr>
              <th style={{ width: "30%" }}>Sub-tenant ID</th>
              <td className="mono">{sub._id}</td>
            </tr>
            <tr>
              <th>Agent Group</th>
              <td>
                <Link to={`/library/specialties/${sub.specialty}`}>
                  {sub.specialty}
                </Link>
                <span className="muted small" style={{ marginLeft: 8 }}>
                  (inherits from library live)
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Calling Modes — inbound always on, outbound is a toggle */}
      <div className="card">
        <div className="card-header">
          <h3>Calling Modes</h3>
        </div>
        <div style={{ display: "flex", gap: 24, alignItems: "center", padding: "4px 0" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "not-allowed" }}>
            <input
              type="checkbox"
              checked={true}
              disabled
              readOnly
            />
            <span>Inbound</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: isLive ? "not-allowed" : "pointer" }}>
            <input
              type="checkbox"
              checked={Array.isArray(sub.enabled_modes) && sub.enabled_modes.includes("outbound")}
              disabled={isLive}
              onChange={async (e) => {
                const current = Array.isArray(sub.enabled_modes) ? sub.enabled_modes : ["inbound"];
                const set = new Set(current);
                set.add("inbound");
                if (e.target.checked) set.add("outbound");
                else set.delete("outbound");
                try {
                  await api.setSubTenantEnabledModes(tid, sid, [...set]);
                  window.location.reload();
                } catch (err) {
                  alert(err?.message || "Failed to update enabled modes");
                }
              }}
            />
            <span>Outbound</span>
          </label>
        </div>
        <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
          Outbound mode lets an external app (like your EHR) queue calls that Sandy places via Twilio.
          Enable it to unlock the Outbound Config tab and the Outbound Campaigns tab.
        </p>
      </div>

      {/* Agent list — mirrors the library specialty agent table */}
      <div className="card">
        <div
          className="card-header"
          style={{ justifyContent: "space-between" }}
        >
          <h3>
            Agents{" "}
            <span
              className="muted small"
              style={{ fontWeight: 400, marginLeft: 6 }}
            >
              {agents.length}
            </span>
          </h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-sm"
              disabled={validateBusy || agents.length === 0}
              onClick={async () => {
                setValidateBusy(true);
                setValidateError(null);
                setValidateResult(null);
                try {
                  const res = await api.validateSubTenantAgents(tid, sid, mode);
                  setValidateResult(res.data);
                } catch (err) {
                  setValidateError(err);
                } finally {
                  setValidateBusy(false);
                }
              }}
              title="Run the agent-contract validator (docs/agent-contract.md §7) against every agent in this sub-tenant."
            >
              {validateBusy ? "Validating..." : "✓ Validate Agents"}
            </button>
            {!isLive && (
              <button
                className="btn btn-sm"
                onClick={async () => {
                  // Load library agents for this specialty
                  try {
                    const res = await api.listSpecialtyAgents(sub.specialty, "live");
                    setLibraryAgents(res.data || []);
                  } catch {}
                  setShowImport(true);
                }}
              >
                + Import Agent
              </button>
            )}
          </div>
        </div>
        <p className="muted small mb-16">
          Agents inherited from the{" "}
          <Link to={`/library/specialties/${sub.specialty}`}>
            {sub.specialty}
          </Link>{" "}
          library group. Click an agent to edit its prompt.
          Use "Import Agent" to add more agents from the library.
        </p>
        {loading ? (
          <Loading />
        ) : agents.length === 0 ? (
          <div className="muted small" style={{ padding: "8px 0" }}>
            No agents found. The library agent group may be empty.
          </div>
        ) : (
          <table className="table" style={{ border: "none" }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th style={{ width: 160 }}>Modes</th>
                <th className="right" style={{ width: 70 }}>
                  Tools
                </th>
                <th className="right" style={{ width: 90 }}>
                  Protocols
                </th>
                <th style={{ width: 130 }}>Handoff</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 90 }}>Version</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const cfg = cfgByAgent[a.key] || {};
                const enabled =
                  sub.enabled_agents == null ||
                  sub.enabled_agents.includes(a.key);
                // An agent is "overridden" if it has a sub-tenant-owned
                // version > 1 (v1 = the initial fork from the wizard,
                // which is the library's content. v2+ = edited).
                const isOverridden = (a.version || 1) > 1;
                return (
                  <tr
                    key={a.key}
                    className="clickable-row"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      nav(
                        `${envPrefix}/prompts/${encodeURIComponent(a.key)}`
                      )
                    }
                  >
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: 6,
                            background: cfg.color || "#455A64",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {(cfg.icon && cfg.icon.trim()) ||
                            (cfg.name || a.key).charAt(0).toUpperCase()}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>
                            {cfg.name || a.key}
                          </div>
                          <div className="muted small mono">{a.key}</div>
                        </div>
                      </div>
                      {!enabled && (
                        <span
                          className="badge badge-neutral"
                          style={{ marginLeft: 8 }}
                        >
                          disabled
                        </span>
                      )}
                    </td>
                    <td className="muted small">
                      {cfg.type || "—"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const modes = Array.isArray(cfg.modes) && cfg.modes.length
                          ? cfg.modes
                          : ["inbound", "outbound"];
                        const isBoth = modes.includes("inbound") && modes.includes("outbound");
                        const pill = (m) => {
                          const on = modes.includes(m);
                          return (
                            <button
                              key={m}
                              type="button"
                              disabled={isLive}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const set = new Set(modes);
                                if (set.has(m)) set.delete(m);
                                else set.add(m);
                                // Require at least one mode
                                if (set.size === 0) return;
                                const newModes = [...set];
                                try {
                                  await api.writeAgentConfig(tid, sid, a.key, {
                                    mode,
                                    author: "admin-ui",
                                    commit_msg: `Set modes to ${newModes.join(",")}`,
                                    content: { ...cfg, modes: newModes },
                                  });
                                  const res = await api.listAgentConfigs(tid, sid, mode).catch(() => ({ data: [] }));
                                  setAgentConfigs((res.data || []).filter((c) => c.agent_id !== "orchestrator"));
                                } catch {}
                              }}
                              style={{
                                padding: "2px 8px",
                                marginRight: 4,
                                fontSize: 11,
                                borderRadius: 10,
                                border: "1px solid var(--color-border)",
                                background: on ? "var(--color-accent, #2E4F72)" : "#f4f4f4",
                                color: on ? "white" : "#888",
                                cursor: isLive ? "not-allowed" : "pointer",
                              }}
                            >
                              {m}
                            </button>
                          );
                        };
                        return (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {pill("inbound")}
                            {pill("outbound")}
                            {isBoth && <span className="muted small" style={{ marginLeft: 4 }}>both</span>}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="right">
                      <span className="mono small">
                        {(cfg.tools || []).length || "—"}
                      </span>
                    </td>
                    <td className="right">
                      <span className="mono small">
                        {(a.linked_protocols || cfg.linked_protocols || [])
                          .length || "—"}
                      </span>
                    </td>
                    <td>
                      <select
                        value={cfg.handoff_mode || "lazy"}
                        onClick={(e) => e.stopPropagation()}
                        onChange={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.writeAgentConfig(tid, sid, a.key, {
                              mode,
                              author: "admin-ui",
                              commit_msg: `Set handoff_mode to ${e.target.value}`,
                              content: { ...cfg, handoff_mode: e.target.value },
                            });
                            // Refresh agent configs
                            const res = await api.listAgentConfigs(tid, sid, mode).catch(() => ({ data: [] }));
                            setAgentConfigs((res.data || []).filter((c) => c.agent_id !== "orchestrator"));
                          } catch {}
                        }}
                        style={{
                          padding: "2px 6px", fontSize: 11, borderRadius: 4,
                          border: "1px solid var(--color-border)", background: "#fafafa",
                          cursor: "pointer", minWidth: 0,
                        }}
                        disabled={isLive}
                      >
                        <option value="preload">Preload</option>
                        <option value="lazy">Lazy</option>
                      </select>
                    </td>
                    <td>
                      {isOverridden ? (
                        <span className="badge badge-accent">
                          Overridden
                        </span>
                      ) : (
                        <span className="badge badge-neutral">
                          Inherited
                        </span>
                      )}
                    </td>
                    <td>
                      <VersionBadge version={a.version} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Built-in Agents (Chain Framework) ──────────────────────────
          First-class category alongside library agents. Each sub-tenant
          has a forked copy of the 9 platform chain framework prompts
          (greeter, verifier, intake, closer + outbound variants + the
          navigation templates). Editor is rendered inline via the
          BuiltInAgentsSection's internal editingKey state. */}
      <BuiltInAgentsSection
        tid={tid}
        sid={sid}
        env={mode}
        isDraft={!isLive}
        outboundEnabled={Array.isArray(sub?.enabled_modes) ? sub.enabled_modes.includes("outbound") : false}
      />

      {/* Handoff modes explanation */}
      <div className="card" style={{ marginTop: 0, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <div className="card-header">
          <h3>Agent Handoff Modes</h3>
        </div>
        <p className="muted small" style={{ marginBottom: 12 }}>
          Controls how each agent's instructions are delivered to the LLM during a voice call.
          You can mix modes within the same sub-tenant.
        </p>
        <table className="table" style={{ border: "none", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: "22%" }}>Mode</th>
              <th>When prompt loads</th>
              <th style={{ width: "18%" }}>Latency on route</th>
              <th style={{ width: "22%" }}>Best for</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Preload</strong></td>
              <td>
                At call start — full prompt embedded in <code>system_instruction</code> alongside the
                orchestrator. Always in context, no on-demand loading.
              </td>
              <td className="muted">Zero</td>
              <td className="muted small">
                Agents used on almost every call. Higher initial prompt cost but zero-latency routing.
              </td>
            </tr>
            <tr>
              <td><strong>Lazy</strong> <span className="badge badge-neutral" style={{ fontSize: 10 }}>default</span></td>
              <td>
                On every <code>route_to_agent</code> — full prompt injected into the tool response
                with a strong isolation preamble ("CRITICAL CONTEXT SWITCH: you are NOW this agent,
                DISREGARD all previous agent instructions"). Re-sent on every route to refresh
                the model's attention, preventing persona drift on long calls.
              </td>
              <td className="muted">~0 ms</td>
              <td className="muted small">
                Most agents. Token-efficient for single-intent calls. Strong isolation preamble gives
                near-session-restart fidelity without latency.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="muted small" style={{ marginTop: 12 }}>
          <strong>Tip:</strong> start with <strong>Lazy</strong> for all agents. Use <strong>Preload</strong>
          only for agents called on nearly every call — it increases the initial prompt size but
          eliminates any handoff overhead. Call <code>get_agent_instructions(agent_id)</code> mid-call
          if an agent seems to have lost its thread on a long conversation.
        </p>
      </div>

      {/* Validate Agents Modal — agent-contract conformance results */}
      {(validateResult || validateError) && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => { setValidateResult(null); setValidateError(null); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 8, padding: 24,
              minWidth: 640, maxWidth: 860, maxHeight: "85vh", overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0 }}>Agent Contract Validation</h3>
                <p className="muted small" style={{ margin: "4px 0 0" }}>
                  Results from checking each agent against <code>docs/agent-contract.md</code> §7 conformance rules.
                </p>
              </div>
              <button className="btn btn-sm" onClick={() => { setValidateResult(null); setValidateError(null); }}>Close</button>
            </div>

            {validateError && (
              <div style={{ padding: 12, background: "#FFEBEE", color: "#c62828", borderRadius: 4, marginBottom: 12 }}>
                Validation failed: {validateError.message || String(validateError)}
              </div>
            )}

            {validateResult && (
              <>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16,
                }}>
                  <StatCard label="Total" value={validateResult.summary.total} />
                  <StatCard label="Passing" value={validateResult.summary.passing} />
                  <StatCard label="Failing" value={validateResult.summary.failing} />
                  <StatCard label="With warnings" value={validateResult.summary.withWarnings} />
                </div>

                {validateResult.results.length === 0 && (
                  <div className="muted small" style={{ padding: 8 }}>
                    No agents to validate.
                  </div>
                )}

                {validateResult.results.map((r) => {
                  const badge = r.ok
                    ? { text: "✓ Conforms", color: "#2e7d32", bg: "#E8F5E9" }
                    : { text: "✗ Fails", color: "#c62828", bg: "#FFEBEE" };
                  const hasWarn = r.warnings.length > 0;
                  return (
                    <div
                      key={r.id}
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 8,
                        background: r.ok ? "#FAFCFA" : "#FFFAFA",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: hasWarn || !r.ok ? 8 : 0 }}>
                        <span
                          style={{
                            padding: "2px 8px", borderRadius: 4,
                            background: badge.bg, color: badge.color,
                            fontSize: 11, fontWeight: 700,
                          }}
                        >
                          {badge.text}
                        </span>
                        <strong style={{ fontSize: 14 }}>{r.id}</strong>
                        <span className="muted small" style={{ fontSize: 11 }}>
                          {r.direction}{r.isEntry ? " · entry agent" : ""}
                        </span>
                        {hasWarn && !r.ok === false && (
                          <span
                            style={{
                              marginLeft: "auto", fontSize: 10, color: "#8a6d00",
                              background: "#FFF8DC", padding: "2px 6px", borderRadius: 3,
                            }}
                          >
                            {r.warnings.length} warning{r.warnings.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>

                      {r.errors.length > 0 && (
                        <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, color: "#c62828" }}>
                          {r.errors.map((e, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <strong>{e.code}:</strong> {e.message}
                            </li>
                          ))}
                        </ul>
                      )}
                      {r.warnings.length > 0 && (
                        <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, color: "#8a6d00" }}>
                          {r.warnings.map((w, i) => (
                            <li key={i} style={{ marginBottom: 4 }}>
                              <strong>{w.code}:</strong> {w.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Import Agent Modal */}
      {showImport && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        onClick={() => setShowImport(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 8, padding: 24,
              minWidth: 450, maxWidth: 600, maxHeight: "70vh", overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 12 }}>Import Agent from Library</h3>
            <p className="muted small mb-16">
              Select an agent from the <strong>{sub.specialty}</strong> library group to add to this sub-tenant.
              Already-imported agents are excluded.
            </p>
            {(() => {
              const existingKeys = new Set(agents.map((a) => a.key));
              const available = libraryAgents.filter((la) => !existingKeys.has(la.key) && la.key !== "orchestrator");
              if (available.length === 0) {
                return <p className="muted">All library agents are already imported.</p>;
              }
              return available.map((la) => (
                <div
                  key={la.key}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 0", borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  <div>
                    <strong>{la.metadata?.name || la.key}</strong>
                    <div className="muted small mono">{la.key}</div>
                  </div>
                  <button
                    className="btn btn-sm primary"
                    disabled={!!importBusy[la.key]}
                    onClick={async () => {
                      setImportBusy((prev) => ({ ...prev, [la.key]: true }));
                      try {
                        await api.forkAgentToSubTenant(tid, sid, la.key);
                        // Reload agents
                        const [promptsRes, configsRes] = await Promise.all([
                          api.listSubTenantPrompts(tid, sid, mode),
                          api.listAgentConfigs(tid, sid, mode).catch(() => ({ data: [] })),
                        ]);
                        setAgents((promptsRes.data || []).filter((a) => a.key !== "orchestrator").sort((a, b) => (a.key || "").localeCompare(b.key || "")));
                        setAgentConfigs((configsRes.data || []).filter((c) => c.agent_id !== "orchestrator"));
                      } catch (err) {
                        alert(err.message || "Failed to import agent");
                      } finally {
                        setImportBusy((prev) => ({ ...prev, [la.key]: false }));
                      }
                    }}
                  >
                    {importBusy[la.key] ? "Importing..." : "Import"}
                  </button>
                </div>
              ));
            })()}
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button className="btn btn-sm" onClick={() => setShowImport(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PromptsTab({ tid, sid, sub, mode = "draft", envPrefix }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.listSubTenantPrompts(tid, sid, mode);
        setPrompts((res.data || []).sort((a, b) => a.key.localeCompare(b.key)));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid, mode]);

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  return (
    <>
      <div className="mb-16" style={{ display: "flex", justifyContent: "space-between" }}>
        <span className="muted small">
          {prompts.length} agent{prompts.length === 1 ? "" : "s"} · {mode} mode
        </span>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table className="table" style={{ border: "none" }}>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Version</th>
              <th>Status</th>
              <th>Seeded From</th>
              <th>Chars</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {prompts.length === 0 ? (
              <tr><td colSpan="6" className="table-empty">No prompts forked.</td></tr>
            ) : (
              prompts.map((p) => {
                const enabled =
                  sub.enabled_agents == null || sub.enabled_agents.includes(p.key);
                return (
                  <tr key={p.key}>
                    <td>
                      <Link to={`${envPrefix || `/tenants/${tid}/sub-tenants/${sid}/draft`}/prompts/${p.key}`}>
                        <strong>{p.key}</strong>
                      </Link>
                      {!enabled && (
                        <span className="badge badge-neutral" style={{ marginLeft: 8 }}>
                          disabled
                        </span>
                      )}
                    </td>
                    <td><VersionBadge version={p.version} /></td>
                    <td><StatusBadge doc={p} /></td>
                    <td className="mono small">
                      {p.seeded_from
                        ? `${p.seeded_from.specialty}/${p.seeded_from.key} v${p.seeded_from.version}`
                        : <span className="subtle">—</span>}
                    </td>
                    <td>{p.content?.length?.toLocaleString() ?? "—"}</td>
                    <td className="muted small">{formatDate(p.created_at)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Sub-tenant Config tab (full editable form) ─────────────────────
//
// Edits sub_tenant_config.content directly. Saves write a new version
// via PUT /api/tenants/:tid/sub-tenants/:sid/config. The form
// gracefully reads both the new shape (`llm_config`, `system_tools`)
// and the legacy override shape (`llm_config_overrides`,
// `system_tools_overrides`) — first save normalizes to the new shape.

const SHARED_FIELDS = new Set([
  "practice_name", "practice_phone", "practice_timezone",
  "practice_address", "practice_website", "patient_practice",
]);
function readField(content, key, dir) {
  if (!content) return undefined;
  if (SHARED_FIELDS.has(key)) return content?.shared?.[key] ?? content?.[key];
  return content?.[dir]?.[key] ?? content?.[key];
}
function pillBtn(active, color) {
  return {
    padding: "6px 14px",
    borderRadius: 4,
    border: `1px solid ${active ? color : "#D0D0D0"}`,
    background: active ? color : "#FFFFFF",
    color: active ? "#FFFFFF" : "#333",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: active ? 600 : 400,
  };
}

function SubConfigTab({ tid, sid, mode = "draft", isLive = false, enabledModes = ["inbound"] }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subTabDirection, setSubTabDirection] = React.useState("inbound");

  const [variables, setVariables] = useState(DEFAULT_VARIABLES);
  const [llmConfig, setLlmConfig] = useState(DEFAULT_LLM_CONFIG);
  const [systemTools, setSystemTools] = useState(DEFAULT_SYSTEM_TOOLS);
  const [dataServiceUrl, setDataServiceUrl] = useState("");
  const [toolBackends, setToolBackends] = useState({}); // { tool_name: "http://..." }
  // Outbound-only telephony config (account_sid, auth_token_secret_ref,
  // from_number, amd_enabled, voicemail_ok, practice_phone, practice_name).
  // Rendered on the Telephony tab when subTabDirection === "outbound".
  // Persisted at content.outbound.twilio.
  const [twilioOutbound, setTwilioOutbound] = useState({});
  const [availableTools, setAvailableTools] = useState([]); // [{ _id, name, category, kind }]
  const [subTenantTools, setSubTenantTools] = useState([]); // full tool docs used by this sub-tenant
  const [expandedTool, setExpandedTool] = useState(null); // which tool ID is expanded
  const [configSubTab, setConfigSubTab] = useState("llm"); // "llm" | "capabilities" | "integration" | "telephony"
  // One-shot migration for users who last saved configSubTab = "chain-prompts"
  // (the subtab removed in PR3). Snap them to "llm" so they don't see a
  // blank screen. Delete this effect after one release cycle.
  React.useEffect(() => {
    if (configSubTab === "chain-prompts") setConfigSubTab("llm");
  }, [configSubTab]);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedHash, setSavedHash] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setSaveError(null);
    try {
      const [res, toolsRes, agentConfigsRes] = await Promise.all([
        api.getSubTenantConfig(tid, sid, mode),
        api.listTools().catch(() => ({ data: [] })),
        api.listAgentConfigs(tid, sid, mode).catch(() => ({ data: [] })),
      ]);
      const allTools = toolsRes.data || [];
      // Filter to server-type tools only for the backend URL dropdown
      setAvailableTools(
        allTools.filter((t) => t.kind === "server" || !t.kind)
      );
      // Collect unique tool IDs used by this sub-tenant's agents
      const usedToolIds = new Set();
      for (const ac of (agentConfigsRes.data || [])) {
        for (const toolId of (ac.content?.tools || [])) usedToolIds.add(toolId);
      }
      // Build full tool reference from catalog, keyed by ID and name
      const toolMap = {};
      for (const t of allTools) {
        if (t._id) toolMap[t._id] = t;
        if (t.id) toolMap[t.id] = t;
        if (t.name) toolMap[t.name] = t;
      }
      setSubTenantTools(
        [...usedToolIds].map((id) => toolMap[id]).filter(Boolean)
          .sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.name || "").localeCompare(b.name || ""))
      );
      setCfg(res.data);
      const content = res.data?.content || {};
      const dir = subTabDirection;
      const v = { ...DEFAULT_VARIABLES, ...(readField(content, "variables", dir) || {}) };
      const l = {
        ...DEFAULT_LLM_CONFIG,
        ...(readField(content, "llm_config", dir) || readField(content, "llm_config_overrides", dir) || {}),
      };
      const s = {
        ...DEFAULT_SYSTEM_TOOLS,
        ...(readField(content, "system_tools", dir) || readField(content, "system_tools_overrides", dir) || {}),
      };
      const ds = readField(content, "data_service_url", dir) || "";
      const tb = readField(content, "tool_backends", dir) || {};
      // twilioOutbound is only used when we're editing the outbound block.
      // Falls back to empty object if nothing is set yet.
      const tw = (content.outbound && content.outbound.twilio) || {};
      setVariables(v);
      setLlmConfig(l);
      setSystemTools(s);
      setDataServiceUrl(ds);
      setToolBackends(tb);
      setTwilioOutbound(tw);
      setSavedHash(JSON.stringify({ v, l, s, ds, tb, tw }));
      setError(null);
    } catch (err) {
      // 404 is expected for a brand-new sub-tenant with no config doc
      // yet — render the form with defaults so the user can save and
      // create the first version.
      if (err?.status === 404) {
        setCfg(null);
        setVariables(DEFAULT_VARIABLES);
        setLlmConfig(DEFAULT_LLM_CONFIG);
        setSystemTools(DEFAULT_SYSTEM_TOOLS);
        setDataServiceUrl("");
        setToolBackends({});
        setSavedHash(
          JSON.stringify({
            v: DEFAULT_VARIABLES,
            l: DEFAULT_LLM_CONFIG,
            s: DEFAULT_SYSTEM_TOOLS,
            ds: "",
            tb: {},
          })
        );
        setError(null);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [tid, sid, mode, subTabDirection]);

  useEffect(() => {
    reload();
  }, [reload]);

  const dirty =
    savedHash != null &&
    JSON.stringify({ v: variables, l: llmConfig, s: systemTools, ds: dataServiceUrl, tb: toolBackends, tw: twilioOutbound }) !==
      savedHash;

  const updateVar = (patch) => setVariables((v) => ({ ...v, ...patch }));
  const updateLlm = (patch) => setLlmConfig((l) => ({ ...l, ...patch }));

  // Auto-migrate legacy tenants: provider is now implicitly "chain". If a
  // saved config still has provider="gemini"/"openai"/"pipeline" from the
  // pre-chain era, force it to "chain" + a sane chain voice_model so the
  // UI and runtime are consistent. Runs once per load when needed.
  useEffect(() => {
    if (!llmConfig) return;
    const validChainVoiceModels = new Set(["chain-gemini", "chain-gemini-3.1-flash-live", "chain-openai", "chain-pipeline"]);
    const needsProviderFix = llmConfig.provider !== "chain";
    const needsVoiceModelFix = !validChainVoiceModels.has(llmConfig.voice_model);
    if (needsProviderFix || needsVoiceModelFix) {
      updateLlm({
        provider: "chain",
        voice_model: needsVoiceModelFix ? "chain-gemini" : llmConfig.voice_model,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmConfig?.provider, llmConfig?.voice_model]);
  const toggleTool = (id) =>
    setSystemTools((s) => ({ ...s, [id]: !s[id] }));

  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      const prev = cfg?.content || {};
      const dir = subTabDirection;
      const perDirPatch = {
        variables,
        llm_config: llmConfig,
        system_tools: systemTools,
        data_service_url: dataServiceUrl || undefined,
        tool_backends: Object.keys(toolBackends).length > 0 ? toolBackends : undefined,
      };
      // When editing the outbound block, include the twilio config. It lives
      // at content.outbound.twilio and is used by the outbound worker.
      const outboundBlock = dir === "outbound"
        ? { ...(prev.outbound || {}), ...perDirPatch, twilio: twilioOutbound }
        : { ...(prev.outbound || {}) };

      await api.writeSubTenantConfig(tid, sid, {
        mode, // saves to whatever environment we're viewing (draft or live)
        author: "sub-tenant-detail",
        commit_msg: `Edit ${mode} config (${dir})`,
        content: {
          // Spread ALL existing fields first to preserve orchestration,
          // post_call_actions, and any other sections managed by other tabs
          ...prev,
          // Preserve shared and the OTHER direction block untouched
          shared: { ...(prev.shared || {}) },
          inbound: dir === "inbound"
            ? { ...(prev.inbound || {}), ...perDirPatch }
            : { ...(prev.inbound || {}) },
          outbound: outboundBlock,
          // Write-through to root for legacy readers during migration. Safe
          // to drop once all readers consume the split shape.
          ...perDirPatch,
        },
      });
      await reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !cfg && !savedHash) return <Loading />;
  if (error && !cfg) return <ErrorBanner error={error} />;

  return (
    <>
      <div
        className="mb-16"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          {cfg && (
            <span className="muted small">
              v{cfg.version}
              {cfg.commit_msg ? ` · ${cfg.commit_msg}` : ""}
            </span>
          )}
        </div>
        <button
          className="btn primary"
          onClick={save}
          disabled={!dirty || busy}
        >
          {busy ? "Saving..." : `Save to ${mode}`}
        </button>
      </div>

      <ErrorBanner error={saveError} />

      {/* Inbound/Outbound direction sub-tabs — only when outbound is enabled */}
      {enabledModes.includes("outbound") && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, padding: "8px 12px", background: "#FAFAFA", border: "1px solid #E0E0E0", borderRadius: 6 }}>
          <button
            onClick={() => setSubTabDirection("inbound")}
            style={pillBtn(subTabDirection === "inbound", "#4A6FA5")}
          >Inbound</button>
          <button
            onClick={() => setSubTabDirection("outbound")}
            style={pillBtn(subTabDirection === "outbound", "#A5824A")}
          >Outbound</button>
          <span className="muted small" style={{ marginLeft: "auto", alignSelf: "center", fontSize: 11 }}>
            Shared fields (practice name, phone, timezone) apply to both directions.
          </span>
        </div>
      )}

      {/* fieldset disabled natively disables all descendant inputs
          when in live mode — no need to add disabled to every field. */}
      {/* Sub-tab navigation */}
      <div className="tabs mb-16" style={{ borderBottom: "1px solid var(--color-border)" }}>
        {[
          { id: "llm", label: "LLM" },
          { id: "audio", label: "Audio" },
          { id: "variables", label: "Variables" },
          { id: "capabilities", label: "Capabilities" },
          { id: "integration", label: "Integration" },
          { id: "telephony", label: "Telephony" },
          { id: "chain", label: "View Chain" },
          // Chain Prompts subtab removed in PR3. Built-in agent editing
          // now lives inline under Agents → Built-in Agents.
        ].map((t) => (
          <button
            key={t.id}
            className={`tab ${configSubTab === t.id ? "active" : ""}`}
            onClick={() => setConfigSubTab(t.id)}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom: configSubTab === t.id ? "2px solid var(--color-primary)" : "2px solid transparent",
              cursor: "pointer",
              fontWeight: configSubTab === t.id ? 600 : 400,
              color: configSubTab === t.id ? "var(--color-primary)" : "var(--color-text-muted)",
              fontSize: 13,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <fieldset disabled={false} style={{ border: "none", padding: 0, margin: 0 }}>

      {configSubTab === "llm" && (
      <div className="card">
        <div className="card-header">
          <h3>LLM Configuration</h3>
        </div>

        {/* ── Provider & Models ──────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Provider & Models</h4>
            <span className="config-section-meta">Vendor, voice, and text models</span>
          </div>
          <div className="config-section-body">
        {/* Provider is implicitly "chain" — selector hidden by design.
            All deployments run sequential agents; the Voice Model dropdown
            below picks the audio backend within chain mode. */}

        {(() => {
          // Force chain provider on render — covers any legacy tenants whose
          // saved provider value is "gemini"/"openai"/"pipeline" from the
          // pre-chain era.
          const prov = PROVIDERS.chain;
          // Voice catalog depends on which audio backend the chain picked:
          // chain-gemini → Gemini voices, chain-openai → OpenAI voices,
          // chain-pipeline → Google Cloud TTS voices.
          const voiceCat = voicesForChain(llmConfig.voice_model);
          return (<>
            <div className="form-field">
              <label>Voice Model</label>
              <select
                value={llmConfig.voice_model}
                onChange={(e) => {
                  const next = e.target.value;
                  // Auto-swap voice if the current one isn't valid for the
                  // newly-selected audio backend (e.g. switching from
                  // chain-gemini "Aoede" to chain-pipeline needs a Cloud TTS
                  // voice like "en-US-Neural2-C").
                  const nextCat = voicesForChain(next);
                  const currentVoiceValid = nextCat.voices.some(v => v.id === llmConfig.voice);
                  const patch = { voice_model: next };
                  if (!currentVoiceValid) patch.voice = nextCat.defaultVoice;
                  updateLlm(patch);
                }}
              >
                {prov.voiceModels.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                The model used for live voice calls (Twilio + browser mic). Native audio models (Gemini Live) handle speech directly with the lowest latency. For chain mode, this also selects the audio backend.
                </div>
              </details>
            </div>
            <div className="form-field">
              <label>Text Model</label>
              <select
                value={llmConfig.text_model}
                onChange={(e) => updateLlm({ text_model: e.target.value })}
              >
                {prov.textModels.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                The model used for text chat sessions and post-call analysis. Smaller/faster models (Flash, mini) are usually fine for text. Use larger models (Pro, GPT-4o) only when the conversation is complex.
                </div>
              </details>
            </div>
            <div className="form-field">
              <label>Voice</label>
              <select
                value={llmConfig.voice}
                onChange={(e) => updateLlm({ voice: e.target.value })}
              >
                {voiceCat.voices.map((v) => (
                  <option key={v.id} value={v.id}>{v.id} — {v.desc}</option>
                ))}
              </select>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                The voice the AI uses to speak. Pick one that matches your practice's persona (warm, professional, calm). Each voice has different accents and tones.
                </div>
              </details>
              {voiceCat.previewUrl && (
                <details className="help-details">
                  <summary className="help-summary">Learn more</summary>
                  <div className="help-body">
                  {voiceCat.voices.length} voices. Preview at{" "}
                  <a href={voiceCat.previewUrl} target="_blank" rel="noopener">
                    {voiceCat.previewLabel}
                  </a>
                  </div>
                </details>
              )}
            </div>
          </>);
        })()}
          </div>
        </section>

        {/* ── Behavior ───────────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Behavior</h4>
            <span className="config-section-meta">Determinism, length, and safety</span>
          </div>
          <div className="config-section-body">
        <div className="form-field">
          <label>Temperature</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="muted small">Precise</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={llmConfig.temperature}
              onChange={(e) =>
                updateLlm({ temperature: parseFloat(e.target.value) })
              }
              style={{ flex: 1 }}
            />
            <span className="muted small">Creative</span>
            <span className="mono small" style={{ minWidth: 32 }}>
              {Number(llmConfig.temperature || 0).toFixed(2)}
            </span>
          </div>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            Controls how predictable the AI is. Healthcare calls should stay near <strong>0.1</strong> (deterministic, reliable) so the agent reads back appointment details exactly as they are. Higher values cause the agent to improvise or hallucinate.
            </div>
          </details>
        </div>
        <div className="config-grid-2">
          <div className="form-field">
            <label>Max Output Tokens</label>
            <input
              type="number"
              min={64}
              max={32768}
              step={64}
              value={llmConfig.max_output_tokens}
              onChange={(e) =>
                updateLlm({
                  max_output_tokens: parseInt(e.target.value, 10) || 0,
                })
              }
            />
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
              The maximum length of any single AI response. Higher = the AI can produce longer answers but uses more tokens (cost). For voice calls, 1024-2048 is plenty (one turn is short). For text or long analyses, 8192 is safer.
              </div>
            </details>
          </div>
          <div className="form-field">
            <label>Safety Level</label>
            <select
              value={llmConfig.safety_level}
              onChange={(e) => updateLlm({ safety_level: e.target.value })}
            >
              {SAFETY_LEVEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
              How aggressively Gemini blocks risky output (medical advice, harmful content). For oncology and other clinical settings, use <strong>Default</strong> or <strong>Permissive</strong> — too strict will block legitimate medical conversation. Too loose risks the agent giving unsafe medical guidance.
              </div>
            </details>
          </div>
        </div>
          </div>
        </section>

        {/* ── Call Limits ────────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Call Limits</h4>
            <span className="config-section-meta">Maximum duration & timeout warnings</span>
          </div>
          <div className="config-section-body">
        <div className="agent-config-grid">
          <div className="form-field">
            <label>Max Call Duration</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="number"
                min={30}
                max={3600}
                step={30}
                value={llmConfig.max_call_duration}
                onChange={(e) =>
                  updateLlm({
                    max_call_duration: parseInt(e.target.value, 10) || 0,
                  })
                }
                style={{ flex: 1 }}
              />
              <span className="muted small">
                seconds ({Math.round(llmConfig.max_call_duration / 60)} min)
              </span>
            </div>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
              Hard cap on call length. Prevents runaway calls (looping conversations, stuck patients) from racking up cost. Most clinical calls finish in 5-10 minutes — 600s (10 min) is a safe default.
              </div>
            </details>
          </div>
          <div className="form-field">
            <label>Timeout Warning At</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="number"
                min={30}
                max={3600}
                step={30}
                value={llmConfig.timeout_warning_at}
                onChange={(e) =>
                  updateLlm({
                    timeout_warning_at: parseInt(e.target.value, 10) || 0,
                  })
                }
                style={{ flex: 1 }}
              />
              <span className="muted small">
                seconds ({Math.round(llmConfig.timeout_warning_at / 60)} min)
              </span>
            </div>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
              When the agent should warn the patient that the call is wrapping up. Should be 30-60s before the hard timeout so the patient has time to wrap up.
              </div>
            </details>
          </div>
        </div>
          </div>
        </section>

        {/* ── Voice Style ────────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Voice Style & Routing</h4>
            <span className="config-section-meta">Pacing and agent handoff strategy</span>
          </div>
          <div className="config-section-body">
        <div className="form-field">
          <label>Agent Handoff Mode</label>
          <select
            value={llmConfig.handoff_mode || "lazy"}
            onChange={(e) => updateLlm({ handoff_mode: e.target.value })}
            style={{
              padding: "6px 10px",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              fontSize: 13,
            }}
          >
            <option value="lazy">Lazy (default — agent prompts loaded on demand via tool response)</option>
            <option value="preload">Preload (all agent prompts in system prompt at call start)</option>
          </select>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            <strong>Lazy:</strong> one Gemini session; sub-agent prompts injected into route_to_agent
            tool response with a strong isolation preamble. Fast handoff, token-efficient for simple calls.
            Re-sent on every route to refresh the model's attention.
            <br />
            <strong>Preload:</strong> all agent prompts embedded in the system prompt at call start.
            Higher initial token cost but zero-latency routing. Best when most agents are used every call.
            </div>
          </details>
        </div>
        <div className="form-field">
          <label>Voice Pacing Instructions</label>
          <textarea
            rows={3}
            value={llmConfig.voice_pacing_instructions}
            onChange={(e) =>
              updateLlm({ voice_pacing_instructions: e.target.value })
            }
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              fontSize: 13,
              resize: "vertical",
            }}
          />
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            Free-text instruction injected into the agent's prompt to control voice delivery. Examples: "Speak slowly and clearly. Pause between sentences." or "Be conversational and friendly." Affects how the AI's voice sounds — useful when the default delivery feels rushed or robotic.
            </div>
          </details>
        </div>
          </div>
        </section>

        {/* ── Resilience ─────────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Resilience</h4>
            <span className="config-section-meta">Recovery from dead air & silence</span>
          </div>
          <div className="config-section-body">
        {/* Silence Re-prompt — only used by chain provider */}
        <div className="form-field">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={llmConfig.silence_reprompt?.enabled !== false}
              onChange={(e) => updateLlm({
                silence_reprompt: { ...(llmConfig.silence_reprompt || {}), enabled: e.target.checked },
              })}
            />
            Silence Re-prompt
          </label>
          <p className="muted small" style={{ marginTop: 4, marginBottom: 8 }}>
            Without this, if the patient stops responding (background noise, distracted, hung up but call didn't end), the agent waits silently forever. With this enabled, the agent re-engages after the timeout — better patient experience and faster recovery from dead air. Used by chain provider in both voice and text modes.
          </p>
          {llmConfig.silence_reprompt?.enabled !== false && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Timeout (seconds)</label>
                <input
                  type="number"
                  min={3} max={30}
                  value={llmConfig.silence_reprompt?.timeout_seconds ?? 8}
                  onChange={(e) => updateLlm({
                    silence_reprompt: { ...(llmConfig.silence_reprompt || {}), timeout_seconds: Number(e.target.value) },
                  })}
                  style={{ width: "100%", padding: 6, border: "1px solid var(--color-border)", borderRadius: 4, fontSize: 13 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Max Re-prompts</label>
                <input
                  type="number"
                  min={1} max={5}
                  value={llmConfig.silence_reprompt?.max_attempts ?? 2}
                  onChange={(e) => updateLlm({
                    silence_reprompt: { ...(llmConfig.silence_reprompt || {}), max_attempts: Number(e.target.value) },
                  })}
                  style={{ width: "100%", padding: 6, border: "1px solid var(--color-border)", borderRadius: 4, fontSize: 13 }}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 11, color: "var(--color-text-muted)" }}>Re-prompt Text</label>
                <input
                  type="text"
                  value={llmConfig.silence_reprompt?.text ?? "Are you still there? I didn't catch that — could you repeat?"}
                  onChange={(e) => updateLlm({
                    silence_reprompt: { ...(llmConfig.silence_reprompt || {}), text: e.target.value },
                  })}
                  style={{ width: "100%", padding: 6, border: "1px solid var(--color-border)", borderRadius: 4, fontSize: 13 }}
                />
              </div>
            </div>
          )}
        </div>
          </div>
        </section>

        {/* Audio Quality settings moved to the Audio tab. */}
        {false && (
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Audio Quality</h4>
            <span className="config-section-meta">Noise suppression & mic sensitivity</span>
          </div>
          <div className="config-section-body">
        <div className="form-field">
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={llmConfig.noise_suppression !== false}
              onChange={(e) => updateLlm({ noise_suppression: e.target.checked })}
            />
            AI Noise Suppression (RNNoise)
          </label>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            Server-side ML noise suppression powered by RNNoise. Strips background noise
            (fans, TV, traffic, keyboard) from patient audio while preserving speech.
            Applied to both browser and phone calls before audio reaches the LLM.
            Reduces false barge-ins caused by ambient noise. Enabled by default.
            </div>
          </details>
        </div>
        <div className="form-field">
          <label>
            Mic Noise Gate — {((llmConfig.mic_noise_threshold || 0.003) * 1000).toFixed(1)}
            <span className="muted small" style={{ marginLeft: 8 }}>
              {(llmConfig.mic_noise_threshold || 0.003) <= 0.002 ? "(very sensitive — picks up background noise)" :
               (llmConfig.mic_noise_threshold || 0.003) <= 0.005 ? "(balanced)" :
               (llmConfig.mic_noise_threshold || 0.003) <= 0.01 ? "(moderate — filters some quiet speech)" :
               "(aggressive — may miss quiet speech)"}
            </span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 11, color: "#999" }}>Sensitive</span>
            <input
              type="range"
              min="0.001"
              max="0.02"
              step="0.001"
              value={llmConfig.mic_noise_threshold || 0.003}
              onChange={(e) => updateLlm({ mic_noise_threshold: parseFloat(e.target.value) })}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: 11, color: "#999" }}>Aggressive</span>
          </div>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            Controls how much background noise is filtered from the patient's microphone.
            Lower = more sensitive (picks up quiet speech but also noise).
            Higher = more aggressive (filters noise but may miss soft-spoken patients).
            </div>
          </details>
        </div>

        {/* ── AGC (Automatic Gain Control) ──────────────────────── */}
        <div className="form-field" style={{ borderTop: "1px solid var(--color-border)", paddingTop: 14, marginTop: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={llmConfig.agc_enabled === true}
              onChange={(e) => updateLlm({ agc_enabled: e.target.checked })}
            />
            Automatic Gain Control (AGC)
          </label>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
              Normalizes the inbound voice level so the LLM's audio encoder sees a
              consistent dynamic range across callers. Phones vary wildly in mic gain —
              cell callers are often 20 dB quieter than speakerphone users.
              Without AGC: quiet callers lose words; loud ones clip into distortion.
              <br /><br />
              Applied <strong>after</strong> noise suppression so only clean speech is
              levelled (not noise). Works for both browser and phone audio.
              Off by default — enable when your tenant sees accuracy issues from
              highly-variable audio levels.
            </div>
          </details>
        </div>

        {llmConfig.agc_enabled === true && (
          <>
            <div className="config-grid-2">
              <div className="form-field">
                <label>
                  Target Level — {(llmConfig.agc_target_dbfs ?? -20)} dBFS
                  <span className="muted small" style={{ marginLeft: 8 }}>
                    {(llmConfig.agc_target_dbfs ?? -20) <= -24 ? "(quieter)"
                      : (llmConfig.agc_target_dbfs ?? -20) >= -16 ? "(louder)"
                      : "(balanced)"}
                  </span>
                </label>
                <input
                  type="range"
                  min="-30" max="-12" step="1"
                  value={llmConfig.agc_target_dbfs ?? -20}
                  onChange={(e) => updateLlm({ agc_target_dbfs: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  RMS level AGC aims for. −20 dBFS is a safe default for most encoders.
                </p>
              </div>

              <div className="form-field">
                <label>
                  Max Boost — {llmConfig.agc_max_gain_db ?? 20} dB
                </label>
                <input
                  type="range"
                  min="6" max="30" step="1"
                  value={llmConfig.agc_max_gain_db ?? 20}
                  onChange={(e) => updateLlm({ agc_max_gain_db: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Hard ceiling on amplification. Prevents boosting silence into noise.
                </p>
              </div>
            </div>

            <div className="config-grid-2">
              <div className="form-field">
                <label>
                  Noise Floor — {llmConfig.agc_noise_floor_dbfs ?? -45} dBFS
                </label>
                <input
                  type="range"
                  min="-60" max="-30" step="1"
                  value={llmConfig.agc_noise_floor_dbfs ?? -45}
                  onChange={(e) => updateLlm({ agc_noise_floor_dbfs: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Below this input level, gain is frozen — prevents hiss amplification during silence.
                </p>
              </div>

              <div className="form-field">
                <label>
                  Limiter Ceiling — {llmConfig.agc_limiter_dbfs ?? -1} dBFS
                </label>
                <input
                  type="range"
                  min="-6" max="0" step="1"
                  value={llmConfig.agc_limiter_dbfs ?? -1}
                  onChange={(e) => updateLlm({ agc_limiter_dbfs: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Safety ceiling for peak limiter. Prevents loud transients from clipping.
                </p>
              </div>
            </div>

            <div className="config-grid-2">
              <div className="form-field">
                <label>
                  Attack — {llmConfig.agc_attack_ms ?? 10} ms
                </label>
                <input
                  type="range"
                  min="1" max="100" step="1"
                  value={llmConfig.agc_attack_ms ?? 10}
                  onChange={(e) => updateLlm({ agc_attack_ms: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  How fast gain responds to louder speech. Lower = catches transients; higher = smoother.
                </p>
              </div>

              <div className="form-field">
                <label>
                  Release — {llmConfig.agc_release_ms ?? 800} ms
                </label>
                <input
                  type="range"
                  min="100" max="3000" step="50"
                  value={llmConfig.agc_release_ms ?? 800}
                  onChange={(e) => updateLlm({ agc_release_ms: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  How slowly gain recovers on quiet speech. Long = stable through natural pauses.
                </p>
              </div>
            </div>
          </>
        )}
          </div>
        </section>
        )}

        {/* ── Speech Services (STT / TTS providers) ─────────────── */}
        {/* Only meaningful for the STT→LLM→TTS pipeline provider.
            When the sub-tenant uses native voice (Gemini Live / OpenAI
            Realtime / Chain + Gemini Live), these selectors are ignored
            by the runtime — but we still show them so operators who are
            evaluating providers don't have to remember when they apply. */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Speech Services (Pipeline mode)</h4>
            <span className="config-section-meta">STT + TTS provider per stage. Requires voice_model = "chain-pipeline".</span>
          </div>
          <div className="config-section-body">
            <div className="config-grid-2">
              <div className="form-field">
                <label>STT Provider</label>
                <select
                  value={llmConfig.stt_provider || "google"}
                  onChange={(e) => updateLlm({ stt_provider: e.target.value })}
                >
                  <option value="google">Google Cloud Speech (latest_long) — $0.024/min</option>
                  <option value="cartesia">Cartesia Ink-Whisper — ~$0.13/min</option>
                </select>
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Both stream in real time. Cartesia usually finalizes faster; Google has stronger accent coverage.
                </p>
              </div>
              <div className="form-field">
                <label>TTS Provider</label>
                <select
                  value={llmConfig.tts_provider || "google"}
                  onChange={(e) => updateLlm({ tts_provider: e.target.value })}
                >
                  <option value="google">Google Cloud TTS (Neural2) — $16/1M chars</option>
                  <option value="cartesia">Cartesia Sonic-2 — ~$15/1M chars</option>
                </select>
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Sonic-2 streams audio so first-byte is ~100–200ms vs 400–800ms for Google.
                </p>
              </div>
            </div>

            {/* Cartesia-specific knobs — only shown when either stage uses Cartesia. */}
            {(llmConfig.tts_provider === "cartesia" || llmConfig.stt_provider === "cartesia") && (
              <>
                <div className="help-details" style={{ background: "#fff3cd", border: "1px solid #ffeaa7", padding: 10, borderRadius: 4, fontSize: 12, color: "#664d03", marginTop: 8 }}>
                  <strong>Setup:</strong> the runtime reads the API key from <code>CARTESIA_API_KEY</code> env var.
                  If unset, the pipeline falls back to Google on both stages and logs a warning at call start.
                </div>

                {llmConfig.tts_provider === "cartesia" && (
                  <div className="config-grid-2" style={{ marginTop: 12 }}>
                    <div className="form-field">
                      <label>Cartesia Voice</label>
                      <select
                        value={llmConfig.cartesia_voice || CARTESIA_VOICE_OPTIONS[0].value}
                        onChange={(e) => updateLlm({ cartesia_voice: e.target.value })}
                      >
                        {CARTESIA_VOICE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                        Preview at <a href="https://play.cartesia.ai/voices" target="_blank" rel="noreferrer">play.cartesia.ai/voices</a>. Paste a custom voice UUID below to override.
                      </p>
                      <input
                        type="text"
                        placeholder="…or paste a custom voice UUID"
                        value={llmConfig.cartesia_voice && !CARTESIA_VOICE_OPTIONS.some(v => v.value === llmConfig.cartesia_voice) ? llmConfig.cartesia_voice : ""}
                        onChange={(e) => updateLlm({ cartesia_voice: e.target.value.trim() })}
                        style={{ width: "100%", padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 12, fontFamily: "var(--font-mono)", marginTop: 6 }}
                      />
                    </div>
                    <div className="form-field">
                      <label>TTS Model</label>
                      <select
                        value={llmConfig.cartesia_tts_model || "sonic-2"}
                        onChange={(e) => updateLlm({ cartesia_tts_model: e.target.value })}
                      >
                        {CARTESIA_TTS_MODELS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                        Sonic-2 is the current production model. Only change if Cartesia ships a newer tier.
                      </p>
                    </div>
                  </div>
                )}

                {llmConfig.stt_provider === "cartesia" && (
                  <div className="form-field" style={{ marginTop: 12 }}>
                    <label>STT Model</label>
                    <select
                      value={llmConfig.cartesia_stt_model || "ink-whisper"}
                      onChange={(e) => updateLlm({ cartesia_stt_model: e.target.value })}
                    >
                      {CARTESIA_STT_MODELS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Output Filter LLM ─────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Output Filter LLM</h4>
            <span className="config-section-meta">Safety + accuracy guardrail before responses reach the caller</span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={llmConfig.filter_llm?.enabled === true}
                  onChange={(e) => updateLlm({
                    filter_llm: { ...(llmConfig.filter_llm || {}), enabled: e.target.checked },
                  })}
                />
                Enable Output Filter
              </label>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                  Runs every agent's response through a small LLM <strong>before</strong> it reaches the caller.
                  Catches hallucinated dates/times not present in tool results, out-of-scope medical advice,
                  specific drug dosing recommendations, and PII leaks. Adds ~150-300ms per response.
                  <br /><br />
                  In <strong>STT→LLM→TTS</strong> mode the filter operates on full text before TTS — clean and reliable.
                  In <strong>native voice</strong> modes (Gemini Live / OpenAI Realtime) the filter runs at sentence
                  boundaries on the streaming output transcription.
                </div>
              </details>
            </div>

            {llmConfig.filter_llm?.enabled === true && (
              <>
                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Filter Model</label>
                    <select
                      value={llmConfig.filter_llm?.model || "gemini-2.5-flash-lite"}
                      onChange={(e) => updateLlm({
                        filter_llm: { ...(llmConfig.filter_llm || {}), model: e.target.value },
                      })}
                    >
                      <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite (fastest, cheapest)</option>
                      <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                      <option value="gemini-2.5-pro">Gemini 2.5 Pro (most thorough)</option>
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-4o">GPT-4o</option>
                    </select>
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      Smaller = faster + cheaper. Flash-Lite fits most cases.
                    </p>
                  </div>
                  <div className="form-field">
                    <label>Filter Mode</label>
                    <select
                      value={llmConfig.filter_llm?.mode || "rewrite"}
                      onChange={(e) => updateLlm({
                        filter_llm: { ...(llmConfig.filter_llm || {}), mode: e.target.value },
                      })}
                    >
                      <option value="rewrite">Rewrite — filter can edit the response</option>
                      <option value="block">Block — only allow or reject (replace with safe default)</option>
                      <option value="log">Log only — observe, never modify</option>
                    </select>
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      <strong>Rewrite</strong> is most useful. <strong>Log</strong> is for evaluating the filter before turning it on.
                    </p>
                  </div>
                </div>

                <div className="form-field">
                  <label>Filter Instructions</label>
                  <textarea
                    rows={4}
                    value={llmConfig.filter_llm?.instructions || ""}
                    onChange={(e) => updateLlm({
                      filter_llm: { ...(llmConfig.filter_llm || {}), instructions: e.target.value },
                    })}
                    style={{ width: "100%", padding: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 12, fontFamily: "var(--font-mono)", resize: "vertical", minHeight: 80 }}
                  />
                  <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                    System prompt for the filter. The filter receives the agent's intended response + recent tool results + the patient's last message, and decides allow/rewrite/block based on these instructions.
                  </p>
                </div>

                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Apply To</label>
                    <input
                      type="text"
                      placeholder="all agents (leave blank), or e.g. triage, scheduling"
                      value={(llmConfig.filter_llm?.apply_to_agents || []).join(", ")}
                      onChange={(e) => updateLlm({
                        filter_llm: {
                          ...(llmConfig.filter_llm || {}),
                          apply_to_agents: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                        },
                      })}
                    />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      Comma-separated agent ids to scope the filter to. Empty = filter every agent.
                    </p>
                  </div>
                  <div className="form-field">
                    <label>Timeout — {llmConfig.filter_llm?.timeout_ms ?? 2000} ms</label>
                    <input
                      type="range"
                      min="500" max="5000" step="100"
                      value={llmConfig.filter_llm?.timeout_ms ?? 2000}
                      onChange={(e) => updateLlm({
                        filter_llm: { ...(llmConfig.filter_llm || {}), timeout_ms: parseInt(e.target.value, 10) },
                      })}
                      style={{ width: "100%" }}
                    />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      If the filter doesn't return in this time, the agent's response is sent through unchanged.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

      </div>
      )}

      {configSubTab === "audio" && (
      <div className="card">
        <div className="card-header">
          <h3>Audio Processing</h3>
        </div>
        <p className="muted small mb-16">
          Configure the inbound audio enhancement chain that sits between the caller and the LLM,
          plus outbound voice pacing for phone calls. Stages run left-to-right in the diagram below.
          Disabled stages are pass-through.
        </p>

        {/* ── Enhancement chain visualization ───────────────────── */}
        {(() => {
          const rnnoiseOn = llmConfig.noise_suppression === true;
          const dfnOn = llmConfig.deepfilternet_enabled === true;
          const bweOn = llmConfig.twilio_bwe_enabled === true;
          const agcOn = llmConfig.agc_enabled === true;
          const vadOn = llmConfig.phone_vad_enabled !== false;
          const stages = [
            { id: "source", kind: "source", label: "Caller", tag: "Browser / Phone" },
            { id: "bwe",     kind: "stage",  label: "Bandwidth Ext", tag: "neural · phone only", active: bweOn, phoneOnly: true },
            { id: "rnnoise", kind: "stage",  label: "RNNoise",       tag: "classical denoise",   active: rnnoiseOn },
            { id: "dfn",     kind: "stage",  label: "DeepFilterNet", tag: "neural denoise+dereverb", active: dfnOn },
            { id: "agc",     kind: "stage",  label: "AGC",           tag: "level normalize",     active: agcOn },
            { id: "vad",     kind: "stage",  label: "VAD Gate",      tag: "voice activity",      active: vadOn },
            { id: "dest",    kind: "destination", label: "LLM", tag: "Gemini encoder" },
          ];
          return (
            <>
              <div className="audio-chain">
                {stages.map((s, i) => (
                  <React.Fragment key={s.id}>
                    {i > 0 && (
                      <div className={`audio-chain-arrow ${s.active || s.kind !== "stage" ? "stage-active" : ""}`}>→</div>
                    )}
                    <div className={`audio-chain-node ${
                      s.kind === "source" ? "source"
                      : s.kind === "destination" ? "destination"
                      : (s.active ? "stage-active" : "stage-inactive")
                    }`}>
                      <div className="audio-chain-node-label">{s.label}</div>
                      <div className="audio-chain-node-tag">{s.tag}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="audio-chain-legend">
                <span><span className="audio-chain-legend-swatch" style={{ background: "#fff8dc", border: "1px solid #c9a227" }} /> Active stage</span>
                <span><span className="audio-chain-legend-swatch" style={{ background: "#fff", border: "1px dashed #aaa" }} /> Disabled (pass-through)</span>
                <span><span className="audio-chain-legend-swatch" style={{ background: "#eef4ff", border: "1px solid #4a6fa5" }} /> Source</span>
                <span><span className="audio-chain-legend-swatch" style={{ background: "#ecf7ed", border: "1px solid #2d7a3b" }} /> Destination</span>
              </div>
            </>
          );
        })()}

        {/* ── 1. Bandwidth Extension (Twilio only) ──────────────── */}
        <section className="config-section" style={{ opacity: 0.55 }}>
          <div className="config-section-header">
            <h4 className="config-section-title">1 · Bandwidth Extension</h4>
            <span className="config-section-meta">
              <span className="badge badge-neutral" style={{ marginRight: 8 }}>Coming soon</span>
              Neural 8→16 kHz reconstruction · phone only
            </span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "not-allowed" }}>
                <input
                  type="checkbox"
                  checked={false}
                  disabled
                  readOnly
                />
                Enable Bandwidth Extension (Twilio)
              </label>
              <p className="muted small" style={{ marginTop: 6, fontSize: 11 }}>
                Not yet available — no production-ready ONNX model is currently bundled. Planned for a future release.
              </p>
            </div>
          </div>
        </section>

        {/* ── 2. Noise Suppression (RNNoise) ────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">2 · RNNoise</h4>
            <span className="config-section-meta">Classical noise suppression (WASM)</span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={llmConfig.noise_suppression === true}
                  onChange={(e) => updateLlm({ noise_suppression: e.target.checked })}
                />
                Enable RNNoise
              </label>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                  ML noise suppression via RNNoise (WASM). Strips background noise (fans, TV, traffic,
                  keyboard) while preserving most speech. Fast, robust, no GPU needed. Aggressive on
                  fricatives — pair with DeepFilterNet below for clean speech + dereverb, or use alone
                  for low-cost denoise.
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* ── 3. DeepFilterNet ──────────────────────────────────── */}
        <section className="config-section" style={{ opacity: 0.55 }}>
          <div className="config-section-header">
            <h4 className="config-section-title">3 · DeepFilterNet</h4>
            <span className="config-section-meta">
              <span className="badge badge-neutral" style={{ marginRight: 8 }}>Coming soon</span>
              Neural speech enhancement + dereverb
            </span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, cursor: "not-allowed" }}>
                <input
                  type="checkbox"
                  checked={false}
                  disabled
                  readOnly
                />
                Enable DeepFilterNet
              </label>
              <p className="muted small" style={{ marginTop: 6, fontSize: 11 }}>
                Not yet available — ONNX model export requires a Rust toolchain build. Planned for a future release.
              </p>
            </div>
          </div>
        </section>

        {/* ── 4. AGC ────────────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">4 · Automatic Gain Control</h4>
            <span className="config-section-meta">Normalize level for the LLM encoder</span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={llmConfig.agc_enabled === true}
                  onChange={(e) => updateLlm({ agc_enabled: e.target.checked })}
                />
                Enable AGC
              </label>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                  Normalizes the inbound voice level so the LLM's audio encoder sees a consistent
                  dynamic range across callers. Phones vary wildly in mic gain — cell callers are
                  often 20 dB quieter than speakerphone users. Quiet = missed words; loud = clipping.
                  Applied AFTER denoise so it only levels clean speech, not noise.
                </div>
              </details>
            </div>

            {llmConfig.agc_enabled === true && (
              <>
                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Target Level — {(llmConfig.agc_target_dbfs ?? -20)} dBFS</label>
                    <input type="range" min="-30" max="-12" step="1"
                      value={llmConfig.agc_target_dbfs ?? -20}
                      onChange={(e) => updateLlm({ agc_target_dbfs: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      RMS level AGC aims for. −20 dBFS is safe for most encoders.
                    </p>
                  </div>
                  <div className="form-field">
                    <label>Max Boost — {llmConfig.agc_max_gain_db ?? 20} dB</label>
                    <input type="range" min="6" max="30" step="1"
                      value={llmConfig.agc_max_gain_db ?? 20}
                      onChange={(e) => updateLlm({ agc_max_gain_db: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      Hard ceiling on amplification.
                    </p>
                  </div>
                </div>
                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Noise Floor — {llmConfig.agc_noise_floor_dbfs ?? -45} dBFS</label>
                    <input type="range" min="-60" max="-30" step="1"
                      value={llmConfig.agc_noise_floor_dbfs ?? -45}
                      onChange={(e) => updateLlm({ agc_noise_floor_dbfs: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      Below this, gain is frozen (prevents hiss amplification).
                    </p>
                  </div>
                  <div className="form-field">
                    <label>Limiter Ceiling — {llmConfig.agc_limiter_dbfs ?? -1} dBFS</label>
                    <input type="range" min="-6" max="0" step="1"
                      value={llmConfig.agc_limiter_dbfs ?? -1}
                      onChange={(e) => updateLlm({ agc_limiter_dbfs: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                    <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                      Peak limiter safety ceiling.
                    </p>
                  </div>
                </div>
                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Attack — {llmConfig.agc_attack_ms ?? 10} ms</label>
                    <input type="range" min="1" max="100" step="1"
                      value={llmConfig.agc_attack_ms ?? 10}
                      onChange={(e) => updateLlm({ agc_attack_ms: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                  </div>
                  <div className="form-field">
                    <label>Release — {llmConfig.agc_release_ms ?? 800} ms</label>
                    <input type="range" min="100" max="3000" step="50"
                      value={llmConfig.agc_release_ms ?? 800}
                      onChange={(e) => updateLlm({ agc_release_ms: parseInt(e.target.value, 10) })}
                      style={{ width: "100%" }} />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* ── 5. VAD Gate ───────────────────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">5 · Voice Activity Detection (Phone)</h4>
            <span className="config-section-meta">Final gate before the LLM</span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={llmConfig.phone_vad_enabled !== false}
                  onChange={(e) => updateLlm({ phone_vad_enabled: e.target.checked })}
                />
                Enable VAD gate on inbound phone audio
              </label>
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                  Final stage. Prevents residual noise from triggering Gemini barge-in mid-response.
                  Closes when RMS is below threshold; hysteresis keeps it stable through natural pauses.
                </div>
              </details>
            </div>

            {llmConfig.phone_vad_enabled !== false && (
              <>
                <div className="config-grid-2">
                  <div className="form-field">
                    <label>Open Threshold — {((llmConfig.phone_vad_open_threshold ?? 0.008) * 1000).toFixed(1)}</label>
                    <input type="range" min="0.002" max="0.03" step="0.001"
                      value={llmConfig.phone_vad_open_threshold ?? 0.008}
                      onChange={(e) => updateLlm({ phone_vad_open_threshold: parseFloat(e.target.value) })}
                      style={{ width: "100%" }} />
                  </div>
                  <div className="form-field">
                    <label>Close Threshold — {((llmConfig.phone_vad_close_threshold ?? 0.003) * 1000).toFixed(1)}</label>
                    <input type="range" min="0.001" max="0.02" step="0.001"
                      value={llmConfig.phone_vad_close_threshold ?? 0.003}
                      onChange={(e) => updateLlm({ phone_vad_close_threshold: parseFloat(e.target.value) })}
                      style={{ width: "100%" }} />
                  </div>
                </div>
              </>
            )}

            <div className="form-field">
              <label>
                Browser Mic Noise Gate — {((llmConfig.mic_noise_threshold || 0.003) * 1000).toFixed(1)}
              </label>
              <input type="range" min="0.001" max="0.02" step="0.001"
                value={llmConfig.mic_noise_threshold || 0.003}
                onChange={(e) => updateLlm({ mic_noise_threshold: parseFloat(e.target.value) })}
                style={{ width: "100%" }} />
              <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                Applies only to browser mic (not phone). Lower = catches soft speech; higher = cuts more noise.
              </p>
            </div>
          </div>
        </section>

        {/* ── 6. Outbound voice pacing ──────────────────────────── */}
        <section className="config-section">
          <div className="config-section-header">
            <h4 className="config-section-title">Outbound Voice Pacing (Twilio)</h4>
            <span className="config-section-meta">Agent speech speed over phone</span>
          </div>
          <div className="config-section-body">
            <div className="form-field">
              <label>
                Base Playback Rate — {(llmConfig.twilio_playback_rate ?? 0.82).toFixed(2)}x
                <span className="muted small" style={{ marginLeft: 8 }}>
                  {(llmConfig.twilio_playback_rate ?? 0.82) < 0.85 ? "(slower)"
                    : (llmConfig.twilio_playback_rate ?? 0.82) > 0.95 ? "(natural)"
                    : "(balanced)"}
                </span>
              </label>
              <input type="range" min="0.70" max="1.10" step="0.01"
                value={llmConfig.twilio_playback_rate ?? 0.82}
                onChange={(e) => updateLlm({ twilio_playback_rate: parseFloat(e.target.value) })}
                style={{ width: "100%" }} />
              <details className="help-details">
                <summary className="help-summary">Learn more</summary>
                <div className="help-body">
                  Fixed speed the agent plays at over Twilio. 0.82x slows Gemini's default pace to
                  be clearer for older patients and poor connections. Only affects Twilio phone calls.
                </div>
              </details>
            </div>

            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input type="checkbox"
                  checked={llmConfig.twilio_adaptive_age_enabled === true}
                  onChange={(e) => updateLlm({ twilio_adaptive_age_enabled: e.target.checked })} />
                Auto-slow for older patients
              </label>
              <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                After verification, drop rate based on patient age: 65+ → 0.80x, 75+ → 0.76x, 85+ → 0.72x.
              </p>
            </div>

            <div className="form-field">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                <input type="checkbox"
                  checked={llmConfig.twilio_adaptive_trouble_enabled === true}
                  onChange={(e) => updateLlm({ twilio_adaptive_trouble_enabled: e.target.checked })} />
                Auto-slow when patient signals trouble listening
              </label>
              <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                Detects phrases like "what?", "can you repeat", "slower" — decrements rate mid-call.
              </p>
            </div>

            {(llmConfig.twilio_adaptive_age_enabled || llmConfig.twilio_adaptive_trouble_enabled) && (
              <div className="config-grid-2">
                <div className="form-field">
                  <label>Minimum Rate — {(llmConfig.twilio_adaptive_min_rate ?? 0.70).toFixed(2)}x</label>
                  <input type="range" min="0.60" max="0.85" step="0.01"
                    value={llmConfig.twilio_adaptive_min_rate ?? 0.70}
                    onChange={(e) => updateLlm({ twilio_adaptive_min_rate: parseFloat(e.target.value) })}
                    style={{ width: "100%" }} />
                </div>
                <div className="form-field">
                  <label>Trouble Step — {(llmConfig.twilio_adaptive_trouble_step ?? 0.04).toFixed(2)}</label>
                  <input type="range" min="0.01" max="0.10" step="0.01"
                    value={llmConfig.twilio_adaptive_trouble_step ?? 0.04}
                    onChange={(e) => updateLlm({ twilio_adaptive_trouble_step: parseFloat(e.target.value) })}
                    style={{ width: "100%" }} />
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
      )}

      {configSubTab === "capabilities" && (<>
      <div className="card">
        <div className="card-header">
          <h3>Built-in call handling capabilities</h3>
        </div>
        {CAPABILITY_DEFS.map((cap) => (
          <SubCapabilityRow
            key={cap.id}
            cap={cap}
            on={!!systemTools[cap.id]}
            onToggle={() => toggleTool(cap.id)}
            extraField={
              cap.id === "language_detection" && systemTools[cap.id] ? (
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid var(--color-border)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    Default language
                  </label>
                  <select
                    value={variables.default_language}
                    onChange={(e) =>
                      updateVar({ default_language: e.target.value })
                    }
                    style={{ maxWidth: 240 }}
                  >
                    {LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : cap.id === "barge_in" && systemTools[cap.id] ? (
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid var(--color-border)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    Allow interruption after
                  </label>
                  <select
                    value={(() => {
                      const bs = llmConfig?.barge_in_suppression;
                      // "0"         → suppression off (immediate barge-in)
                      // "adaptive"  → adaptive mode (short=strict, long=permissive)
                      // any 0<f≤1   → fixed fraction
                      if (!bs || bs.enabled !== true) return "0";
                      if (bs.mode === "adaptive") return "adaptive";
                      const f = typeof bs.min_fraction === "number" ? bs.min_fraction : 0.7;
                      return String(f);
                    })()}
                    onChange={(e) => {
                      const val = e.target.value;
                      const prev = llmConfig?.barge_in_suppression || {};
                      if (val === "0") {
                        updateLlm({
                          barge_in_suppression: { ...prev, enabled: false },
                        });
                      } else if (val === "adaptive") {
                        // Adaptive: short utterances strict, long permissive.
                        // Defaults preserved if the user had already customized them.
                        updateLlm({
                          barge_in_suppression: {
                            enabled: true,
                            mode: "adaptive",
                            grace_ms: prev.grace_ms ?? 1500,
                            chars_per_sec: prev.chars_per_sec ?? 15,
                            adaptive: {
                              short_ms:       prev.adaptive?.short_ms       ?? 3000,
                              long_ms:        prev.adaptive?.long_ms        ?? 20000,
                              short_fraction: prev.adaptive?.short_fraction ?? 0.85,
                              long_fraction:  prev.adaptive?.long_fraction  ?? 0.50,
                            },
                          },
                        });
                      } else {
                        const frac = parseFloat(val);
                        updateLlm({
                          barge_in_suppression: {
                            enabled: true,
                            mode: "fixed",
                            grace_ms: prev.grace_ms ?? 1500,
                            min_fraction: frac,
                            chars_per_sec: prev.chars_per_sec ?? 15,
                          },
                        });
                      }
                    }}
                    style={{ maxWidth: 380 }}
                  >
                    <option value="0">Immediately (no suppression — default)</option>
                    <option value="adaptive">Adaptive — short sentences strict, long permissive (recommended)</option>
                    <option value="0.3">30% of current utterance (permissive, fixed)</option>
                    <option value="0.5">50% of current utterance (balanced, fixed)</option>
                    <option value="0.7">70% of current utterance (strict, fixed)</option>
                    <option value="0.85">85% of current utterance (very strict, fixed)</option>
                    <option value="1">100% — never interrupt (patient must wait for full sentence)</option>
                  </select>
                  <div className="muted small" style={{ marginTop: 4, lineHeight: 1.4 }}>
                    <strong>Adaptive</strong> scales with sentence length: short utterances (≤ 3 s, e.g. "Yes.")
                    need 85% before bargeable, long utterances (≥ 20 s, e.g. prep instructions) need only 50%.
                    Between those, linearly interpolated. Prevents accidental interrupts on quick confirmations
                    while letting patients cut in on long monologues. A 1.5-second hard minimum always applies.
                  </div>
                </div>
              ) : null
            }
          />
        ))}
      </div>
      </>)}

      {configSubTab === "variables" && (<>
      <div className="card">
        <div className="card-header">
          <h3>Variables</h3>
        </div>
        <p className="muted small mb-16">
          Practice-specific values (name, phone, address, hours) substituted into agent prompts via <code>{"{{variable_name}}"}</code> syntax. Set them once here — they replace placeholders in every agent prompt at runtime. Avoids hardcoding tenant-specific text into shared prompts.
        </p>

        {/* Standard variables grouped by section */}
        {(() => {
          const sections = [];
          const seen = new Set();
          for (const def of VARIABLE_DEFINITIONS) {
            if (!seen.has(def.section)) { seen.add(def.section); sections.push(def.section); }
          }
          return sections.map((section) => (
            <div key={section} style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
                {section}
              </h4>
              {VARIABLE_DEFINITIONS.filter((d) => d.section === section).map((def) => (
                <div key={def.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ minWidth: 180, paddingTop: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: "block" }}>{def.label}</label>
                    <code style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{`{{${def.key}}}`}</code>
                    {def.help && <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>{def.help}</div>}
                  </div>
                  {def.type === "textarea" ? (
                    <textarea
                      rows={2}
                      placeholder={def.placeholder}
                      value={variables[def.key] || ""}
                      onChange={(e) => updateVar({ [def.key]: e.target.value })}
                      style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, resize: "vertical" }}
                    />
                  ) : def.type === "select" && def.options ? (
                    <select
                      value={variables[def.key] || def.placeholder || ""}
                      onChange={(e) => updateVar({ [def.key]: e.target.value })}
                      style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13 }}
                    >
                      {def.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder={def.placeholder}
                      value={variables[def.key] || ""}
                      onChange={(e) => updateVar({ [def.key]: e.target.value })}
                      style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13 }}
                    />
                  )}
                </div>
              ))}
            </div>
          ));
        })()}

        {/* Custom variables (arbitrary key-value pairs) */}
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
            Custom Variables
          </h4>
          <p className="muted small mb-8">
            Add any additional key-value pairs. Use <code>{"{{key}}"}</code> in prompts to reference them.
          </p>
          {(() => {
            const standardKeys = new Set(VARIABLE_DEFINITIONS.map((d) => d.key));
            const customEntries = Object.entries(variables).filter(([k]) => !standardKeys.has(k));
            return (
              <>
                {customEntries.map(([key, val]) => (
                  <div key={key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => {
                        const newKey = e.target.value;
                        setVariables((prev) => {
                          const updated = {};
                          for (const [k, v] of Object.entries(prev)) {
                            updated[k === key ? newKey : k] = v;
                          }
                          return updated;
                        });
                      }}
                      placeholder="variable_key"
                      style={{ width: 180, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
                    />
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => updateVar({ [key]: e.target.value })}
                      placeholder="Value"
                      style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13 }}
                    />
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setVariables((prev) => { const { [key]: _, ...rest } = prev; return rest; })}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-sm"
                  style={{ marginTop: 4 }}
                  onClick={() => updateVar({ [`custom_${customEntries.length + 1}`]: "" })}
                >
                  + Add Variable
                </button>
              </>
            );
          })()}
        </div>
      </div>
      </>)}

      {configSubTab === "integration" && (<>
      <div className="card">
        <div className="card-header">
          <h3>Integration</h3>
        </div>
        <p className="muted small mb-16">
          Backend service URLs used by server-type tools at runtime.
          Set a default URL, then optionally override individual tools.
        </p>

        {/* Default URL */}
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label>Default Data Service URL</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              placeholder="http://localhost:3005"
              value={dataServiceUrl}
              onChange={(e) => setDataServiceUrl(e.target.value)}
              style={{
                flex: 1,
                padding: 8,
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-sm)",
                fontSize: 13,
                fontFamily: "monospace",
              }}
            />
            {dataServiceUrl && Object.keys(toolBackends).length > 0 && (
              <button
                className="btn btn-sm"
                title="Set all tool overrides to this URL"
                onClick={() => {
                  const updated = {};
                  for (const k of Object.keys(toolBackends)) updated[k] = dataServiceUrl;
                  setToolBackends(updated);
                }}
              >
                Apply to All
              </button>
            )}
          </div>
          <details className="help-details">
            <summary className="help-summary">Learn more</summary>
            <div className="help-body">
            Fallback URL for all tools that don't have a specific override below.
            </div>
          </details>
        </div>

        {/* Per-tool overrides */}
        <div style={{ marginTop: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            Tool URL Overrides
            <span className="badge" style={{ fontSize: 11 }}>
              {Object.keys(toolBackends).length || 0}
            </span>
          </label>

          {Object.entries(toolBackends).map(([toolName, url]) => {
            const toolMeta = availableTools.find((t) => (t._id || t.id) === toolName);
            return (
              <div key={toolName} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <select
                  value={toolName}
                  onChange={(e) => {
                    const newName = e.target.value;
                    if (!newName || newName === toolName) return;
                    setToolBackends((prev) => {
                      const updated = {};
                      for (const [k, v] of Object.entries(prev)) {
                        updated[k === toolName ? newName : k] = v;
                      }
                      return updated;
                    });
                  }}
                  style={{
                    width: 220,
                    padding: 8,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: 13,
                  }}
                >
                  <option value={toolName}>
                    {toolMeta?.name || toolName}
                  </option>
                  {availableTools
                    .filter((t) => !toolBackends[(t._id || t.id)] || (t._id || t.id) === toolName)
                    .map((t) => {
                      const id = t._id || t.id;
                      if (id === toolName) return null;
                      return (
                        <option key={id} value={id}>
                          {t.name || id} {t.category ? `(${t.category})` : ""}
                        </option>
                      );
                    })}
                </select>
                <input
                  type="text"
                  value={url}
                  onChange={(e) =>
                    setToolBackends((prev) => ({ ...prev, [toolName]: e.target.value }))
                  }
                  placeholder="http://..."
                  style={{
                    flex: 1,
                    padding: 8,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: 13,
                    fontFamily: "monospace",
                  }}
                />
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() =>
                    setToolBackends((prev) => {
                      const { [toolName]: _, ...rest } = prev;
                      return rest;
                    })
                  }
                  title="Remove override"
                >
                  ✕
                </button>
              </div>
            );
          })}

          <button
            className="btn btn-sm"
            style={{ marginTop: 4 }}
            onClick={() => {
              // Find first tool not already overridden
              const usedTools = new Set(Object.keys(toolBackends));
              const next = availableTools.find((t) => !usedTools.has(t._id || t.id));
              const toolId = next ? (next._id || next.id) : `tool_${usedTools.size + 1}`;
              setToolBackends((prev) => ({ ...prev, [toolId]: dataServiceUrl || "" }));
            }}
          >
            + Add Override
          </button>
          <p className="muted small" style={{ marginTop: 8 }}>
            Override the backend URL for specific tools.
            Tools without an override use the default URL above.
          </p>
        </div>
      </div>

      {/* ── Tool Reference ─────────────────────────── */}
      {subTenantTools.length > 0 && (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <h3>Tool Reference</h3>
          <span className="badge">{subTenantTools.length} tools</span>
        </div>
        <p className="muted small mb-16">
          All tools used by this sub-tenant's agents with their resolved URLs and input parameters.
          Click a tool to expand.
        </p>
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
          {subTenantTools.map((tool, i) => {
            const toolId = tool._id || tool.id;
            const isExpanded = expandedTool === toolId;
            // Tool content may be a JSON string (from versioned library) or an object
            let tc = {};
            try {
              tc = typeof tool.content === "string" ? JSON.parse(tool.content) : (tool.content || {});
            } catch { tc = {}; }
            // Merge: top-level fields + parsed content (content has the rich data)
            const toolName = tc.name || tool.name || toolId;
            const toolKind = tc.kind || tool.kind_type || tool.kind || "";
            const toolCategory = tc.category || tool.category || "";
            const toolDescription = tc.description || tool.description || "";
            const request = tc.request || tool.request || {};
            // Resolve the URL: per-tool override → default → placeholder
            const resolvedBase = toolBackends[toolName] || toolBackends[toolId] || dataServiceUrl || "http://localhost:3005";
            const templateUrl = request.url || "";
            const resolvedUrl = templateUrl
              .replace("{{env.DATA_SERVICE_URL}}", resolvedBase)
              .replace("{tenant_id}", tid || "{tenant_id}");
            const method = request.method || "POST";
            const bodyParams = (request.body_params || []).filter((p) => p.source === "llm");
            const pathParams = (request.path_params || []).filter((p) => p.source === "llm");
            const queryParams = (request.query_params || []).filter((p) => p.source === "llm");
            const clientParams = tc.parameters || tool.parameters || [];
            const allParams = toolKind === "server" ? [...bodyParams, ...pathParams, ...queryParams] : clientParams;

            return (
              <div key={toolId} style={{ borderBottom: i < subTenantTools.length - 1 ? "1px solid var(--color-border)" : "none" }}>
                <div
                  onClick={() => setExpandedTool(isExpanded ? null : toolId)}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: isExpanded ? "var(--color-bg-subtle)" : "transparent",
                    transition: "background 0.15s",
                  }}
                >
                  <span style={{ fontSize: 11, color: "var(--color-text-muted)", width: 16, textAlign: "center" }}>
                    {isExpanded ? "▼" : "▶"}
                  </span>
                  {toolKind === "server" && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, fontFamily: "monospace",
                      padding: "2px 6px", borderRadius: 3, minWidth: 42, textAlign: "center",
                      background: method === "GET" ? "#e8f5e9" : method === "POST" ? "#e3f2fd" : method === "DELETE" ? "#ffebee" : "#fff3e0",
                      color: method === "GET" ? "#2e7d32" : method === "POST" ? "#1565c0" : method === "DELETE" ? "#c62828" : "#e65100",
                    }}>
                      {method}
                    </span>
                  )}
                  <span style={{ fontWeight: 600, fontSize: 13, fontFamily: "monospace" }}>{toolName}</span>
                  {toolCategory && (
                    <span className="badge" style={{ fontSize: 10, marginLeft: "auto" }}>{toolCategory}</span>
                  )}
                  {(toolBackends[toolName] || toolBackends[toolId]) && (
                    <span style={{ fontSize: 10, color: "var(--color-warning)", fontWeight: 600 }}>OVERRIDE</span>
                  )}
                </div>

                {isExpanded && (
                  <div style={{ padding: "0 14px 14px 40px", fontSize: 13 }}>
                    <p className="muted small" style={{ marginBottom: 8 }}>{toolDescription}</p>

                    {/* Resolved URL */}
                    {toolKind === "server" && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                          Resolved URL
                        </label>
                        <code style={{
                          display: "block",
                          padding: "8px 10px",
                          background: "var(--color-bg-subtle)",
                          borderRadius: "var(--radius-sm)",
                          fontSize: 12,
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}>
                          {method} {resolvedUrl}
                        </code>
                      </div>
                    )}

                    {/* Parameters table */}
                    {allParams.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                          Input Parameters
                        </label>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Name</th>
                              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Type</th>
                              <th style={{ textAlign: "center", padding: "4px 8px", fontWeight: 600 }}>Required</th>
                              <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600 }}>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allParams.map((p) => (
                              <tr key={p.name} style={{ borderBottom: "1px solid var(--color-border)" }}>
                                <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{p.name}</td>
                                <td style={{ padding: "4px 8px" }}>
                                  <span style={{ fontSize: 11, padding: "1px 5px", borderRadius: 3, background: "var(--color-bg-subtle)" }}>
                                    {p.type}
                                  </span>
                                  {p.enum && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>({p.enum.join(" | ")})</span>}
                                </td>
                                <td style={{ padding: "4px 8px", textAlign: "center" }}>
                                  {p.required ? "✓" : "—"}
                                </td>
                                <td style={{ padding: "4px 8px" }} className="muted">{p.description || ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Sample input */}
                    {allParams.length > 0 && (
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                          Sample Input
                        </label>
                        <pre style={{
                          padding: "8px 10px",
                          background: "var(--color-bg-subtle)",
                          borderRadius: "var(--radius-sm)",
                          fontSize: 12,
                          fontFamily: "monospace",
                          overflow: "auto",
                          margin: 0,
                        }}>
{JSON.stringify(
  Object.fromEntries(
    allParams.filter((p) => p.source !== "runtime_variable").map((p) => [
      p.name,
      p.type === "string" ? (p.enum ? p.enum[0] : (p.description || `<${p.name}>`))
        : p.type === "integer" || p.type === "number" ? 1
        : p.type === "boolean" ? true
        : p.type === "array" ? ["item1"]
        : p.type === "object" ? {}
        : `<${p.type}>`,
    ])
  ),
  null,
  2
)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}
      </>
      )}

      {configSubTab === "telephony" && (
      <div className="card">
        <div className="card-header">
          <h3>Telephony {subTabDirection === "outbound" ? "· Outbound" : "· Inbound"}</h3>
        </div>
        <p className="muted small mb-16">
          {subTabDirection === "outbound"
            ? "Configure Twilio credentials for placing outbound calls. The worker dials patients using the from-number below and streams their audio into Sandy."
            : "Configure phone numbers for inbound calls. Each number routes to this sub-tenant's voice agent. Use separate numbers for testing (draft mode) and production (live mode)."}
        </p>

        {subTabDirection === "outbound" && (
          <>
            {/* Twilio outbound credentials */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
                Twilio Credentials
              </h4>
              <div style={{ display: "grid", gap: 10 }}>
                <label>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Account SID</div>
                  <code className="muted" style={{ fontSize: 10 }}>outbound.twilio.account_sid</code>
                  <input
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxx"
                    value={twilioOutbound.account_sid || ""}
                    onChange={(e) => setTwilioOutbound((t) => ({ ...t, account_sid: e.target.value }))}
                    style={{ width: "100%", padding: 6, fontFamily: "monospace", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                  />
                </label>
                <label>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Auth Token Secret Ref</div>
                  <code className="muted" style={{ fontSize: 10 }}>outbound.twilio.auth_token_secret_ref</code>
                  <input
                    type="text"
                    placeholder="e.g. twilio_auth_token"
                    value={twilioOutbound.auth_token_secret_ref || ""}
                    onChange={(e) => setTwilioOutbound((t) => ({ ...t, auth_token_secret_ref: e.target.value }))}
                    style={{ width: "100%", padding: 6, fontFamily: "monospace", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                  />
                  <div className="muted small" style={{ marginTop: 2 }}>
                    Pointer to a secret managed in the <strong>Secrets</strong> tab. Never paste the raw token here.
                  </div>
                </label>
                <label>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>From Number (Caller ID)</div>
                  <code className="muted" style={{ fontSize: 10 }}>outbound.twilio.from_number</code>
                  <input
                    type="text"
                    placeholder="+15551234567"
                    value={twilioOutbound.from_number || ""}
                    onChange={(e) => setTwilioOutbound((t) => ({ ...t, from_number: e.target.value }))}
                    style={{ width: "100%", padding: 6, fontFamily: "monospace", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                  />
                  <div className="muted small" style={{ marginTop: 2 }}>E.164 format. The number patients see on caller ID.</div>
                </label>
              </div>
            </div>

            {/* AMD + voicemail options */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
                Answering Machine Detection
              </h4>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={twilioOutbound.amd_enabled !== false}
                  onChange={(e) => setTwilioOutbound((t) => ({ ...t, amd_enabled: e.target.checked }))}
                />
                Enable Twilio AMD (detect voicemail before Sandy speaks)
              </label>
              <div className="muted small" style={{ marginTop: 4, marginLeft: 24 }}>
                Twilio listens to the first 3 seconds to decide human vs machine. On "machine_start", the runtime plays a short voicemail message instead of connecting Sandy.
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={twilioOutbound.voicemail_ok === true}
                  onChange={(e) => setTwilioOutbound((t) => ({ ...t, voicemail_ok: e.target.checked }))}
                />
                Leave voicemail when AMD detects a machine
              </label>
              <div className="muted small" style={{ marginTop: 4, marginLeft: 24 }}>
                For HIPAA-sensitive tasks (e.g. lab results), the runtime will still leave only a generic callback message — never specifics.
              </div>
            </div>

            {/* Voicemail variables */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
                Voicemail Message Variables
              </h4>
              <div style={{ display: "grid", gap: 10 }}>
                <label>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Practice Name (spoken in voicemail)</div>
                  <code className="muted" style={{ fontSize: 10 }}>outbound.twilio.practice_name</code>
                  <input
                    type="text"
                    placeholder="Metro Health Oncology"
                    value={twilioOutbound.practice_name || ""}
                    onChange={(e) => setTwilioOutbound((t) => ({ ...t, practice_name: e.target.value }))}
                    style={{ width: "100%", padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                  />
                </label>
                <label>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Callback Number (in voicemail)</div>
                  <code className="muted" style={{ fontSize: 10 }}>outbound.twilio.practice_phone</code>
                  <input
                    type="text"
                    placeholder="+15551234567"
                    value={twilioOutbound.practice_phone || ""}
                    onChange={(e) => setTwilioOutbound((t) => ({ ...t, practice_phone: e.target.value }))}
                    style={{ width: "100%", padding: 6, fontFamily: "monospace", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                  />
                </label>
              </div>
            </div>
          </>
        )}

        {subTabDirection === "inbound" && (
        <>
        {/* Twilio section (inbound phone numbers) */}
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
            Twilio
          </h4>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ minWidth: 180, paddingTop: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block" }}>Testing Number</label>
              <code style={{ fontSize: 10, color: "var(--color-text-muted)" }}>phone_number_draft</code>
              <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>Calls use draft prompts + config</div>
            </div>
            <input
              type="text"
              placeholder="+15551234567"
              value={variables.phone_number_draft || ""}
              onChange={(e) => updateVar({ phone_number_draft: e.target.value })}
              style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ minWidth: 180, paddingTop: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block" }}>Production Number</label>
              <code style={{ fontSize: 10, color: "var(--color-text-muted)" }}>phone_number_live</code>
              <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>Calls use live prompts + config</div>
            </div>
            <input
              type="text"
              placeholder="+15559876543"
              value={variables.phone_number_live || ""}
              onChange={(e) => updateVar({ phone_number_live: e.target.value })}
              style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 13, fontFamily: "monospace" }}
            />
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            Enter Twilio phone numbers in E.164 format (e.g., +15551234567).
            Configure the number in <a href="https://console.twilio.com" target="_blank" rel="noopener">Twilio Console</a> →
            Phone Numbers → set webhook to <code>https://your-domain/twilio/voice</code> (POST).
          </p>
        </div>
        </>
        )}

        {/* Outbound voice speed moved to Audio tab. */}
        {false && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
            Outbound Voice Speed
          </h4>
          <div className="form-field">
            <label>
              Playback Rate — {(llmConfig.twilio_playback_rate ?? 0.82).toFixed(2)}x
              <span className="muted small" style={{ marginLeft: 8 }}>
                {(llmConfig.twilio_playback_rate ?? 0.82) < 0.85
                  ? "(slower — easier to follow)"
                  : (llmConfig.twilio_playback_rate ?? 0.82) > 0.95
                    ? "(natural — faster delivery)"
                    : "(balanced)"}
              </span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#999" }}>Slower (0.70x)</span>
              <input
                type="range"
                min="0.70"
                max="1.10"
                step="0.01"
                value={llmConfig.twilio_playback_rate ?? 0.82}
                onChange={(e) => updateLlm({ twilio_playback_rate: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 11, color: "#999" }}>Natural (1.10x)</span>
            </div>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
                Controls how fast the agent's voice plays over the phone. Gemini's native
                voice is paced for fluent English speakers on good audio; over PSTN it
                can sound rushed. We slow it down by default (<strong>0.82x</strong>) so
                older patients and callers on poor connections can follow easily.
                Lower = slower and more deliberate. Higher = more natural but harder
                to follow on noisy lines. Only affects Twilio phone calls — browser
                audio uses its own playback rate. Changes apply to the next incoming call.
              </div>
            </details>
          </div>

          {/* Age-based auto-adjust */}
          <div className="form-field" style={{ marginTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={llmConfig.twilio_adaptive_age_enabled === true}
                onChange={(e) => updateLlm({ twilio_adaptive_age_enabled: e.target.checked })}
              />
              Auto-slow for older patients
            </label>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
                After <code>verify_patient</code> succeeds, the runtime uses the patient's
                date of birth to drop the playback rate one step if the patient is older:
                <strong> 65+ → 0.80x</strong>, <strong>75+ → 0.76x</strong>, <strong>85+ → 0.72x</strong>.
                Never speeds up above the base rate. Applied once per call.
              </div>
            </details>
          </div>

          {/* Trouble-based auto-adjust */}
          <div className="form-field">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={llmConfig.twilio_adaptive_trouble_enabled === true}
                onChange={(e) => updateLlm({ twilio_adaptive_trouble_enabled: e.target.checked })}
              />
              Auto-slow when patient signals trouble listening
            </label>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
                Watches patient utterances for phrases like <em>"what?"</em>, <em>"can you
                repeat"</em>, <em>"I can't hear you"</em>, <em>"slower"</em>, <em>"speak
                up"</em>. Each match decrements the rate by the step below, down to the
                floor. Adjustment happens mid-call — the caller hears the slowdown on the
                agent's next utterance.
              </div>
            </details>
          </div>

          {(llmConfig.twilio_adaptive_age_enabled === true || llmConfig.twilio_adaptive_trouble_enabled === true) && (
            <div className="config-grid-2" style={{ marginTop: 8 }}>
              <div className="form-field">
                <label>
                  Minimum Rate — {(llmConfig.twilio_adaptive_min_rate ?? 0.70).toFixed(2)}x
                </label>
                <input
                  type="range"
                  min="0.60" max="0.85" step="0.01"
                  value={llmConfig.twilio_adaptive_min_rate ?? 0.70}
                  onChange={(e) => updateLlm({ twilio_adaptive_min_rate: parseFloat(e.target.value) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  Floor for all adaptive slowdowns. The agent will never go slower than this.
                </p>
              </div>
              <div className="form-field">
                <label>
                  Trouble Step — {(llmConfig.twilio_adaptive_trouble_step ?? 0.04).toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0.01" max="0.10" step="0.01"
                  value={llmConfig.twilio_adaptive_trouble_step ?? 0.04}
                  onChange={(e) => updateLlm({ twilio_adaptive_trouble_step: parseFloat(e.target.value) })}
                  style={{ width: "100%" }}
                />
                <p className="muted small" style={{ marginTop: 4, fontSize: 11 }}>
                  How much to slow per trouble signal. Higher = bigger jumps.
                </p>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Phone audio gate moved to Audio tab. */}
        {false && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
            Inbound Audio Gate
          </h4>
          <div className="form-field">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={llmConfig.phone_vad_enabled !== false}
                onChange={(e) => updateLlm({ phone_vad_enabled: e.target.checked })}
              />
              Enable voice-activity gate on inbound phone audio
            </label>
            <details className="help-details">
              <summary className="help-summary">Learn more</summary>
              <div className="help-body">
              Server-side gate applied in the Twilio bridge. Filters background noise
              so it doesn't trigger Gemini barge-in mid-response. Does not affect
              browser voice calls (those use the Mic Noise Gate in the LLM tab).
              Changes apply to the next incoming call — no server restart needed.
              </div>
            </details>
          </div>

          {llmConfig.phone_vad_enabled !== false && (
            <>
              <div className="form-field">
                <label>
                  Open Threshold — {((llmConfig.phone_vad_open_threshold ?? 0.008) * 1000).toFixed(1)}
                  <span className="muted small" style={{ marginLeft: 8 }}>
                    RMS level that starts forwarding speech
                  </span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, color: "#999" }}>Sensitive</span>
                  <input
                    type="range"
                    min="0.002"
                    max="0.03"
                    step="0.001"
                    value={llmConfig.phone_vad_open_threshold ?? 0.008}
                    onChange={(e) => updateLlm({ phone_vad_open_threshold: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 11, color: "#999" }}>Aggressive</span>
                </div>
              </div>

              <div className="form-field">
                <label>
                  Close Threshold — {((llmConfig.phone_vad_close_threshold ?? 0.003) * 1000).toFixed(1)}
                  <span className="muted small" style={{ marginLeft: 8 }}>
                    RMS level below which the gate begins to close (hysteresis)
                  </span>
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 11, color: "#999" }}>Sensitive</span>
                  <input
                    type="range"
                    min="0.001"
                    max="0.02"
                    step="0.001"
                    value={llmConfig.phone_vad_close_threshold ?? 0.003}
                    onChange={(e) => updateLlm({ phone_vad_close_threshold: parseFloat(e.target.value) })}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 11, color: "#999" }}>Aggressive</span>
                </div>
                <details className="help-details">
                  <summary className="help-summary">Learn more</summary>
                  <div className="help-body">
                  Must be lower than the open threshold. Prevents flapping on syllable
                  boundaries — once speech starts, quieter consonants keep the gate open.
                  </div>
                </details>
              </div>

              <div className="form-field">
                <label>
                  Hangover — {llmConfig.phone_vad_hangover_ms ?? 400} ms
                  <span className="muted small" style={{ marginLeft: 8 }}>
                    How long to keep the gate open after speech detected
                  </span>
                </label>
                <input
                  type="range"
                  min="100"
                  max="1500"
                  step="50"
                  value={llmConfig.phone_vad_hangover_ms ?? 400}
                  onChange={(e) => updateLlm({ phone_vad_hangover_ms: parseInt(e.target.value, 10) })}
                  style={{ width: "100%" }}
                />
                <details className="help-details">
                  <summary className="help-summary">Learn more</summary>
                  <div className="help-body">
                  Too short = words get chopped into fragments.
                  Too long = noise during pauses gets sent to Gemini.
                  400ms is a good default.
                  </div>
                </details>
              </div>
            </>
          )}
        </div>
        )}

        {/* Azure section (placeholder for future) */}
        <div>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>
            Azure Communication Services
          </h4>
          <p className="muted small">
            Azure integration coming soon. Configure Azure Communication Services phone numbers here.
          </p>
        </div>
      </div>
      )}

      {configSubTab === "chain" && (
        <ChainAnimationTab />
      )}

      {/* "chain-prompts" subtab removed in PR3 — the editor is now
          consolidated under Agents → Built-in Agents. ChainPromptsTab
          is still rendered from BuiltInAgentsSection when an operator
          clicks into an individual built-in agent. */}

      </fieldset>
    </>
  );
}

function SubCapabilityRow({ cap, on, onToggle, extraField }) {
  const locked = !!cap.locked;
  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: "var(--radius-sm)",
        background: locked ? "#fafafa" : on ? "#eef5ff" : "#fafafa",
        border: `1px solid ${
          locked ? "var(--color-border)" : on ? "#c5dcff" : "var(--color-border)"
        }`,
        marginBottom: 10,
        opacity: locked ? 0.75 : 1,
      }}
    >
      <label
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-start",
          cursor: locked ? "default" : "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={on}
          onChange={onToggle}
          disabled={locked}
          style={{ marginTop: 3 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{cap.label}</div>
          <div className="muted small" style={{ marginTop: 2 }}>
            {cap.description}
          </div>
          {locked && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: "#c0531c",
                fontWeight: 600,
              }}
            >
              {cap.locked}
            </div>
          )}
        </div>
      </label>
      {extraField}
    </div>
  );
}

// ─── Sub-tenant Cost + Usage tab ────────────────────────────────────

// ─── Orchestration tab ──────────────────────────────────────────────
//
// Mirrors the library's Orchestration tab. Reads orchestration config
// from sub_tenant_configs.content.orchestration. The orchestrator is
// NOT a regular agent — it's managed here.

// ─── Post-Call Analysis tab ─────────────────────────────────────────
// Each action runs as a separate LLM call after every call ends.
// Tenants can enable/disable, reorder, and customize the prompts.

function PostCallActionsTab({ tid, sid, mode = "draft", isLive = false }) {
  const [actions, setActions] = useState([]);
  const [postCallSettings, setPostCallSettings] = useState({ mode: "single", model: "gemini-2.5-flash" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSubTenantConfig(tid, sid, mode);
      setActions(res.data?.content?.post_call_actions || []);
      setPostCallSettings(res.data?.content?.post_call_settings || { mode: "single", model: "gemini-2.5-flash" });
      setError(null);
    } catch (err) {
      if (err?.status === 404) setActions([]);
      else setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid, mode]);

  const refreshFromLibrary = async () => {
    if (!window.confirm(
      "Replace all post-call actions with the latest library versions?\n\n" +
      "This will overwrite any customizations you've made. " +
      "Your enable/disable settings will be preserved where action IDs match."
    )) return;
    setRefreshing(true);
    setError(null);
    try {
      // Fetch library actions (live = published)
      const libRes = await api.listPostCallActions("live");
      const libActions = (libRes.data || []).map((a) => ({
        id: a.id,
        name: a.name,
        description: a.description,
        order: a.order,
        prompt: a.prompt,
        // Preserve the tenant's enable/disable state if the action existed before
        enabled: actions.find((t) => t.id === a.id)?.enabled ?? a.enabled,
      }));

      // Read current config, merge, write to draft
      const cfgRes = await api.getSubTenantConfig(tid, sid, mode);
      const content = cfgRes.data?.content || {};
      await api.writeSubTenantConfig(tid, sid, {
        mode: "draft",
        author: "refresh-from-library",
        commit_msg: "Refresh post-call actions from library",
        content: { ...content, post_call_actions: libActions, post_call_settings: postCallSettings },
      });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { reload(); }, [reload]);

  const saveActions = async (newActions) => {
    setBusy(true);
    setError(null);
    try {
      const cfgRes = await api.getSubTenantConfig(tid, sid, mode);
      const content = cfgRes.data?.content || {};
      await api.writeSubTenantConfig(tid, sid, {
        mode: "draft",
        author: "post-call-editor",
        commit_msg: "Edit post-call actions",
        content: { ...content, post_call_actions: newActions, post_call_settings: postCallSettings },
      });
      setActions(newActions);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleAction = async (actionId, enabled) => {
    const updated = actions.map((a) =>
      a.id === actionId ? { ...a, enabled } : a
    );
    await saveActions(updated);
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm(`Delete post-call action "${actionId}"?`)) return;
    await saveActions(actions.filter((a) => a.id !== actionId));
  };

  const saveEditing = async () => {
    if (!editing) return;
    const updated = actions.map((a) =>
      a.id === editing.id ? editing : a
    );
    await saveActions(updated);
    setEditing(null);
  };

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  // Editing a single action
  if (editing) {
    return (
      <fieldset disabled={isLive} style={{ border: "none", padding: 0, margin: 0 }}>
        <button className="btn sm mb-16" onClick={() => setEditing(null)}>
          ← Back to actions
        </button>
        <div className="card">
          <div className="card-header">
            <h3>{editing.name}</h3>
          </div>
          <div className="agent-config-grid">
            <div className="form-field">
              <label>Name</label>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label>Order</label>
              <input
                type="number"
                min={1}
                value={editing.order}
                onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>
          <div className="form-field">
            <label>Description</label>
            <textarea
              rows={2}
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              style={{
                width: "100%", padding: 8,
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-sm)", fontSize: 13, resize: "vertical",
              }}
            />
          </div>
          <div className="form-field">
            <label>
              Analysis Prompt{" "}
              <span className="muted small">
                ({(editing.prompt || "").length.toLocaleString()} chars)
              </span>
            </label>
            <textarea
              value={editing.prompt || ""}
              onChange={(e) => setEditing({ ...editing, prompt: e.target.value })}
              spellCheck="false"
              style={{
                width: "100%", minHeight: 400, padding: 12,
                fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-sm)", resize: "vertical",
                background: "#fafafa",
              }}
            />
          </div>
          <div className="btn-row" style={{ marginTop: 12 }}>
            <button className="btn sm" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn primary sm" onClick={saveEditing} disabled={busy}>
              {busy ? "Saving..." : "Save Action"}
            </button>
          </div>
        </div>
      </fieldset>
    );
  }

  // Action list view
  return (
    <fieldset disabled={isLive} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="mb-16" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <p className="muted small" style={{ margin: 0 }}>
          Each action runs as a separate LLM call after every call ends.
          Enable or disable individual checks. Click Edit to customize.
          Actions are inherited from the{" "}
          <a href="/library/draft/post-call-actions" target="_blank" rel="noopener">
            Library
          </a>.
        </p>
        {!isLive && (
          <button
            className="btn sm"
            onClick={refreshFromLibrary}
            disabled={refreshing || busy}
          >
            {refreshing ? "Refreshing..." : "Refresh from Library"}
          </button>
        )}
      </div>

      {/* Post-call settings */}
      <div style={{
        display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
        padding: "10px 14px", marginBottom: 12,
        background: "var(--color-bg-subtle)", border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-sm)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Execution Mode</label>
          <select
            value={postCallSettings.mode || "single"}
            onChange={(e) => {
              const updated = { ...postCallSettings, mode: e.target.value };
              setPostCallSettings(updated);
              // Auto-save settings
              api.getSubTenantConfig(tid, sid, mode).then((cfgRes) => {
                api.writeSubTenantConfig(tid, sid, {
                  mode: "draft", author: "post-call-settings", commit_msg: "Update post-call settings",
                  content: { ...(cfgRes.data?.content || {}), post_call_settings: updated },
                });
              });
            }}
            disabled={isLive}
            style={{ padding: "4px 8px", fontSize: 12, borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
          >
            <option value="single">Single LLM call (recommended — saves ~80% tokens)</option>
            <option value="multi">Multiple LLM calls (one per action)</option>
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Model</label>
          <select
            value={postCallSettings.model || "gemini-2.5-flash"}
            onChange={(e) => {
              const updated = { ...postCallSettings, model: e.target.value };
              setPostCallSettings(updated);
              api.getSubTenantConfig(tid, sid, mode).then((cfgRes) => {
                api.writeSubTenantConfig(tid, sid, {
                  mode: "draft", author: "post-call-settings", commit_msg: "Update post-call model",
                  content: { ...(cfgRes.data?.content || {}), post_call_settings: updated },
                });
              });
            }}
            disabled={isLive}
            style={{ padding: "4px 8px", fontSize: 12, borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
          >
            <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (cheapest)</option>
            <option value="gemini-2.5-flash">Gemini 2.5 Flash (balanced)</option>
            <option value="gemini-2.5-pro">Gemini 2.5 Pro (best quality)</option>
          </select>
        </div>
      </div>

      {actions.sort((a, b) => (a.order || 99) - (b.order || 99)).map((action, idx) => (
        <div
          key={action.id}
          className="card"
          style={{
            marginBottom: 12,
            opacity: action.enabled ? 1 : 0.6,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: action.enabled ? "#6A1B9A" : "#ccc",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {idx + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                {action.name}
              </div>
              <div className="muted small mono" style={{ marginTop: 2 }}>
                {action.id} · Order: {action.order}
              </div>
              <p className="muted small" style={{ marginTop: 6 }}>
                {action.description}
              </p>
              <div className="muted small" style={{ marginTop: 4 }}>
                Prompt: {(action.prompt || "").length.toLocaleString()} chars
              </div>
            </div>
            <div className="btn-row" style={{ flexShrink: 0 }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: isLive ? "default" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={action.enabled}
                  onChange={(e) => toggleAction(action.id, e.target.checked)}
                  disabled={isLive}
                />
                <span className="small">
                  {action.enabled ? "On" : "Off"}
                </span>
              </label>
              <button
                className="btn sm"
                onClick={() => setEditing({ ...action })}
                disabled={isLive}
              >
                Edit
              </button>
              <button
                className="btn sm"
                style={{ color: "#c62828" }}
                onClick={() => deleteAction(action.id)}
                disabled={isLive}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {actions.length === 0 && (
        <div className="muted small" style={{ padding: 16, textAlign: "center" }}>
          No post-call actions configured. They'll be seeded when the
          tenant is created.
        </div>
      )}
    </fieldset>
  );
}


// ─── Voice Call tab ─────────────────────────────────────────────────
// Connects to the Configural server's /ws endpoint for a voice call.
// The configMode (draft/live) determines which version of prompts and
// config the runtime loads — draft for testing, live for production.

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let b = "";
  for (let i = 0; i < bytes.length; i++) b += String.fromCharCode(bytes[i]);
  return btoa(b);
}

function fmtTime() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", second: "2-digit",
  });
}

function TestingTab({ tid, sid, configMode = "draft" }) {
  const [testSubTab, setTestSubTab] = useState("voice");
  return (
    <>
      <div className="tabs mb-16" style={{ borderBottom: "1px solid var(--color-border)" }}>
        {[
          { id: "voice", label: "Voice Call" },
          { id: "text", label: "Text Chat" },
        ].map((t) => (
          <button
            key={t.id}
            className={`tab ${testSubTab === t.id ? "active" : ""}`}
            onClick={() => setTestSubTab(t.id)}
            style={{
              padding: "8px 16px",
              background: "none",
              border: "none",
              borderBottom: testSubTab === t.id ? "2px solid var(--color-primary)" : "2px solid transparent",
              cursor: "pointer",
              fontWeight: testSubTab === t.id ? 600 : 400,
              color: testSubTab === t.id ? "var(--color-primary)" : "var(--color-text-muted)",
              fontSize: 13,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {testSubTab === "voice" && <VoiceCallTab tid={tid} sid={sid} configMode={configMode} />}
      {testSubTab === "text" && <TextChatTab tid={tid} sid={sid} configMode={configMode} />}
    </>
  );
}

// ─── Shared call UI components ─────────────────────────────────────

function CallToolEvent({ name, args, result, time, durationMs }) {
  const isError = result?.error;
  const agentCtx = result?._agent_context;
  // Strip _agent_context from display
  const displayResult = result ? Object.fromEntries(Object.entries(result).filter(([k]) => k !== "_agent_context")) : {};
  return (
    <details style={{ margin: "4px 16px", borderRadius: 6, background: "#fafafa", border: "1px solid #e8e8e8" }}>
      <summary style={{
        padding: "6px 10px", cursor: "pointer", fontSize: 12,
        fontFamily: "var(--font-mono)", fontWeight: 600,
        color: isError ? "#c62828" : "#555",
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <span style={{
          display: "inline-block", width: 6, height: 6, borderRadius: "50%",
          background: isError ? "#ef5350" : "#66bb6a", flexShrink: 0,
        }} />
        {name}
        {durationMs != null && <span style={{ fontWeight: 400, color: "#1565c0", fontSize: 10 }}>{durationMs}ms</span>}
        {time && <span style={{ fontWeight: 400, color: "#999", marginLeft: "auto", fontSize: 10 }}>{time}</span>}
      </summary>
      <div style={{ padding: "0 10px 8px", fontSize: 11, fontFamily: "var(--font-mono)" }}>
        {args && Object.keys(args).length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <strong style={{ color: "#888", fontSize: 10, textTransform: "uppercase" }}>Input</strong>
            <pre style={{ margin: "2px 0", padding: 6, background: "#fff", borderRadius: 4, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 120, overflow: "auto" }}>
{JSON.stringify(args, null, 2)}</pre>
          </div>
        )}
        {Object.keys(displayResult).length > 0 && (
          <div>
            <strong style={{ color: "#888", fontSize: 10, textTransform: "uppercase" }}>Output</strong>
            <pre style={{ margin: "2px 0", padding: 6, background: "#fff", borderRadius: 4, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 160, overflow: "auto" }}>
{JSON.stringify(displayResult, null, 2).slice(0, 1500)}</pre>
          </div>
        )}
      </div>
    </details>
  );
}

function CallAgentSwitch({ agent, task }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", margin: "6px 0" }}>
      <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
      <span style={{ fontSize: 11, color: "#888", fontWeight: 600, whiteSpace: "nowrap" }}>
        Agent: {agent}{task ? ` — ${task}` : ""}
      </span>
      <div style={{ flex: 1, height: 1, background: "#e0e0e0" }} />
    </div>
  );
}

// Agent color palette — each sub-agent gets a distinct muted color
const AGENT_COLORS = {
  orchestrator:     { border: "#78909c", label: "#607d8b", bg: "transparent" },
  scheduling:       { border: "#4db6ac", label: "#00897b", bg: "#f1f8f6" },
  triage:           { border: "#e57373", label: "#c62828", bg: "#fef5f5" },
  "appointment-prep": { border: "#9575cd", label: "#6a1b9a", bg: "#f8f5fc" },
  sdoh:             { border: "#ffb74d", label: "#e65100", bg: "#fdf6ef" },
  "referral-intake":  { border: "#4fc3f7", label: "#0277bd", bg: "#f2f9fd" },
};
const EXTRA_AGENT_PALETTES = [
  { border: "#a1887f", label: "#5d4037", bg: "#faf5f2" },
  { border: "#f06292", label: "#c2185b", bg: "#fdf2f6" },
  { border: "#aed581", label: "#558b2f", bg: "#f6faf0" },
  { border: "#ba68c8", label: "#7b1fa2", bg: "#f9f2fc" },
  { border: "#4dd0e1", label: "#00838f", bg: "#f0fafb" },
  { border: "#dce775", label: "#9e9d24", bg: "#fafaf0" },
  { border: "#ff8a65", label: "#d84315", bg: "#fdf4f0" },
  { border: "#7986cb", label: "#303f9f", bg: "#f4f5fc" },
];
const _agentColorCache = {};
function getAgentColor(agentKey) {
  if (AGENT_COLORS[agentKey]) return AGENT_COLORS[agentKey];
  if (_agentColorCache[agentKey]) return _agentColorCache[agentKey];
  // Deterministic pick based on string hash so same agent always gets same color
  let hash = 0;
  for (let i = 0; i < agentKey.length; i++) hash = ((hash << 5) - hash + agentKey.charCodeAt(i)) | 0;
  const palette = EXTRA_AGENT_PALETTES[Math.abs(hash) % EXTRA_AGENT_PALETTES.length];
  _agentColorCache[agentKey] = palette;
  return palette;
}

function CallBubble({ role, text, agent, time, isSystem, englishText }) {
  const isPatient = role === "patient";
  const agentKey = (agent || "orchestrator").toLowerCase();
  const ac = getAgentColor(agentKey);
  const label = isSystem ? "System" : isPatient ? "Patient" : agent || "orchestrator";
  const borderColor = isSystem ? "#ffc107" : isPatient ? "#5c6bc0" : ac.border;
  // Show English translation inline if the original text is non-English
  // and we have a translation that differs from the original.
  const showTranslation = englishText && englishText.trim() && englishText.trim() !== (text || "").trim();
  return (
    <div style={{
      padding: "4px 10px",
      margin: "1px 0",
      borderLeft: `3px solid ${borderColor}`,
      background: isSystem ? "#fffde7" : isPatient ? "#f5f7ff" : ac.bg,
      fontSize: 13,
      lineHeight: 1.45,
    }}>
      <span style={{ fontSize: 10, color: isPatient ? "#5c6bc0" : ac.label, fontWeight: 600, marginRight: 6 }}>{label}</span>
      <span style={{ fontSize: 10, color: "#ccc" }}>{time}</span>
      <div style={{ marginTop: 1, fontWeight: isPatient ? 500 : 400, color: isPatient ? "#283593" : "#4a4a4a" }}>{text}</div>
      {showTranslation && (
        <div style={{
          marginTop: 2,
          paddingLeft: 8,
          borderLeft: "2px solid #cfd8dc",
          fontSize: 11,
          color: "#607d8b",
          fontStyle: "italic",
        }}>
          <span style={{ fontSize: 9, fontWeight: 600, color: "#90a4ae", marginRight: 4, textTransform: "uppercase" }}>EN</span>
          {englishText}
        </div>
      )}
    </div>
  );
}

function CallStatusBar({ status, activeAgent, configMode, callType, onStart, onStop }) {
  const isLive = status === "connected";
  const isOff = status === "disconnected" || status === "error";
  const statusColor = isLive ? "#2e7d32" : status === "connecting" || status === "waiting" ? "#e65100" : "#666";
  const statusText = status === "connecting" ? "Connecting..." : status === "waiting" ? "Setting up..." : isLive ? `${callType} active` : "Ready";
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 14px", marginBottom: 12,
      background: isLive ? "#e8f5e9" : "#fafafa",
      border: `1px solid ${isLive ? "#a5d6a7" : "var(--color-border)"}`,
      borderRadius: "var(--radius-sm)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor, display: "inline-block" }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>{statusText}</span>
        {isLive && <span className="badge badge-live" style={{ fontSize: 10 }}>{activeAgent}</span>}
        <span className="muted" style={{ fontSize: 11 }}>{configMode}</span>
      </div>
      <div>
        {isOff ? (
          <button className="btn primary sm" onClick={onStart} style={{ fontSize: 12 }}>
            Start {callType === "Voice Call" ? "Voice Call" : "Text Chat"}
          </button>
        ) : (
          <button className="btn sm" style={{ background: "#d32f2f", color: "#fff", fontSize: 12 }} onClick={onStop}>
            End
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Voice Call tab ─────────────────────────────────────────────────

function VoiceCallTab({ tid, sid, configMode = "draft" }) {
  const [status, setStatus] = useState("disconnected");
  const [transcript, setTranscript] = useState([]);
  const [activeAgent, setActiveAgent] = useState("orchestrator");
  const [micThreshold, setMicThreshold] = useState(0.003);
  const [callProvider, setCallProvider] = useState("gemini");
  const [voiceModel, setVoiceModel] = useState("");
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const playerRef = useRef(null);
  const workletRef = useRef(null);
  const scrollRef = useRef(null);

  // Load mic sensitivity from config
  useEffect(() => {
    api.getSubTenantConfig(tid, sid, configMode).then((res) => {
      const llm = res.data?.content?.llm_config;
      if (llm?.mic_noise_threshold != null) setMicThreshold(llm.mic_noise_threshold);
      if (llm?.provider) setCallProvider(llm.provider);
      if (llm?.voice_model) setVoiceModel(llm.voice_model);
    }).catch(() => {});
  }, [tid, sid, configMode]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  const addMsg = useCallback((role, text, agent) => {
    if (!text) return;
    setTranscript((prev) => {
      let lastIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i]?.type === "message") { lastIdx = i; break; }
      }
      const last = lastIdx >= 0 ? prev[lastIdx] : null;
      if (last && last.role === role) {
        if (text.startsWith(last.text)) return [...prev.slice(0, lastIdx), { ...last, text }, ...prev.slice(lastIdx + 1)];
        if (text === last.text) return prev;
        if (last.text.includes(text)) return prev;
        return [...prev.slice(0, lastIdx), { ...last, text: last.text + text }, ...prev.slice(lastIdx + 1)];
      }
      return [...prev, { type: "message", role, text, agent, time: fmtTime() }];
    });
  }, []);

  const handleMsg = useCallback((str) => {
    let msg;
    try { msg = JSON.parse(str); } catch { return; }

    if (msg.type === "ready") {
      setStatus("connected");
      wsRef.current?.send(JSON.stringify({
        client_content: { turns: [{ role: "user", parts: [{ text: "[Patient connected. Begin greeting.]" }] }], turn_complete: true },
      }));
      return;
    }

    if (msg.type === "interrupted") {
      playerRef.current?.flush();
      return;
    }

    if (msg.type === "error") {
      setTranscript((p) => [...p, { type: "message", role: "system", text: `Error: ${msg.message}`, time: fmtTime() }]);
      return;
    }

    const sc = msg.serverContent || msg.server_content;
    if (sc) {
      if (sc.interrupted) playerRef.current?.flush();
      const turn = sc.modelTurn || sc.model_turn;
      if (turn?.parts) {
        for (const p of turn.parts) {
          const d = p.inlineData || p.inline_data;
          if (d?.data) playerRef.current?.play(d.data);
        }
      }
      const iT = sc.inputTranscription || sc.input_transcription;
      if (iT?.text) { playerRef.current?.flush(); addMsg("patient", iT.text, activeAgent); }
      const oT = sc.outputTranscription || sc.output_transcription;
      if (oT?.text) addMsg("agent", oT.text, activeAgent);
      if (sc.turnComplete || sc.turn_complete) setTranscript((p) => [...p, { type: "divider" }]);
    }

    if (msg.type === "tool_result") {
      const ctx = msg.result?._agent_context;
      if (ctx) setActiveAgent(ctx.active_agent);
      if (msg.name === "route_to_agent" && msg.result?.routed) {
        setTranscript((p) => [...p, { type: "switch", agent: msg.result.agent, task: msg.result.task?.description }]);
      }
      setTranscript((p) => [...p, { type: "tool", name: msg.name, args: msg.args, result: msg.result, time: fmtTime() }]);
      if (msg.name === "end_call") setTimeout(() => disconnect(), 3000);
    }
    // Session restart handoff: update the badge when the new session is
    // ready. Don't flush audio here — the server already sent an
    // "interrupted" signal before the restart to clear old-session audio,
    // and by now the NEW session may already be speaking. Flushing here
    // would kill the new agent's first words.
    if (msg.type === "session_restarted" && msg.agent) {
      setActiveAgent(msg.agent);
      setTranscript((p) => [...p, { type: "switch", agent: msg.agent, task: "session restarted" }]);
    }
  }, [addMsg, activeAgent]);

  const startMic = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 },
    });
    streamRef.current = stream;
    const ctx = new AudioContext({ sampleRate: 16000 });
    audioCtxRef.current = ctx;
    await ctx.audioWorklet.addModule("/pcm-processor.js");
    const src = ctx.createMediaStreamSource(stream);
    const wk = new AudioWorkletNode(ctx, "pcm-processor");
    workletRef.current = wk;
    // Send noise threshold to the worklet
    wk.port.postMessage({ noiseThreshold: micThreshold });
    wk.port.onmessage = (e) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          realtime_input: { media_chunks: [{ data: arrayBufferToBase64(e.data), mime_type: "audio/pcm;rate=16000" }] },
        }));
      }
    };
    src.connect(wk);
  }, [micThreshold]);

  const connect = useCallback(async () => {
    // Dynamic import of AudioPlayer (avoids SSR issues)
    const { AudioPlayer } = await import("../audio/playback.js");
    playerRef.current = new AudioPlayer();
    // Playback rate depends on the audio source. Different Gemini Live
    // generations were trained on different speech paces — what sounds
    // natural varies per model family:
    //   - Gemini 2.5 Live (gemini-live-2.5-*, chain-gemini): speaks fast,
    //     needs 0.82x to sound natural.
    //   - Gemini 3.1 Flash Live (gemini-3.1-flash-live-*,
    //     chain-gemini-3.1-flash-live): natural pace built in — 0.82x
    //     made it noticeably slow; use 1.0x.
    //   - Cloud TTS (pipeline path): already natural speed → 1.0x.
    const vm = typeof voiceModel === "string" ? voiceModel : "";
    let rate = 1.0;
    if (/3\.1.*live|chain-gemini-3\.1/i.test(vm)) {
      rate = 1.0;                    // 3.1 Flash Live — natural built-in
    } else if (/^gemini-live-2\.5|^chain-gemini$/i.test(vm)) {
      rate = 0.82;                   // 2.5 Live — native audio too fast
    } else if (/^gemini.*live|^chain-gemini/i.test(vm)) {
      rate = 0.9;                    // unknown future Gemini Live → moderate
    }
    playerRef.current.setPlaybackRate(rate);

    setStatus("connecting");
    setTranscript([]);
    setActiveAgent("orchestrator");

    const wsUrl = runtimeWsUrl("/ws");
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("waiting");
      ws.send(JSON.stringify({
        type: "configure",
        tenantId: tid,
        subTenantId: sid,
        callMode: "voice",
        configMode,
      }));
    };
    ws.onmessage = (e) => handleMsg(e.data);
    ws.onclose = () => setStatus("disconnected");
    ws.onerror = () => setStatus("error");

    try { await startMic(); } catch { setStatus("error"); }
  }, [handleMsg, startMic, tid, sid, configMode]);

  const disconnect = useCallback(() => {
    wsRef.current?.close(); wsRef.current = null;
    audioCtxRef.current?.close(); audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
    playerRef.current?.stop();
    setStatus("disconnected");
  }, []);

  useEffect(() => () => disconnect(), [disconnect]);

  const isLive = status === "connected";
  const isOff = status === "disconnected" || status === "error";

  return (
    <div>
      <CallStatusBar status={status} activeAgent={activeAgent} configMode={configMode} callType="Voice Call" onStart={connect} onStop={disconnect} />
      <div style={{
        minHeight: 400, maxHeight: "60vh", overflow: "auto",
        border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
        padding: 12, background: "#fff",
      }}>
        {transcript.length === 0 ? (
          <div className="empty-state" style={{ padding: 40 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🎙️</div>
            <div className="muted small">Ready to call. Click "Start Voice Call" and speak naturally.</div>
          </div>
        ) : (
          transcript.map((e, i) => {
            if (e.type === "divider") return <div key={i} style={{ height: 6 }} />;
            if (e.type === "switch") return <CallAgentSwitch key={i} agent={e.agent} task={e.task} />;
            if (e.type === "tool") return <CallToolEvent key={i} name={e.name} args={e.args} result={e.result} time={e.time} durationMs={e.duration_ms} />;
            if (e.type === "message") return <CallBubble key={i} role={e.role} text={e.text} agent={e.agent} time={e.time} isSystem={e.role === "system"} />;
            return null;
          })
        )}
        {isLive && transcript.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0 4px 10px", fontSize: 12, color: "#666", fontStyle: "italic" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#007a5a", display: "inline-block", animation: "pulse 1.5s infinite" }} />
            Listening...
          </div>
        )}
        <div ref={scrollRef} />
      </div>
    </div>
  );
}

// ─── Text Chat tab ──────────────────────────────────────────────────

function TextChatTab({ tid, sid, configMode = "draft" }) {
  const [status, setStatus] = useState("disconnected");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [activeAgent, setActiveAgent] = useState("orchestrator");
  const [inputDisabled, setInputDisabled] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const connect = useCallback(() => {
    setStatus("connecting");
    setMessages([]);
    setActiveAgent("orchestrator");

    const ws = new WebSocket(runtimeWsUrl("/ws"));
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("waiting");
      ws.send(JSON.stringify({
        type: "configure",
        tenantId: tid,
        subTenantId: sid,
        callMode: "text",
        configMode,
      }));
    };

    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }

      if (msg.type === "ready") {
        setStatus("connected");
        setInputDisabled(true); // wait for greeting
        return;
      }

      if (msg.type === "error") {
        setMessages((p) => [...p, { role: "system", text: `Error: ${msg.message}`, time: fmtTime() }]);
        return;
      }

      if (msg.type === "text_response") {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "agent" && last.agent === msg.agent && !last.complete) {
            return [...prev.slice(0, -1), { ...last, text: last.text + msg.text }];
          }
          return [...prev, { role: "agent", text: msg.text, agent: msg.agent, time: fmtTime() }];
        });
      }

      if (msg.type === "turn_complete") {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === "agent") return [...prev.slice(0, -1), { ...last, complete: true }];
          return prev;
        });
        setInputDisabled(false);
      }

      if (msg.type === "tool_result") {
        const ctx = msg.result?._agent_context;
        if (ctx) setActiveAgent(ctx.active_agent);
        setMessages((p) => [...p, { role: "tool", name: msg.name, args: msg.args, result: msg.result, time: fmtTime() }]);
      }
    };

    ws.onclose = () => setStatus("disconnected");
    ws.onerror = () => setStatus("error");
  }, [tid, sid, configMode]);

  const disconnect = useCallback(() => {
    wsRef.current?.close(); wsRef.current = null;
    setStatus("disconnected");
  }, []);

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    setMessages((p) => [...p, { role: "patient", text, time: fmtTime() }]);
    setInput("");
    setInputDisabled(true);
    wsRef.current.send(JSON.stringify({
      client_content: { turns: [{ role: "user", parts: [{ text }] }], turn_complete: true },
    }));
  }, [input]);

  useEffect(() => () => disconnect(), [disconnect]);

  const isLive = status === "connected";
  const isOff = status === "disconnected" || status === "error";

  return (
    <div>
      <CallStatusBar status={status} activeAgent={activeAgent} configMode={configMode} callType="Text Chat" onStart={connect} onStop={disconnect} />
      <div style={{
        minHeight: 400, maxHeight: "60vh", overflow: "auto",
        border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
        padding: 12, background: "#fff",
      }}>
        {messages.length === 0 && isOff ? (
          <div className="empty-state" style={{ padding: 40 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>💬</div>
            <div className="muted small">Click "Start Text Chat" to begin a conversation.</div>
          </div>
        ) : (
          messages.map((m, i) => {
            if (m.role === "tool") return <CallToolEvent key={i} name={m.name} args={m.args} result={m.result} time={m.time} />;
            return <CallBubble key={i} role={m.role} text={m.text} agent={m.agent} time={m.time} isSystem={m.role === "system"} />;
          })
        )}
        <div ref={scrollRef} />
      </div>

      {isLive && (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder={inputDisabled ? "Agent is responding..." : "Type a message..."}
            disabled={inputDisabled}
            style={{
              flex: 1, padding: "8px 12px", fontSize: 13,
              border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
            }}
          />
          <button
            className="btn primary sm"
            onClick={sendMessage}
            disabled={inputDisabled || !input.trim()}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Call Log tab ───────────────────────────────────────────────────

function CallLogTab({ tid, sid, mode = "draft" }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "voice" | "text"
  const [filterStatus, setFilterStatus] = useState("all"); // "all" | "completed" | "error"
  const [filterDirection, setFilterDirection] = useState("all"); // "all" | "inbound" | "outbound"

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listSubTenantConversations(tid, sid, mode);
      setCalls(res.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid, mode]);

  useEffect(() => { reload(); }, [reload]);

  if (loading && calls.length === 0) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  if (selected) {
    return (
      <CallDetail tid={tid} sid={sid} callId={selected} onBack={() => setSelected(null)} />
    );
  }

  // Apply client-side filters
  const filtered = calls.filter((c) => {
    if (filterMode !== "all" && c.mode !== filterMode) return false;
    if (filterStatus !== "all" && c.status !== filterStatus) return false;
    if (filterDirection !== "all") {
      const d = c.direction || "inbound";
      if (d !== filterDirection) return false;
    }
    return true;
  });

  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: 16, borderBottom: "1px solid #e1e4e8", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ margin: 0 }}>
          Call Log{" "}
          <span className="muted small" style={{ fontWeight: 400 }}>
            {filtered.length}{filtered.length !== calls.length ? ` of ${calls.length}` : ""} call{calls.length !== 1 ? "s" : ""}
          </span>
        </h3>
        <div className="btn-row" style={{ gap: 8 }}>
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            style={{ padding: "4px 8px", fontSize: 12, borderRadius: 4, border: "1px solid var(--color-border)" }}
          >
            <option value="all">All modes</option>
            <option value="voice">Voice only</option>
            <option value="text">Text only</option>
          </select>
          <select
            value={filterDirection}
            onChange={(e) => setFilterDirection(e.target.value)}
            style={{ padding: "4px 8px", fontSize: 12, borderRadius: 4, border: "1px solid var(--color-border)" }}
          >
            <option value="all">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ padding: "4px 8px", fontSize: 12, borderRadius: 4, border: "1px solid var(--color-border)" }}
          >
            <option value="all">All statuses</option>
            <option value="completed">Completed</option>
            <option value="error">Errors</option>
          </select>
          <a
            className="btn sm"
            href={runtimeHttpUrl("/last-prompt")}
            target="_blank"
            rel="noopener"
            title="Open the full system prompt + tools that were sent to the LLM for the most recent call on this runtime. Overwritten on every new call."
          >
            View last prompt
          </a>
          <button
            className="btn sm"
            onClick={reload}
            disabled={loading}
          >
            {loading ? "..." : "Refresh"}
          </button>
        </div>
      </div>
      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th>Call ID</th>
            <th>Mode</th>
            <th>Direction</th>
            <th>Patient</th>
            <th>Date</th>
            <th>Duration</th>
            <th className="right">Msgs</th>
            <th className="right">Tools</th>
            <th className="right" title="Time to first token — average per turn (ms)">TTFT avg</th>
            <th>Quality</th>
            <th>Guardrails</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={12} className="table-empty">
                {calls.length === 0
                  ? "No calls recorded yet. Use the Voice Call or Text Chat tab to make a test call."
                  : "No calls match the current filters."}
              </td>
            </tr>
          ) : (
            filtered.map((c) => {
              const qs = c.analysis?.call_quality?.overall_score;
              const gs = c.analysis?.guardrail_compliance?.score;
              return (
                <tr
                  key={c._id}
                  className="clickable-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(c._id)}
                >
                  <td className="mono small">{c._id}</td>
                  <td>
                    <span
                      className={`badge ${c.mode === "voice" ? "badge-accent" : "badge-neutral"}`}
                    >
                      {c.mode === "text" ? "Text" : "Voice"}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const d = c.direction || "inbound";
                      return (
                        <span
                          className={`badge ${d === "outbound" ? "badge-accent" : "badge-neutral"}`}
                          title={d === "outbound" ? "Outbound call" : "Inbound call"}
                        >
                          {d === "outbound" ? "↑ Outbound" : "↓ Inbound"}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <strong>{c.patient_name || "—"}</strong>
                  </td>
                  <td className="muted small">{formatDate(c.started_at)}</td>
                  <td className="mono small">{c.duration || "—"}</td>
                  <td className="right mono small">{c.msg_count ?? "—"}</td>
                  <td className="right mono small">{c.tool_count ?? "—"}</td>
                  <td
                    className="right mono small"
                    title={
                      c.ttft_greeting_ms != null || c.ttft_p95_ms != null
                        ? `Greeting: ${c.ttft_greeting_ms ?? "—"} ms · p50 ${c.ttft_p50_ms ?? "—"} · p95 ${c.ttft_p95_ms ?? "—"}`
                        : ""
                    }
                  >
                    {c.ttft_avg_ms != null ? `${c.ttft_avg_ms} ms` : "—"}
                  </td>
                  <td>
                    {qs != null ? (
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: qs >= 90 ? "#2e7d32" : qs >= 70 ? "#e65100" : "#c62828",
                        }}
                      >
                        {qs}
                      </span>
                    ) : (
                      <span className="subtle">—</span>
                    )}
                  </td>
                  <td>
                    {gs != null ? (
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: gs >= 90 ? "#2e7d32" : gs >= 70 ? "#e65100" : "#c62828",
                        }}
                      >
                        {gs}
                      </span>
                    ) : (
                      <span className="subtle">—</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${c.status === "completed" ? "badge-live" : "badge-stale"}`}
                    >
                      {c.status || "—"}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function OutboundCampaignsTab({ tid, sid, mode = "draft", isLive = false }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQueue, setShowQueue] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  // Queue-call form: operator enters ONLY a patient ID, we look up the rest.
  // patient + pending_tasks populate after lookup.
  const [form, setForm] = useState({
    patient_id: "",
    scheduled_at: "",
    max_retries: 2,
  });
  const [lookup, setLookup] = useState(null);       // { patient, pending_tasks } | null
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [selectedTaskCodes, setSelectedTaskCodes] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [patientListLoading, setPatientListLoading] = useState(false);
  const [patientListError, setPatientListError] = useState(null);

  // Populate the patient dropdown when the modal opens (first time only per
  // modal mount).
  useEffect(() => {
    if (!showQueue || patientList.length > 0 || patientListLoading) return;
    setPatientListLoading(true);
    setPatientListError(null);
    api.listOutboundPatients(tid, sid)
      .then((res) => setPatientList(res.data || []))
      .catch((err) => setPatientListError(err))
      .finally(() => setPatientListLoading(false));
  }, [showQueue, tid, sid, patientList.length, patientListLoading]);

  const TERMINAL = new Set(["completed", "failed", "no_answer", "busy", "voicemail", "expired", "cancelled"]);

  const statusBadgeClass = (s) => {
    if (!s) return "badge-neutral";
    if (["completed"].includes(s)) return "badge-live";
    if (["failed", "no_answer", "busy", "voicemail", "expired"].includes(s)) return "badge-stale";
    if (["cancelled"].includes(s)) return "badge-neutral";
    if (["placing", "ringing", "in_progress"].includes(s)) return "badge-accent";
    return "badge-neutral";
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listOutboundCalls(tid, sid, { limit: 50 });
      setCalls(res.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid]);

  useEffect(() => { reload(); }, [reload]);

  const runLookup = async (patientCode) => {
    const code = (patientCode || form.patient_id || "").trim();
    if (!code) {
      setLookup(null);
      setSelectedTaskCodes([]);
      return;
    }
    setLookupBusy(true);
    setLookupError(null);
    setLookup(null);
    setSelectedTaskCodes([]);
    try {
      const res = await api.lookupOutboundPatient(tid, sid, code);
      const data = res.data || res;
      setLookup(data);
      setSelectedTaskCodes((data.pending_tasks || []).map((t) => t.task_code));
    } catch (err) {
      setLookupError(err);
    } finally {
      setLookupBusy(false);
    }
  };

  const toggleTask = (code) =>
    setSelectedTaskCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const resetForm = () => {
    setForm({ patient_id: "", scheduled_at: "", max_retries: 2 });
    setLookup(null);
    setLookupError(null);
    setSelectedTaskCodes([]);
    setFormError(null);
  };

  const submit = async () => {
    setBusy(true);
    setFormError(null);
    try {
      if (!lookup?.patient) throw new Error("Look up the patient first");
      const selectedTasks = (lookup.pending_tasks || []).filter((t) => selectedTaskCodes.includes(t.task_code));
      // task_dispatcher will pull ALL pending tasks for the patient at call
      // time, so we only need one outbound_call doc per patient. We pick the
      // first selected task's type as the call's primary task_type, and pass
      // the selected task codes through in task_params so the dispatcher can
      // scope to just those if desired.
      const primary = selectedTasks[0] || (lookup.pending_tasks || [])[0];
      const body = {
        task_type: primary?.task_type || "appointment_reminder",
        // Prompts + orchestration get published to live, but configs
        // (LLM creds, Twilio, variables, telephony) stay draft-only. The
        // worker reads the doc's config_mode to know which side to use.
        config_mode: mode === "draft" ? "draft" : "live",
        recipient: {
          patient_id: lookup.patient.patient_id,
          first_name: lookup.patient.first_name,
          last_name:  lookup.patient.last_name,
          phone:      lookup.patient.phone,
          dob:        lookup.patient.dob,
          timezone:   lookup.patient.timezone,
        },
        max_retries: Number(form.max_retries) || 0,
        task_params: {
          selected_task_codes: selectedTaskCodes,
          primary_task: primary
            ? { task_code: primary.task_code, task_type: primary.task_type, title: primary.title }
            : null,
        },
        ...(form.scheduled_at ? { scheduled_at: new Date(form.scheduled_at).toISOString() } : {}),
      };
      await api.createOutboundCall(tid, sid, body);
      setShowQueue(false);
      resetForm();
      await reload();
    } catch (err) {
      setFormError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading && calls.length === 0) return <Loading />;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div style={{ padding: 16, borderBottom: "1px solid #e1e4e8", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ margin: 0 }}>
          Outbound Calls{" "}
          <span className="muted small" style={{ fontWeight: 400 }}>
            {calls.length} call{calls.length !== 1 ? "s" : ""}
          </span>
        </h3>
        <div className="btn-row" style={{ gap: 8 }}>
          <button className="btn sm" onClick={reload} disabled={loading}>
            {loading ? "..." : "Refresh"}
          </button>
          <button className="btn primary sm" onClick={() => setShowQueue(true)}>
            + Queue Call
          </button>
        </div>
      </div>
      <ErrorBanner error={error} />
      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th>Created</th>
            <th>Task</th>
            <th>Recipient</th>
            <th>Status</th>
            <th>Scheduled</th>
            <th className="right">Attempts</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {calls.length === 0 ? (
            <tr>
              <td colSpan={7} className="table-empty">
                No outbound calls queued yet. Click "+ Queue Call" to place one.
              </td>
            </tr>
          ) : (
            calls.map((c) => {
              const terminal = TERMINAL.has(c.status);
              const recip = c.recipient || {};
              const name = [recip.first_name, recip.last_name].filter(Boolean).join(" ") || "—";
              return (
                <tr key={c._id}>
                  <td className="muted small">{formatDate(c.created_at)}</td>
                  <td className="mono small">{c.task_type || "—"}</td>
                  <td>
                    <div><strong>{name}</strong></div>
                    <div className="muted small mono">{recip.phone || ""}</div>
                  </td>
                  <td>
                    <span className={`badge ${statusBadgeClass(c.status)}`}>
                      {c.status || "—"}
                    </span>
                  </td>
                  <td className="muted small">{c.scheduled_at ? formatDate(c.scheduled_at) : "now"}</td>
                  <td className="right mono small">
                    {/* c.attempts is an ARRAY of attempt objects on the doc; we render its length. */}
                    {(Array.isArray(c.attempts) ? c.attempts.length : (c.retry_count ?? 0))}
                    {c.max_retries != null ? ` / ${c.max_retries}` : ""}
                  </td>
                  <td>
                    {!terminal && (
                      <button
                        className="btn sm"
                        onClick={async () => {
                          if (!confirm("Cancel this outbound call?")) return;
                          try {
                            await api.cancelOutboundCall(tid, sid, c._id);
                            await reload();
                          } catch (err) {
                            alert(err?.message || "Cancel failed");
                          }
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {showQueue && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowQueue(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 8, padding: 24,
              minWidth: 480, maxWidth: 640, maxHeight: "85vh", overflow: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Queue Outbound Call</h3>
            <ErrorBanner error={formError} />
            <div style={{ display: "grid", gap: 12 }}>
              {/* Patient dropdown — populated from the data service */}
              <label>
                <div className="muted small" style={{ marginBottom: 3 }}>Patient</div>
                <select
                  value={form.patient_id}
                  onChange={(e) => {
                    const code = e.target.value;
                    setForm((f) => ({ ...f, patient_id: code }));
                    setLookup(null);
                    if (code) runLookup(code);
                  }}
                  disabled={patientListLoading || patientList.length === 0}
                  style={{ width: "100%", padding: 6 }}
                >
                  <option value="">
                    {patientListLoading
                      ? "Loading patients..."
                      : patientList.length === 0
                        ? "No patients found"
                        : `-- select a patient (${patientList.length}) --`}
                  </option>
                  {patientList.map((p) => (
                    <option key={p.patient_id} value={p.patient_id}>
                      {p.patient_id} — {p.first_name} {p.last_name}
                      {p.phone ? ` · ${p.phone}` : ""}
                    </option>
                  ))}
                </select>
                {patientListError && (
                  <div className="muted small" style={{ color: "#c62828", marginTop: 2 }}>
                    Failed to load patients: {patientListError.message || String(patientListError)}
                  </div>
                )}
                {lookupBusy && (
                  <div className="muted small" style={{ marginTop: 2 }}>Looking up patient details…</div>
                )}
              </label>

              {lookupError && (
                <div style={{ padding: 8, background: "#FFEBEE", color: "#c62828", borderRadius: 4, fontSize: 12 }}>
                  {lookupError.message || String(lookupError)}
                </div>
              )}

              {lookup?.patient && (
                <>
                  <div style={{ padding: 10, background: "#F5FBF5", border: "1px solid #C8E6C9", borderRadius: 4, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {lookup.patient.first_name} {lookup.patient.last_name}
                    </div>
                    <div className="muted small">
                      <div>ID: <span className="mono">{lookup.patient.patient_id}</span></div>
                      <div>Phone: <span className="mono">{lookup.patient.phone || "—"}</span></div>
                      <div>DOB: {lookup.patient.dob || "—"} · TZ: {lookup.patient.timezone || "—"}</div>
                    </div>
                  </div>

                  <div>
                    <div className="muted small" style={{ marginBottom: 4 }}>
                      Pending Outbound Tasks ({(lookup.pending_tasks || []).length})
                    </div>
                    {(lookup.pending_tasks || []).length === 0 ? (
                      <div className="muted small" style={{ padding: 8, fontStyle: "italic" }}>
                        No pending tasks — Sandy will still reach out and use task_dispatcher to find anything created by call time.
                      </div>
                    ) : (
                      <div style={{ border: "1px solid #E0E0E0", borderRadius: 4, maxHeight: 200, overflowY: "auto" }}>
                        {lookup.pending_tasks.map((t) => (
                          <label
                            key={t.task_code}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              padding: "6px 10px",
                              borderBottom: "1px solid #F0F0F0",
                              cursor: "pointer",
                              background: selectedTaskCodes.includes(t.task_code) ? "#F5FBF5" : "#FFF",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTaskCodes.includes(t.task_code)}
                              onChange={() => toggleTask(t.task_code)}
                              style={{ marginTop: 2 }}
                            />
                            <div style={{ flex: 1, fontSize: 12 }}>
                              <div style={{ fontWeight: 600 }}>
                                {t.title || t.task_type}{" "}
                                <span className="muted small" style={{ marginLeft: 6 }}>
                                  [{t.task_type}]
                                </span>
                              </div>
                              <div className="muted small" style={{ marginTop: 2 }}>
                                <span className="mono">{t.task_code}</span>
                                {t.priority && <> · priority: {t.priority}</>}
                                {t.due_date && <> · due: {t.due_date}</>}
                              </div>
                              {t.description && (
                                <div className="muted small" style={{ marginTop: 2, fontStyle: "italic" }}>
                                  {t.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10 }}>
                    <label>
                      <div className="muted small" style={{ marginBottom: 3 }}>Scheduled At (empty = now)</div>
                      <input
                        type="datetime-local"
                        value={form.scheduled_at}
                        onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
                        style={{ width: "100%", padding: 6 }}
                      />
                    </label>
                    <label>
                      <div className="muted small" style={{ marginBottom: 3 }}>Max Retries</div>
                      <input
                        type="number"
                        min="0"
                        value={form.max_retries}
                        onChange={(e) => setForm((f) => ({ ...f, max_retries: e.target.value }))}
                        style={{ width: "100%", padding: 6 }}
                      />
                    </label>
                  </div>
                </>
              )}
            </div>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn sm" onClick={() => { setShowQueue(false); resetForm(); }} disabled={busy}>Cancel</button>
              <button
                className="btn primary sm"
                onClick={submit}
                disabled={busy || !lookup?.patient}
                title={!lookup?.patient ? "Look up a patient first" : ""}
              >
                {busy ? "Queuing..." : "Queue Call"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CallDetail({ tid, sid, callId, onBack }) {
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailTab, setDetailTab] = useState("chain");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getSubTenantConversation(tid, sid, callId);
        setCall(res.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid, callId]);

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!call) return null;

  // Parse _raw if present — single-call mode may produce multiple JSON blocks
  const rawAnalysis = call.analysis || {};
  let analysis = rawAnalysis;
  if (rawAnalysis._raw && Object.keys(rawAnalysis).length <= 1) {
    try {
      const merged = {};
      const blocks = rawAnalysis._raw.split(/```json?\s*/i).filter(Boolean).map((b) => b.replace(/```\s*$/i, "").trim()).filter(Boolean);
      for (const block of blocks) {
        try { Object.assign(merged, JSON.parse(block)); } catch {
          const m = block.match(/\{[\s\S]*\}/);
          if (m) try { Object.assign(merged, JSON.parse(m[0])); } catch {}
        }
      }
      if (Object.keys(merged).length > 0) analysis = merged;
    } catch {}
  }
  const qualityScore = analysis?.call_quality?.overall_score;
  const guardrailScore = analysis?.guardrail_compliance?.score;

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button className="btn sm" onClick={onBack}>← Back</button>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            className="btn sm"
            onClick={() => {
              const lines = [`Call Transcript — ${call._id}`, `Date: ${formatDate(call.started_at)}`, `Duration: ${call.duration || "—"}`, `Mode: ${call.mode}`, `Status: ${call.status}`, "=".repeat(60), ""];
              for (const e of (call.events || [])) {
                if (e.type === "transcript") {
                  const speaker = e.role === "patient" ? "Patient" : `Agent (${e.agent || "orchestrator"})`;
                  lines.push(`${speaker}: ${e.text}`);
                } else if (e.type === "tool_call") {
                  lines.push(`[TOOL] ${e.name}(${JSON.stringify(e.args || {}).slice(0, 200)})`);
                }
              }
              const blob = new Blob([lines.join("\n")], { type: "text/plain" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `${call._id}-transcript.txt`;
              a.click();
            }}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <span style={{ fontSize: 14 }}>📄</span> Transcript
          </button>
          {call.audio_file && (
            <button
              className="btn sm"
              onClick={() => {
                const a = document.createElement("a");
                a.href = runtimeHttpUrl(`/audio/${call._id}`);
                a.download = `${call._id}.wav`;
                a.click();
              }}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <span style={{ fontSize: 14 }}>💾</span> Audio
            </button>
          )}
        </div>
      </div>

      {/* Audio player */}
      {call.audio_file && (
        <div style={{
          marginBottom: 12, padding: "8px 12px",
          background: "#fafafa", border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#666", minWidth: 40 }}>Audio</span>
          <audio controls preload="none" style={{ flex: 1, height: 32 }}>
            <source src={runtimeHttpUrl(`/audio/${call._id}`)} type="audio/wav" />
          </audio>
          <span className="muted" style={{ fontSize: 10 }}>
            {call.audio_bytes ? `${Math.round(call.audio_bytes / 1024)} KB` : ""}
          </span>
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
          padding: "12px 16px",
          background: "#fafafa",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-sm)",
          alignItems: "center",
        }}
      >
        <span className="mono small" style={{ fontWeight: 600 }}>{call._id}</span>
        <span className="badge badge-accent">{call.mode === "text" ? "Text" : "Voice"}</span>
        {call.provider && <span className="badge" style={{ background: call.provider === "chain" ? "#e8eaf6" : call.provider === "pipeline" ? "#fff3e0" : "#e8f5e9", color: "#333", fontSize: 11 }}>{call.provider}{call.provider === "chain" ? " (chain)" : ""}</span>}
        <span className="muted small">{call.duration || "—"}</span>
        <span className="muted small">{call.msg_count || (call.transcript || []).length} msgs</span>
        <span className="muted small">{call.tool_count || (call.tool_calls || []).length} tools</span>
        {call.usage_summary && (() => {
          const us = call.usage_summary;
          const cb = us.cost_breakdown;
          const hasBreakdown = cb && (cb.stt_usd || cb.llm_usd || cb.tts_usd || cb.audio_usd);
          const tooltip = hasBreakdown
            ? `STT: $${(cb.stt_usd || 0).toFixed(4)} · LLM: $${(cb.llm_usd || 0).toFixed(4)} · TTS: $${(cb.tts_usd || 0).toFixed(4)} · Audio: $${(cb.audio_usd || 0).toFixed(4)}
STT: ${us.stt_seconds || 0}s · TTS: ${us.tts_chars || 0} chars · LLM: ${us.llm_calls || 0} calls (${(us.llm_input_tokens || 0).toLocaleString()} in / ${(us.llm_output_tokens || 0).toLocaleString()} out / ${(us.llm_cached_tokens || 0).toLocaleString()} cached)`
            : (us.total_input_tokens
                ? `Input: ${us.total_input_tokens} · Output: ${us.total_output_tokens} · Cached: ${us.total_cached_tokens || 0} · API calls: ${us.api_calls}`
                : `Audio: ${us.audio_duration_seconds || 0}s · Provider: ${us.provider || call.provider || "gemini"} · Tools: ${us.api_calls || 0}`);
          const mini = hasBreakdown
            ? ` (L $${(cb.llm_usd || 0).toFixed(3)} · S $${(cb.stt_usd || 0).toFixed(3)} · T $${(cb.tts_usd || 0).toFixed(3)})`
            : "";
          return (
            <span className="muted small" title={tooltip}>
              ~${us.total_estimated_cost_usd?.toFixed(4) || "0.0000"}{mini}
            </span>
          );
        })()}
        {call.ttft_avg_ms != null && (
          <span
            className="muted small"
            title={`Greeting: ${call.ttft_greeting_ms ?? "—"} ms · p50 ${call.ttft_p50_ms ?? "—"} · p95 ${call.ttft_p95_ms ?? "—"} · min ${call.ttft_min_ms ?? "—"} · max ${call.ttft_max_ms ?? "—"}`}
          >
            TTFT {call.ttft_avg_ms} ms avg
          </span>
        )}
        {qualityScore != null && (
          <span style={{
            fontWeight: 700, fontSize: 13, padding: "2px 8px", borderRadius: 4,
            background: qualityScore >= 90 ? "#e8f5e9" : qualityScore >= 70 ? "#fff3e0" : "#ffebee",
            color: qualityScore >= 90 ? "#2e7d32" : qualityScore >= 70 ? "#e65100" : "#c62828",
          }}>
            Quality: {qualityScore}
          </span>
        )}
        {guardrailScore != null && (
          <span style={{
            fontWeight: 700, fontSize: 13, padding: "2px 8px", borderRadius: 4,
            background: guardrailScore >= 90 ? "#e8f5e9" : guardrailScore >= 70 ? "#fff3e0" : "#ffebee",
            color: guardrailScore >= 90 ? "#2e7d32" : guardrailScore >= 70 ? "#e65100" : "#c62828",
          }}>
            Guardrails: {guardrailScore}
          </span>
        )}
        <span className={`badge ${call.status === "completed" ? "badge-live" : "badge-stale"}`}>{call.status}</span>
      </div>

      {call.usage_summary?.cost_breakdown && (
        <CostBreakdown us={call.usage_summary} />
      )}

      {/* Tabbed: Chain (with inline transcript) | Analysis */}
      <div className="btn-row mb-16">
        <button
          className={`btn sm ${detailTab === "chain" ? "primary" : ""}`}
          onClick={() => setDetailTab("chain")}
        >
          Chain & Transcript
        </button>
        <button
          className={`btn sm ${detailTab === "analysis" ? "primary" : ""}`}
          onClick={() => setDetailTab("analysis")}
        >
          Analysis
        </button>
      </div>

      {detailTab === "chain" && (
        <ChainTimeline events={call.events || []} />
      )}

      {detailTab === "analysis" && (
        <div>
          {(!analysis || Object.keys(analysis).length === 0) ? (
            <div className="empty-state">
              <h3>No analysis yet</h3>
              <p>
                Post-call analysis runs automatically after the call ends.
                It may take 10-30 seconds to complete.
              </p>
              <button
                className="btn sm"
                style={{ marginTop: 12 }}
                onClick={async () => {
                  setLoading(true);
                  try {
                    const res = await api.getSubTenantConversation(tid, sid, callId);
                    setCall(res.data);
                  } catch {}
                  setLoading(false);
                }}
              >
                Check again
              </button>
            </div>
          ) : (
            <>
              {/* Summary */}
              {analysis.call_summary && (
                <div className="card">
                  <div className="card-header">
                    <h3>Call Summary</h3>
                  </div>
                  <p>{analysis.call_summary.overview}</p>
                  {analysis.call_summary.topics_discussed?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <strong className="small">Topics:</strong>{" "}
                      {analysis.call_summary.topics_discussed.map((t, i) => (
                        <span key={i} className="badge badge-neutral" style={{ margin: "2px 4px" }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  {analysis.call_summary.outcomes?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <strong className="small">Outcomes:</strong>
                      <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                        {analysis.call_summary.outcomes.map((o, i) => (
                          <li key={i} className="small">{o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {analysis.call_summary.key_facts?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <strong className="small">Key Facts:</strong>
                      <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                        {analysis.call_summary.key_facts.map((f, i) => (
                          <li key={i} className="small">{f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Call Status */}
              {analysis.call_status && (
                <div className="card" style={{ borderColor: analysis.call_status.completed ? "#43a047" : "#e65100" }}>
                  <div className="card-header">
                    <h3>Call Status</h3>
                    <span className={`badge ${analysis.call_status.completed ? "badge-live" : "badge-stale"}`}>
                      {analysis.call_status.completed ? "Completed" : "Incomplete"}
                    </span>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <div><strong className="small">Reason:</strong> <span className="small">{analysis.call_status.completion_reason?.replace(/_/g, " ")}</span></div>
                    <div><strong className="small">Last Agent:</strong> <span className="small">{analysis.call_status.last_active_agent}</span></div>
                    {analysis.call_status.incomplete_task && (
                      <div style={{ marginTop: 4, padding: "4px 8px", background: "#fff3e0", borderRadius: 4 }}>
                        <strong className="small" style={{ color: "#e65100" }}>Incomplete Task:</strong>{" "}
                        <span className="small">{analysis.call_status.incomplete_task}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quality scores */}
              {analysis.call_quality && (
                <div className="card">
                  <div className="card-header">
                    <h3>
                      Call Quality{" "}
                      <span
                        className="badge"
                        style={{
                          background:
                            (analysis.call_quality.overall_score || 0) >= 90
                              ? "#e8f5e9"
                              : (analysis.call_quality.overall_score || 0) >= 70
                              ? "#fff3e0"
                              : "#ffebee",
                          color:
                            (analysis.call_quality.overall_score || 0) >= 90
                              ? "#2e7d32"
                              : (analysis.call_quality.overall_score || 0) >= 70
                              ? "#e65100"
                              : "#c62828",
                          marginLeft: 8,
                        }}
                      >
                        {analysis.call_quality.overall_score}/100
                      </span>
                    </h3>
                  </div>
                  {analysis.call_quality.categories && (
                    <div className="agent-config-grid">
                      {Object.entries(analysis.call_quality.categories).map(
                        ([key, cat]) => (
                          <div key={key} className="form-field">
                            <label>{key.replace(/_/g, " ")}</label>
                            <div>
                              <strong>{cat.score}/100</strong>
                              {cat.notes && (
                                <span className="muted small" style={{ marginLeft: 8 }}>
                                  {cat.notes}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Guardrail compliance */}
              {analysis.guardrail_compliance && (
                <div className="card">
                  <div className="card-header">
                    <h3>
                      Guardrail Compliance{" "}
                      <span
                        className="badge"
                        style={{
                          background:
                            (analysis.guardrail_compliance.score || 0) >= 90
                              ? "#e8f5e9"
                              : "#ffebee",
                          color:
                            (analysis.guardrail_compliance.score || 0) >= 90
                              ? "#2e7d32"
                              : "#c62828",
                          marginLeft: 8,
                        }}
                      >
                        {analysis.guardrail_compliance.score}/100
                      </span>
                    </h3>
                  </div>
                  {(analysis.guardrail_compliance.violations || []).length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <strong className="small" style={{ color: "#c62828" }}>
                        Violations:
                      </strong>
                      {analysis.guardrail_compliance.violations.map((v, i) => (
                        <div
                          key={i}
                          style={{
                            padding: "6px 8px",
                            margin: "4px 0",
                            background: "#ffebee",
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          <strong>{v.type}</strong>{" "}
                          <span className="badge" style={{ background: v.severity === "critical" ? "#c62828" : v.severity === "high" ? "#e65100" : "#ffc107", color: "#fff", fontSize: 10 }}>
                            {v.severity}
                          </span>
                          <div className="muted small" style={{ marginTop: 2 }}>
                            {v.description}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(analysis.guardrail_compliance.positive_observations || []).length > 0 && (
                    <div>
                      <strong className="small" style={{ color: "#2e7d32" }}>
                        Positive:
                      </strong>
                      <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                        {analysis.guardrail_compliance.positive_observations.map((o, i) => (
                          <li key={i} className="small">{o}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Hallucination check */}
              {analysis.hallucination_check && (
                <div className="card" style={{ borderColor: analysis.hallucination_check.detected ? "#f44336" : "#4caf50" }}>
                  <div className="card-header" style={{ justifyContent: "space-between" }}>
                    <h3>Hallucination Check</h3>
                    <span style={{
                      fontWeight: 700, fontSize: 13, padding: "2px 10px", borderRadius: 4,
                      background: analysis.hallucination_check.score >= 90 ? "#e8f5e9" : analysis.hallucination_check.score >= 70 ? "#fff3e0" : "#ffebee",
                      color: analysis.hallucination_check.score >= 90 ? "#2e7d32" : analysis.hallucination_check.score >= 70 ? "#e65100" : "#c62828",
                    }}>
                      {analysis.hallucination_check.score ?? "—"}
                    </span>
                  </div>
                  <p className="muted small" style={{ marginBottom: 8 }}>
                    {analysis.hallucination_check.clean_tool_calls ?? 0} / {analysis.hallucination_check.total_tool_calls_checked ?? 0} tool calls clean
                  </p>
                  {(analysis.hallucination_check.hallucinations || []).length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <strong className="small" style={{ color: "#c62828" }}>Hallucinations detected:</strong>
                      {analysis.hallucination_check.hallucinations.map((h, i) => (
                        <div key={i} style={{
                          margin: "4px 0", padding: "6px 10px", background: "#ffebee",
                          borderLeft: "3px solid #f44336", borderRadius: 4, fontSize: 12,
                        }}>
                          <div><strong>{h.tool || h.type || "Unknown"}</strong>{h.field ? ` → ${h.field}` : ""}</div>
                          <div className="muted">{h.description || h.detail || JSON.stringify(h)}</div>
                          {h.transcript_excerpt && (
                            <div style={{ marginTop: 4, fontStyle: "italic", color: "#666" }}>"{h.transcript_excerpt}"</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {(analysis.hallucination_check.suspicious_but_uncertain || []).length > 0 && (
                    <div>
                      <strong className="small" style={{ color: "#e65100" }}>Suspicious (uncertain):</strong>
                      {analysis.hallucination_check.suspicious_but_uncertain.map((s, i) => (
                        <div key={i} style={{
                          margin: "4px 0", padding: "6px 10px", background: "#fff3e0",
                          borderLeft: "3px solid #ff9800", borderRadius: 4, fontSize: 12,
                        }}>
                          {typeof s === "string" ? s : (s.description || JSON.stringify(s))}
                        </div>
                      ))}
                    </div>
                  )}
                  {!analysis.hallucination_check.detected && (analysis.hallucination_check.hallucinations || []).length === 0 && (
                    <p className="muted small" style={{ color: "#2e7d32" }}>No hallucinations detected. All tool call parameters traced to patient speech or tool responses.</p>
                  )}
                </div>
              )}

              {/* Critical items */}
              {analysis.critical_items && Object.values(analysis.critical_items).some((a) => a?.length > 0) && (
                <div className="card" style={{ borderColor: "#f44336" }}>
                  <div className="card-header">
                    <h3 style={{ color: "#c62828" }}>Critical Items</h3>
                  </div>
                  {Object.entries(analysis.critical_items).map(([key, items]) =>
                    items?.length > 0 ? (
                      <div key={key} style={{ marginBottom: 8 }}>
                        <strong className="small">{key.replace(/_/g, " ")}:</strong>
                        <ul style={{ margin: "2px 0", paddingLeft: 20 }}>
                          {items.map((item, i) => (
                            <li key={i} className="small">{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null
                  )}
                </div>
              )}

              {/* Patient Distress */}
              {analysis.patient_distress && analysis.patient_distress.detected && (
                <div className="card" style={{ borderColor: analysis.patient_distress.level === "severe" ? "#c62828" : analysis.patient_distress.level === "moderate" ? "#e65100" : "#ffc107" }}>
                  <div className="card-header">
                    <h3>Patient Distress</h3>
                    <span className={`badge ${analysis.patient_distress.level === "severe" ? "badge-danger" : analysis.patient_distress.level === "moderate" ? "badge-stale" : "badge-neutral"}`}>
                      {analysis.patient_distress.level}
                    </span>
                  </div>
                  {(analysis.patient_distress.indicators || []).length > 0 && (
                    <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                      {analysis.patient_distress.indicators.map((ind, i) => (
                        <li key={i} className="small">{ind}</li>
                      ))}
                    </ul>
                  )}
                  {analysis.patient_distress.recommendation && (
                    <p className="small" style={{ marginTop: 6, color: "#555" }}>
                      <strong>Recommendation:</strong> {analysis.patient_distress.recommendation}
                    </p>
                  )}
                </div>
              )}

              {/* Patient Preferences */}
              {(analysis.patient_preferences || []).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Patient Preferences</h3>
                  </div>
                  {analysis.patient_preferences.map((pref, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: i < analysis.patient_preferences.length - 1 ? "1px solid #eee" : "none" }}>
                      <div style={{ fontSize: 13 }}>{pref.preference}</div>
                      {pref.context && <div className="muted small" style={{ marginTop: 2 }}>{pref.context}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Next Call Actions (from call-summary) */}
              {(analysis.next_call_actions || []).length > 0 && (
                <div className="card" style={{ borderColor: "#1565c0" }}>
                  <div className="card-header">
                    <h3 style={{ color: "#1565c0" }}>Next Call Actions</h3>
                  </div>
                  {analysis.next_call_actions.map((item, i) => (
                    <div key={i} style={{ padding: "6px 0", borderBottom: i < analysis.next_call_actions.length - 1 ? "1px solid #eee" : "none" }}>
                      <span className={`badge ${item.priority === "high" ? "badge-stale" : "badge-neutral"}`} style={{ marginRight: 8, fontSize: 10 }}>
                        {item.priority || "medium"}
                      </span>
                      <strong style={{ fontSize: 13 }}>{item.action}</strong>
                      {item.agent && <span className="badge badge-accent" style={{ marginLeft: 8, fontSize: 10 }}>{item.agent}</span>}
                      {item.context && <div className="muted small" style={{ marginTop: 2, paddingLeft: 4 }}>{item.context}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Next Call Follow-Up (from patient-preferences) */}
              {(analysis.next_call_follow_up || []).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Next Call Follow-Up</h3>
                  </div>
                  {analysis.next_call_follow_up.map((item, i) => (
                    <div key={i} style={{ padding: "6px 0", borderBottom: i < analysis.next_call_follow_up.length - 1 ? "1px solid #eee" : "none" }}>
                      <span className={`badge ${item.priority === "high" ? "badge-stale" : "badge-neutral"}`} style={{ marginRight: 8, fontSize: 10 }}>
                        {item.priority || "medium"}
                      </span>
                      {item.item}
                      {item.agent && <span className="badge badge-accent" style={{ marginLeft: 8, fontSize: 10 }}>{item.agent}</span>}
                      {item.context && <div className="muted small" style={{ marginTop: 2, paddingLeft: 4 }}>{item.context}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Critical Items (from patient-preferences) */}
              {analysis.critical_items && Object.values(analysis.critical_items).some((a) => a?.length > 0) && (
                <div className="card" style={{ borderColor: "#f44336" }}>
                  <div className="card-header">
                    <h3 style={{ color: "#c62828" }}>Critical Items</h3>
                  </div>
                  {Object.entries(analysis.critical_items).map(([key, items]) =>
                    items?.length > 0 ? (
                      <div key={key} style={{ marginBottom: 6 }}>
                        <strong className="small" style={{ textTransform: "capitalize" }}>{key.replace(/_/g, " ")}:</strong>
                        <ul style={{ margin: "2px 0", paddingLeft: 20 }}>
                          {items.map((item, i) => <li key={i} className="small">{item}</li>)}
                        </ul>
                      </div>
                    ) : null
                  )}
                </div>
              )}

              {/* Follow-Up Items (legacy format) */}
              {(analysis.follow_up_items || []).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Follow-Up Items</h3>
                  </div>
                  {analysis.follow_up_items.map((item, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: i < analysis.follow_up_items.length - 1 ? "1px solid #eee" : "none" }}>
                      <span className={`badge ${item.priority === "urgent" ? "badge-danger" : item.priority === "high" ? "badge-stale" : "badge-neutral"}`} style={{ marginRight: 8, fontSize: 10 }}>
                        {item.priority || "normal"}
                      </span>
                      {item.item || item.action || item.description}
                      {item.timeframe && <span className="muted small" style={{ marginLeft: 8 }}>({item.timeframe})</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Action items */}
              {(analysis.practice_action_items || []).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Practice Action Items</h3>
                  </div>
                  {analysis.practice_action_items.map((a, i) => (
                    <div key={i} style={{ padding: "4px 0", borderBottom: "1px solid #eee" }}>
                      <span className={`badge ${a.priority === "urgent" ? "badge-danger" : a.priority === "high" ? "badge-stale" : "badge-neutral"}`} style={{ marginRight: 8 }}>
                        {a.priority}
                      </span>
                      {a.action}
                    </div>
                  ))}
                </div>
              )}

              {/* Agents involved */}
              {(analysis.agents_involved || []).length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h3>Agents Involved</h3>
                  </div>
                  {analysis.agents_involved.map((a, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: i < analysis.agents_involved.length - 1 ? "1px solid #eee" : "none" }}>
                      <strong>{a.agent_name || a.agent_id}</strong>
                      <span className={`badge ${a.task_completed ? "badge-live" : "badge-stale"}`} style={{ marginLeft: 8 }}>
                        {a.task_completed ? "Completed" : "Incomplete"}
                      </span>
                      <p className="muted small" style={{ margin: "4px 0" }}>{a.summary}</p>
                      {a.key_facts?.length > 0 && (
                        <div className="muted small">
                          Key facts: {a.key_facts.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Raw JSON fallback */}
              {/* Always show raw JSON as expandable detail */}
              <details className="card" style={{ paddingBottom: 8 }}>
                <summary style={{ cursor: "pointer", fontWeight: 600, listStyle: "revert", marginBottom: 8 }}>
                  Raw analysis JSON ({Object.keys(analysis).length} sections)
                </summary>
                <pre style={{
                  margin: 0, padding: 12, background: "#fafafa",
                  border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
                  fontFamily: "var(--font-mono)", fontSize: 11, lineHeight: 1.5,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  maxHeight: "50vh", overflow: "auto",
                }}>
                  {JSON.stringify(analysis, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SubCostTab({ tid, sid }) {
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, u] = await Promise.all([
          api.getSubTenantCostStatus(tid, sid),
          api.getSubTenantLLMUsage(tid, sid, {}),
        ]);
        setStatus(s.data);
        setUsage(u.data || []);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid]);

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;
  if (!status)
    return (
      <div className="empty-state">
        <h3>No cost data</h3>
      </div>
    );

  const pct = status.caps?.monthly_usd
    ? (status.month_to_date.estimated_cost_usd / status.caps.monthly_usd) * 100
    : 0;

  return (
    <>
      <div className="stat-grid">
        <StatCard
          label="Month-to-date"
          value={formatCost(status.month_to_date.estimated_cost_usd)}
          sub={`${status.month_to_date.call_count} calls`}
        />
        <StatCard
          label="Monthly Cap"
          value={
            status.caps?.monthly_usd
              ? formatCost(status.caps.monthly_usd)
              : "—"
          }
          sub={status.caps?.monthly_usd ? `${pct.toFixed(1)}% used` : ""}
        />
        <StatCard
          label="Cost Lock"
          value={status.is_cost_locked ? "LOCKED" : "Active"}
          sub={status.cost_lock_reason || ""}
        />
      </div>

      {status.is_cost_locked && (
        <div className="error-banner">
          <strong>Cost lock active.</strong> New calls are being refused
          until {formatDate(status.cost_locked_until)}.
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Usage aggregation</h3>
        </div>
        {usage.length === 0 ? (
          <p className="muted small">
            No usage rows tagged with this sub-tenant yet. The
            runtime needs to record <span className="mono">sub_tenant_id</span>{" "}
            on every <span className="mono">llm_usage</span> insert for
            this view to populate.
          </p>
        ) : (
          <pre className="prompt-body">{JSON.stringify(usage, null, 2)}</pre>
        )}
      </div>
    </>
  );
}

// ─── Sub-tenant LLM Credentials tab (redacted view) ─────────────────

function SubCredentialsTab({ tid, sid }) {
  const [form, setForm] = useState({
    voice_provider: "gcp",
    voice_gcp_project: "",
    voice_gcp_location: "us-central1",
    voice_credential_ref: "",
    text_provider: "gcp",
    text_gcp_project: "",
    text_gcp_location: "us-central1",
    text_credential_ref: "",
    use_platform_default: true,
    cost_tracking_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSubTenantLLMCredentials(tid, sid);
      if (res.data) {
        setForm((f) => ({
          ...f,
          ...Object.fromEntries(
            Object.entries(res.data).filter(([, v]) => v != null)
          ),
        }));
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setSaved(false); };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateSubTenantLLMCredentials(tid, sid, form);
      setSaved(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <ErrorBanner error={error} />
      <div className="card">
        <div className="card-header" style={{ justifyContent: "space-between" }}>
          <h3>LLM Credentials</h3>
          <button className="btn primary sm" onClick={save} disabled={busy}>
            {busy ? "Saving..." : saved ? "Saved ✓" : "Save credentials"}
          </button>
        </div>
        <p className="muted small mb-16">
          Credentials used by the runtime to authenticate with the LLM
          provider. Credential refs point to entries in the Secrets tab.
        </p>

        <div className="form-field">
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.use_platform_default}
              onChange={(e) => update({ use_platform_default: e.target.checked })}
            />
            Use platform default credentials (no tenant-specific keys needed)
          </label>
        </div>

        {!form.use_platform_default && (
          <>
            <h4 style={{ margin: "16px 0 8px" }}>Voice provider</h4>
            <div className="agent-config-grid">
              <div className="form-field">
                <label>Provider</label>
                <select value={form.voice_provider || "gcp"} onChange={(e) => update({ voice_provider: e.target.value })}>
                  <option value="gcp">Google Cloud (Vertex AI)</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>
              <div className="form-field">
                <label>GCP Project</label>
                <input type="text" value={form.voice_gcp_project || ""} onChange={(e) => update({ voice_gcp_project: e.target.value })} placeholder="my-project-id" />
              </div>
              <div className="form-field">
                <label>GCP Location</label>
                <input type="text" value={form.voice_gcp_location || ""} onChange={(e) => update({ voice_gcp_location: e.target.value })} placeholder="us-central1" />
              </div>
              <div className="form-field">
                <label>Credential secret ref</label>
                <input type="text" value={form.voice_credential_ref || ""} onChange={(e) => update({ voice_credential_ref: e.target.value })} placeholder="my-voice-key" className="mono" />
                <div className="help">Points to a secret in the Secrets tab.</div>
              </div>
            </div>

            <h4 style={{ margin: "16px 0 8px" }}>Text provider</h4>
            <div className="agent-config-grid">
              <div className="form-field">
                <label>Provider</label>
                <select value={form.text_provider || "gcp"} onChange={(e) => update({ text_provider: e.target.value })}>
                  <option value="gcp">Google Cloud (Vertex AI)</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>
              <div className="form-field">
                <label>GCP Project</label>
                <input type="text" value={form.text_gcp_project || ""} onChange={(e) => update({ text_gcp_project: e.target.value })} placeholder="my-project-id" />
              </div>
              <div className="form-field">
                <label>Credential secret ref</label>
                <input type="text" value={form.text_credential_ref || ""} onChange={(e) => update({ text_credential_ref: e.target.value })} placeholder="my-text-key" className="mono" />
              </div>
            </div>
          </>
        )}

        <div className="form-field" style={{ marginTop: 16 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={form.cost_tracking_enabled !== false}
              onChange={(e) => update({ cost_tracking_enabled: e.target.checked })}
            />
            Enable cost tracking (records usage to Cost + Usage tab)
          </label>
        </div>
      </div>
    </>
  );
}

// ─── Sub-tenant Secrets tab ─────────────────────────────────────────

function SubSecretsTab({ tid, sid }) {
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState({ ref: "", value: "", purpose: "", provider: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listSubTenantSecrets(tid, sid);
      setSecrets(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid]);

  useEffect(() => { load(); }, [load]);

  const createSecret = async () => {
    if (!newSecret.ref.trim() || !newSecret.value.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.writeSubTenantSecret(tid, sid, {
        ref: newSecret.ref.trim(),
        value: newSecret.value,
        metadata: {
          purpose: newSecret.purpose.trim() || null,
          provider: newSecret.provider.trim() || null,
        },
      });
      setNewSecret({ ref: "", value: "", purpose: "", provider: "" });
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteSecret = async (ref) => {
    if (!window.confirm(`Delete secret "${ref}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.deleteSubTenantSecret(tid, sid, ref);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const testSecret = async (ref) => {
    setBusy(true);
    try {
      const res = await api.testSubTenantSecret(tid, sid, ref);
      alert(res.data?.ok ? `Secret "${ref}" is resolvable (${res.data.length} chars)` : `Secret "${ref}" failed: ${res.data?.error || "unknown"}`);
    } catch (err) {
      alert(`Test failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <ErrorBanner error={error} />

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 16, borderBottom: "1px solid #e1e4e8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0 }}>Secrets ({secrets.length})</h3>
            <p className="muted small" style={{ margin: "4px 0 0" }}>
              Encrypted at rest (AES-256-GCM). Plaintext never returned by the API.
            </p>
          </div>
          <button className="btn primary sm" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? "Cancel" : "+ New Secret"}
          </button>
        </div>

        {showCreate && (
          <div style={{ padding: 16, background: "#fafafa", borderBottom: "1px solid #e1e4e8" }}>
            <div className="agent-config-grid">
              <div className="form-field">
                <label>Ref (unique key)</label>
                <input type="text" value={newSecret.ref} onChange={(e) => setNewSecret((s) => ({ ...s, ref: e.target.value }))} placeholder="prod-gcp-voice-key" className="mono" />
              </div>
              <div className="form-field">
                <label>Value (plaintext — will be encrypted)</label>
                <input type="password" value={newSecret.value} onChange={(e) => setNewSecret((s) => ({ ...s, value: e.target.value }))} placeholder="paste credential here" />
              </div>
              <div className="form-field">
                <label>Purpose</label>
                <input type="text" value={newSecret.purpose} onChange={(e) => setNewSecret((s) => ({ ...s, purpose: e.target.value }))} placeholder="Voice API key" />
              </div>
              <div className="form-field">
                <label>Provider</label>
                <select value={newSecret.provider} onChange={(e) => setNewSecret((s) => ({ ...s, provider: e.target.value }))}>
                  <option value="">—</option>
                  <option value="gcp">GCP</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="twilio">Twilio</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <button className="btn primary sm" onClick={createSecret} disabled={busy || !newSecret.ref.trim() || !newSecret.value.trim()}>
              {busy ? "Creating..." : "Create Secret"}
            </button>
          </div>
        )}

        <table className="table" style={{ border: "none" }}>
          <thead>
            <tr>
              <th>Ref</th>
              <th>Purpose</th>
              <th>Provider</th>
              <th>Last rotated</th>
              <th>Access</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {secrets.length === 0 ? (
              <tr>
                <td colSpan="6" className="table-empty">
                  No secrets. Click "+ New Secret" to create one.
                </td>
              </tr>
            ) : (
              secrets.map((s) => (
                <tr key={s.ref}>
                  <td className="mono">{s.ref}</td>
                  <td className="muted small">{s.metadata?.purpose || "—"}</td>
                  <td>{s.metadata?.provider || "—"}</td>
                  <td className="muted small">{formatDate(s.last_rotated_at)}</td>
                  <td className="mono small">{s.access_count}</td>
                  <td>
                    <div className="btn-row">
                      <button className="btn sm" onClick={() => testSecret(s.ref)} disabled={busy} title="Test if resolvable">
                        Test
                      </button>
                      <button className="btn sm" onClick={() => deleteSecret(s.ref)} disabled={busy} style={{ color: "#c62828" }} title="Delete">
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Sub-tenant Drafts tab ──────────────────────────────────────────

function DraftDiffModal({ tid, sid, draft, onClose }) {
  const [loading, setLoading] = useState(true);
  const [diffData, setDiffData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let draftContent = "";
        let liveContent = "";
        const kind = draft.kind;
        const agent = draft.agent_id;

        if (kind === "sub_tenant_prompt" && agent) {
          const [draftRes, liveRes] = await Promise.all([
            api.getSubTenantPrompt(tid, sid, agent, "draft"),
            api.getSubTenantPrompt(tid, sid, agent, "live").catch(() => ({ data: null })),
          ]);
          draftContent = draftRes.data?.content || "";
          liveContent = liveRes.data?.content || "";
        } else if (kind === "sub_tenant_config") {
          const [draftRes, liveRes] = await Promise.all([
            api.getSubTenantConfig(tid, sid, "draft"),
            api.getSubTenantConfig(tid, sid, "live").catch(() => ({ data: null })),
          ]);
          draftContent = JSON.stringify(draftRes.data?.content || {}, null, 2);
          liveContent = JSON.stringify(liveRes.data?.content || {}, null, 2);
        } else if (kind === "agent_config" && agent) {
          const [draftRes, liveRes] = await Promise.all([
            api.getAgentConfig(tid, sid, agent, "draft"),
            api.getAgentConfig(tid, sid, agent, "live").catch(() => ({ data: null })),
          ]);
          draftContent = JSON.stringify(draftRes.data?.content || {}, null, 2);
          liveContent = JSON.stringify(liveRes.data?.content || {}, null, 2);
        }

        if (!cancelled) {
          const oldLines = liveContent.split("\n");
          const newLines = draftContent.split("\n");
          const diff = computeDiff(oldLines, newLines);
          const stats = diffStats(diff);
          setDiffData({ diff, stats, draftContent, liveContent });
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tid, sid, draft]);

  const label = draft.agent_id
    ? `${draft.kind.replace("sub_tenant_", "").replace("agent_", "")} — ${draft.agent_id}`
    : draft.kind.replace("sub_tenant_", "");

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "white", borderRadius: 12,
          width: "100%", maxWidth: 900, maxHeight: "90vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 24px", borderBottom: "1px solid #eee",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Draft Changes — {label}</h3>
            <span className="muted small">
              v{draft.version} by {draft.author || "unknown"}
              {draft.commit_msg ? ` — "${draft.commit_msg}"` : ""}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", fontSize: 22, cursor: "pointer",
              color: "#999", lineHeight: 1, padding: "4px 8px",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: loading || error ? 24 : 0 }}>
          {loading && <Loading />}
          {error && <ErrorBanner error={error} />}
          {diffData && (
            <>
              {/* Stats bar */}
              <div style={{
                padding: "8px 24px", background: "#fafafa", borderBottom: "1px solid #eee",
                display: "flex", alignItems: "center", gap: 16, fontSize: 13,
              }}>
                <span style={{ color: "#060" }}>+{diffData.stats.added} added</span>
                <span style={{ color: "#900" }}>−{diffData.stats.removed} removed</span>
                <span className="muted">{diffData.stats.equal} unchanged</span>
                <span style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
                  <span style={{ fontSize: 12 }}>
                    <span style={{
                      display: "inline-block", width: 12, height: 12,
                      background: "#fdd", border: "1px solid #faa",
                      borderRadius: 2, marginRight: 4, verticalAlign: "middle",
                    }} />
                    Live (removed)
                  </span>
                  <span style={{ fontSize: 12 }}>
                    <span style={{
                      display: "inline-block", width: 12, height: 12,
                      background: "#dfd", border: "1px solid #afa",
                      borderRadius: 2, marginRight: 4, verticalAlign: "middle",
                    }} />
                    Draft (added)
                  </span>
                </span>
              </div>

              {diffData.diff.every((d) => d.type === "equal") ? (
                <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
                  <strong>No differences.</strong> Draft matches Live exactly.
                </div>
              ) : (
                <div style={{
                  fontFamily: "var(--font-mono)", fontSize: 12,
                  lineHeight: 1.6, overflow: "auto",
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {diffData.diff.map((line, i) => {
                        const bg = line.type === "removed" ? "#fdd"
                          : line.type === "added" ? "#dfd" : "transparent";
                        const prefix = line.type === "removed" ? "−"
                          : line.type === "added" ? "+" : " ";
                        const color = line.type === "removed" ? "#900"
                          : line.type === "added" ? "#060" : "#444";
                        return (
                          <tr key={i}>
                            <td style={{
                              width: 40, textAlign: "right", padding: "0 8px",
                              color: "#999", background: bg,
                              borderRight: "1px solid #ddd", userSelect: "none",
                            }}>
                              {line.lineNum ?? ""}
                            </td>
                            <td style={{
                              width: 18, textAlign: "center", background: bg,
                              color, fontWeight: 700, userSelect: "none",
                            }}>
                              {prefix}
                            </td>
                            <td style={{
                              padding: "0 8px", background: bg, color,
                              whiteSpace: "pre-wrap", wordBreak: "break-word",
                            }}>
                              {line.text}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Pretty display for the `kind` column in the Revisions table.
function labelForKind(kind) {
  switch (kind) {
    case "sub_tenant_orchestration": return "Orchestrator prompt";
    case "sub_tenant_post_call_actions": return "Post-call actions";
    case "sub_tenant_config": return "Sub-tenant config (legacy)";
    case "sub_tenant_prompt": return "Agent prompt";
    case "agent_config": return "Agent config";
    default: return kind || "—";
  }
}

function SubDraftsTab({ tid, sid }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [diffDraft, setDiffDraft] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setActionResult(null);
    try {
      const res = await api.listSubTenantDrafts(tid, sid);
      setDrafts(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [tid, sid]);

  useEffect(() => {
    reload();
  }, [reload]);

  const publishAll = async () => {
    setBusy(true);
    setActionResult(null);
    try {
      const res = await api.publishAllSubTenantDrafts(tid, sid);
      setActionResult({
        type: "publish",
        published: res.data?.published?.length || 0,
        failed: res.data?.failed?.length || 0,
      });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // Phase 2: publish a single draft row. Routes by kind so the user can
  // promote the orchestrator prompt without touching post-call, voice, etc.
  const publishOne = async (d) => {
    setBusy(true);
    setActionResult(null);
    try {
      if (d.kind === "sub_tenant_orchestration") {
        await api.publishSubTenantOrchestration(tid, sid);
      } else if (d.kind === "sub_tenant_post_call_actions") {
        await api.publishSubTenantPostCall(tid, sid);
      } else if (d.kind === "sub_tenant_prompt" && d.agent_id) {
        await api.publishSubTenantPrompt(tid, sid, d.agent_id);
      } else if (d.kind === "agent_config" && d.agent_id) {
        await api.publishAgentConfig(tid, sid, d.agent_id);
      } else if (d.kind === "sub_tenant_config") {
        // Legacy bundled row — promotes the whole monolith doc as before.
        await api.publishSubTenantConfig?.(tid, sid);
      } else {
        throw new Error(`Don't know how to publish kind=${d.kind}`);
      }
      setActionResult({ type: "publish", published: 1, failed: 0 });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const discardAll = async () => {
    setBusy(true);
    setActionResult(null);
    try {
      const res = await api.discardAllSubTenantDrafts(tid, sid);
      setActionResult({
        type: "discard",
        discarded: res.data?.discarded?.length || 0,
        failed: res.data?.failed?.length || 0,
      });
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading && drafts.length === 0) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  if (drafts.length === 0) {
    return (
      <div className="empty-state">
        <h3>No pending drafts</h3>
        <p>All sub-tenant content is published to Live.</p>
        {actionResult && (
          <p className="muted small" style={{ marginTop: 8 }}>
            {actionResult.type === "publish"
              ? `Published ${actionResult.published} artifact${actionResult.published !== 1 ? "s" : ""}.`
              : `Discarded ${actionResult.discarded} draft${actionResult.discarded !== 1 ? "s" : ""}.`}
            {actionResult.failed > 0 &&
              ` ${actionResult.failed} failed.`}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className="mb-16"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span className="muted small">
          {drafts.length} pending draft{drafts.length !== 1 ? "s" : ""}
        </span>
        <div className="btn-row">
          <button
            className="btn"
            onClick={discardAll}
            disabled={busy}
          >
            {busy ? "Working..." : "Discard All"}
          </button>
          <button
            className="btn primary"
            onClick={publishAll}
            disabled={busy}
          >
            {busy ? "Publishing..." : "Publish All to Live"}
          </button>
        </div>
      </div>

      {actionResult && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            background: actionResult.failed > 0 ? "#fff8f0" : "#f0fff4",
            borderColor: actionResult.failed > 0 ? "#f5c97a" : "#a3e4a5",
          }}
        >
          <p className="muted small" style={{ margin: 0 }}>
            {actionResult.type === "publish"
              ? `Published ${actionResult.published} artifact${actionResult.published !== 1 ? "s" : ""} to Live.`
              : `Discarded ${actionResult.discarded} draft${actionResult.discarded !== 1 ? "s" : ""}.`}
            {actionResult.failed > 0 &&
              ` ${actionResult.failed} failed — check server logs.`}
          </p>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <table className="table" style={{ border: "none" }}>
          <thead>
            <tr>
              <th>Kind</th>
              <th>Agent</th>
              <th>Version</th>
              <th>Author</th>
              <th>Updated</th>
              <th>Commit</th>
              <th style={{ width: 160 }}></th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((d, i) => (
              <tr key={`${d.kind}-${d.agent_id || "config"}-${d.version}-${i}`}>
                <td className="muted small">{labelForKind(d.kind)}</td>
                <td className="mono small">{d.agent_id || "—"}</td>
                <td>v{d.version}</td>
                <td className="muted small">{d.author || "—"}</td>
                <td className="muted small">{formatDate(d.updated_at)}</td>
                <td className="muted small">{d.commit_msg || "—"}</td>
                <td>
                  <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                    <button
                      className="btn small"
                      onClick={() => setDiffDraft(d)}
                      disabled={busy}
                      style={{ fontSize: 12, padding: "2px 10px" }}
                    >
                      Diff
                    </button>
                    <button
                      className="btn small primary"
                      onClick={() => publishOne(d)}
                      disabled={busy}
                      title="Publish only this change to Live"
                      style={{ fontSize: 12, padding: "2px 10px" }}
                    >
                      Publish
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {diffDraft && (
        <DraftDiffModal
          tid={tid}
          sid={sid}
          draft={diffDraft}
          onClose={() => setDiffDraft(null)}
        />
      )}
    </>
  );
}

function AgentConfigsTab({ tid, sid }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.listAgentConfigs(tid, sid);
        setConfigs((res.data || []).sort((a, b) => a.agent_id.localeCompare(b.agent_id)));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tid, sid]);

  if (loading) return <Loading />;
  if (error) return <ErrorBanner error={error} />;

  return (
    <div className="card" style={{ padding: 0 }}>
      <table className="table" style={{ border: "none" }}>
        <thead>
          <tr>
            <th>Agent</th>
            <th>Type</th>
            <th>Enabled</th>
            <th>Tools</th>
            <th>Triggers</th>
          </tr>
        </thead>
        <tbody>
          {configs.length === 0 ? (
            <tr><td colSpan="5" className="table-empty">No agent configs.</td></tr>
          ) : (
            configs.map((a) => {
              const content = a.content || {};
              return (
                <tr key={a.agent_id}>
                  <td><strong>{a.agent_id}</strong></td>
                  <td className="muted small">{content.type || "—"}</td>
                  <td>
                    {content.enabled !== false ? (
                      <span className="badge badge-live">Enabled</span>
                    ) : (
                      <span className="badge badge-neutral">Disabled</span>
                    )}
                  </td>
                  <td className="muted small">{content.tools?.length || 0}</td>
                  <td className="muted small">{content.triggers?.length || 0}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ChainTimeline — pictorial view of the orchestrator chain for a call.
//
// Renders vertically:
//   [Agent card]  ← activation + intent-detections nested as sub-steps + completion
//        ↓
//   <Orchestrator decision chip>
//        ↓
//   [Next agent card]
//        ↓
//   ...
//
// Click any agent card or orchestrator chip → right panel shows the
// full decision data the chain used at that moment.
// ════════════════════════════════════════════════════════════════════
function ChainTimeline({ events }) {
  const all = events || [];
  const hasChain = all.some((e) => e.type === "chain");
  const [selected, setSelected] = useState(null);

  if (all.length === 0) {
    return (
      <div className="empty-state">
        <h3>No events recorded</h3>
        <p className="muted small">This call has no transcript or chain events.</p>
      </div>
    );
  }

  // ── Group events into segments ─────────────────────────────
  // A segment is either:
  //   { kind: "session", agent, activate, items[], complete }
  //     items are a time-ordered mix of: chain sub-events, transcript bubbles,
  //     tool calls, barge-ins — everything that happened while this agent held the floor.
  //   { kind: "decision", event }  (orchestrator dispatch between sessions)
  //   { kind: "preSession", items[] } (transcript before the first agent activation,
  //     or when chain events aren't emitted at all — fallback mode)
  const segments = [];
  let current = null;
  let preSession = null;
  // prompt_snapshot events are emitted inside buildAgentSessionConfig,
  // which fires BEFORE the matching `activate` event. Hold the most
  // recent one for the upcoming activate to consume.
  let pendingPromptSnapshot = null;

  const pushLoose = (item) => {
    if (current) current.items.push(item);
    else {
      if (!preSession) { preSession = { kind: "preSession", items: [] }; segments.push(preSession); }
      preSession.items.push(item);
    }
  };

  for (const e of all) {
    if (e.type === "chain") {
      if (e.subtype === "activate") {
        current = { kind: "session", agent: e.agentId, activate: e, items: [], complete: null, promptSnapshot: null };
        // Consume any pending prompt_snapshot that fired just before this activate.
        if (pendingPromptSnapshot && pendingPromptSnapshot.agentId === e.agentId) {
          current.promptSnapshot = pendingPromptSnapshot;
          pendingPromptSnapshot = null;
        }
        segments.push(current);
        preSession = null;
      } else if (e.subtype === "complete") {
        if (current) { current.complete = e; }
        else pushLoose({ kind: "chain-sub", event: e });
        current = null;
      } else if (e.subtype === "dispatch") {
        segments.push({ kind: "decision", event: e });
      } else if (e.subtype === "prompt_snapshot") {
        // Attach the rendered system prompt + tools to the session that
        // the runtime built it for. The snapshot fires inside
        // buildAgentSessionConfig — it lands in chronological order
        // either just before or just after the matching `activate` event.
        // If we're already inside a session for the same agent → attach.
        // Otherwise, defer onto the next session that opens for this agent.
        if (current && current.agent === e.agentId) {
          current.promptSnapshot = e;
        } else {
          // Look ahead: find next "activate" for this agent and attach there.
          // Cheaper than queueing — just store on a side-channel and consume
          // on the next matching activate. Implemented inline below.
          pendingPromptSnapshot = e;
        }
      } else {
        // classify / enqueue / drop_intent
        pushLoose({ kind: "chain-sub", event: e });
      }
    } else if (e.type === "transcript") {
      pushLoose({ kind: "transcript", event: e });
    } else if (e.type === "tool_call") {
      pushLoose({ kind: "tool_call", event: e });
    } else if (e.type === "barge_in") {
      pushLoose({ kind: "barge_in", event: e });
    }
    // ignore latency events here — too noisy for this view
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(420px, 1fr) minmax(340px, 420px)", gap: 16 }}>
      {/* ── Timeline (pictorial) ── */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="card-header" style={{ padding: "12px 16px" }}>
          <h3>Chain & Transcript</h3>
          <span className="muted small">
            {hasChain ? "Each card = one agent session · messages & tool calls shown inline · click 📄 prompt pill to see what the LLM received" : "No chain events (showing flat transcript) · click any node for details"}
          </span>
        </div>
        <div style={{ padding: "20px 16px", maxHeight: 720, overflowY: "auto" }}>
          {segments.map((seg, idx) => (
            <React.Fragment key={idx}>
              {seg.kind === "session" && (
                <AgentCard
                  session={seg}
                  selected={selected}
                  onSelect={setSelected}
                />
              )}
              {seg.kind === "decision" && (
                <OrchestratorChip
                  event={seg.event}
                  selected={selected?.source === "decision" && selected.idx === idx}
                  onClick={() => setSelected({ source: "decision", idx, event: seg.event })}
                />
              )}
              {seg.kind === "preSession" && (
                <PreSessionBlock
                  items={seg.items}
                  selected={selected}
                  onSelect={setSelected}
                />
              )}
              {idx < segments.length - 1 && <TimelineConnector />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Details panel ── */}
      <div className="card" style={{ padding: 0, position: "sticky", top: 16, alignSelf: "flex-start", maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
        <div className="card-header" style={{ padding: "12px 16px" }}>
          <h3>Decision Data</h3>
        </div>
        <div style={{ padding: 16 }}>
          {!selected ? (
            <p className="muted small">
              Click an agent card or orchestrator node to see the data the chain used at that moment.
            </p>
          ) : (
            <ChainDetails selected={selected} />
          )}
        </div>
      </div>
    </div>
  );
}

// Safely coerce anything to a truncated string for UI display.
function truncStr(v, n = 80) {
  if (v == null) return "";
  const s = typeof v === "string" ? v : (() => { try { return JSON.stringify(v); } catch { return String(v); } })();
  return s.length > n ? s.slice(0, n) : s;
}

// ═══ Agent card — the main visual block ═══
function AgentCard({ session, selected, onSelect }) {
  const { agent, activate, items, complete, promptSnapshot } = session;
  const tAgent = CHAIN_AGENT_META[agent] || { color: "#4A6FA5", accent: "#EAF2F9", icon: "●" };
  const isForced = complete?.forced;
  const isHeaderSelected = selected?.source === "agent-header" && selected.activateTs === activate.timestamp;
  const isPromptSelected = selected?.source === "agent-prompt" && selected.activateTs === activate.timestamp;

  return (
    <div style={{
      border: `1.5px solid ${tAgent.color}`,
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 0,
      background: "#fff",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      {/* Header — click to see activation details */}
      <div
        onClick={() => onSelect({ source: "agent-header", activateTs: activate.timestamp, event: activate })}
        style={{
          background: tAgent.color,
          color: "#fff",
          padding: "10px 14px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: isHeaderSelected ? "3px solid #FFF8DC" : "none",
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 14,
          background: "rgba(255,255,255,0.22)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700,
        }}>{tAgent.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {agent}
          </div>
          {activate.activatingIntent ? (
            <div style={{ fontSize: 12, opacity: 0.9, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              activating intent: "{truncStr(activate.activatingIntent.description, 90)}"
            </div>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              orchestration step (no specific intent)
            </div>
          )}
        </div>
        {promptSnapshot && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onSelect({ source: "agent-prompt", activateTs: activate.timestamp, event: promptSnapshot });
            }}
            title={`View the system prompt this agent received (${promptSnapshot.promptSize} chars, ${promptSnapshot.toolCount} tools)`}
            style={{
              fontSize: 11, fontWeight: 600,
              padding: "3px 9px",
              borderRadius: 12,
              background: isPromptSelected ? "#FFF8DC" : "rgba(255,255,255,0.18)",
              color: isPromptSelected ? "#5b4400" : "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            📄 prompt · {promptSnapshot.promptSize}B · {promptSnapshot.toolCount}🔧
          </div>
        )}
        <div style={{ fontSize: 11, opacity: 0.85 }}>
          {formatTs(activate.timestamp)}
        </div>
      </div>

      {/* Mixed timeline nested inside the agent: transcript bubbles, tool calls, sub-events */}
      {items.length > 0 && (
        <div style={{ padding: "10px 14px 6px 14px", background: "#FAFAFA" }}>
          {items.map((it, i) => (
            <SessionItem
              key={i}
              item={it}
              selected={selected}
              onSelect={onSelect}
              agentColor={tAgent.color}
            />
          ))}
        </div>
      )}

      {/* Footer — completion */}
      {complete && (
        <div
          onClick={() => onSelect({ source: "agent-complete", ts: complete.timestamp, event: complete })}
          style={{
            padding: "8px 14px",
            borderTop: `1px solid ${tAgent.accent}`,
            background: isForced ? "#FFF4E5" : tAgent.accent,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
          }}
        >
          <span style={{ fontWeight: 700, color: isForced ? "#9a6700" : "#2D7A3B" }}>
            {isForced ? "⚠ FORCE-COMPLETED" : "✓ COMPLETED"}
          </span>
          <span className="muted" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {truncStr(complete.summary, 120) || "—"}
          </span>
          <span className="muted small" style={{ fontSize: 11 }}>
            {formatTs(complete.timestamp)}
          </span>
        </div>
      )}
    </div>
  );
}

// ═══ Dispatcher: renders the right thing inside an agent card body ═══
function SessionItem({ item, selected, onSelect, agentColor }) {
  if (item.kind === "chain-sub") {
    return (
      <SubEventRow
        event={item.event}
        selected={selected?.source === "sub" && selected.ts === item.event.timestamp}
        onClick={() => onSelect({ source: "sub", ts: item.event.timestamp, event: item.event })}
      />
    );
  }
  if (item.kind === "transcript") {
    return <TranscriptLine event={item.event} agentColor={agentColor} />;
  }
  if (item.kind === "tool_call") {
    return (
      <ToolCallRow
        event={item.event}
        selected={selected?.source === "tool" && selected.ts === item.event.time}
        onClick={() => onSelect({ source: "tool", ts: item.event.time, event: item.event })}
      />
    );
  }
  if (item.kind === "barge_in") {
    return <BargeInRow event={item.event} />;
  }
  return null;
}

// ═══ Transcript message line inside an agent card ═══
function TranscriptLine({ event, agentColor }) {
  // Recorder uses role="patient"/"agent". Older Gemini Live events use "user"/"model".
  // Check both so this works across providers.
  const isPatient = event.role === "patient" || event.role === "user";
  const bg = isPatient ? "#EEF4FA" : "#FFFFFF";
  const border = isPatient ? "#B0C8DE" : agentColor;
  const label = isPatient ? "PATIENT" : "AGENT";
  const labelColor = isPatient ? "#2E4F72" : agentColor;
  return (
    <div style={{
      display: "flex",
      gap: 10,
      padding: "8px 10px",
      marginBottom: 4,
      background: bg,
      borderLeft: `3px solid ${border}`,
      borderRadius: 4,
      fontSize: 13,
      lineHeight: 1.45,
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        color: labelColor, minWidth: 58, paddingTop: 1,
      }}>
        {label}
      </span>
      <span style={{ flex: 1, color: "#1A1A1A", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {event.text || <span className="muted">(empty)</span>}
        {event.english_text && event.english_text !== event.text && (
          <div className="muted small" style={{ marginTop: 3, fontStyle: "italic" }}>
            → {event.english_text}
          </div>
        )}
      </span>
      {event.time && (
        <span className="muted small" style={{ fontSize: 10, whiteSpace: "nowrap", paddingTop: 2 }}>
          {formatTs(event.time)}
        </span>
      )}
    </div>
  );
}

// ═══ Tool call row (compact, click for full args/result) ═══
function ToolCallRow({ event, selected, onClick }) {
  const { name, args, result, duration_ms } = event;
  const resultSummary = typeof result === "object"
    ? (result?.success === false ? "✗ failed" : "✓ ok")
    : String(result ?? "").slice(0, 40);
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "6px 10px", marginBottom: 4,
        background: selected ? "#FFF8DC" : "#F5F0FA",
        borderLeft: "3px solid #7B4FA5",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 12,
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: 10,
        background: "#7B4FA5", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 700, flexShrink: 0,
      }}>⚙</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#7B4FA5", minWidth: 60, letterSpacing: 0.5 }}>TOOL</span>
      <span style={{ fontWeight: 600, color: "#4A2E72" }}>{name}</span>
      <span className="muted small" style={{ fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        → {resultSummary}
      </span>
      {typeof duration_ms === "number" && (
        <span className="muted small" style={{ fontSize: 10 }}>{duration_ms}ms</span>
      )}
      {event.time && (
        <span className="muted small" style={{ fontSize: 10 }}>{formatTs(event.time)}</span>
      )}
    </div>
  );
}

// ═══ Barge-in pill ═══
function BargeInRow({ event }) {
  return (
    <div style={{
      padding: "4px 10px", margin: "2px 0", fontSize: 11,
      borderLeft: `3px solid ${event.suppressed ? "#ffb74d" : "#ef5350"}`,
      background: event.suppressed ? "#FFF8E1" : "#FFEBEE",
      color: event.suppressed ? "#e65100" : "#c62828",
      borderRadius: 4,
    }}>
      ⚡ Barge-in {event.suppressed ? "(suppressed)" : "(patient interrupted)"}
      {event.timestamp && (
        <span className="muted small" style={{ marginLeft: 8, fontSize: 10 }}>
          {formatTs(event.timestamp)}
        </span>
      )}
    </div>
  );
}

// ═══ Pre-session block — transcript/tool items before the first agent activated,
//     or for whole calls that have no chain events (legacy / non-chain provider) ═══
function PreSessionBlock({ items, selected, onSelect }) {
  return (
    <div style={{
      border: "1px dashed #BFBFBF",
      borderRadius: 8,
      padding: "10px 12px",
      marginBottom: 0,
      background: "#FAFAFA",
    }}>
      <div className="muted small" style={{ marginBottom: 6, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>
        Before first agent activated
      </div>
      {items.map((it, i) => (
        <SessionItem key={i} item={it} selected={selected} onSelect={onSelect} agentColor="#888" />
      ))}
    </div>
  );
}

// ═══ Sub-event row (classify / enqueue / drop) ═══
function SubEventRow({ event, selected, onClick }) {
  const { subtype } = event;
  let icon, color, label, summary;
  if (subtype === "classify") {
    icon = "?"; color = "#8a6d00"; label = "CLASSIFY";
    summary = event.result?.agent
      ? `"${truncStr(event.description, 70)}" → ${event.result.agent}`
      : `"${truncStr(event.description, 70)}" → needs clarification`;
  } else if (subtype === "enqueue") {
    icon = "+"; color = "#2D7A3B"; label = "QUEUE";
    summary = `"${truncStr(event.description, 70)}" → ${event.target}`;
  } else if (subtype === "drop_intent") {
    icon = "×"; color = "#9a6700"; label = "DROP";
    summary = `"${truncStr(event.description, 60)}" dropped (${event.reason})`;
  } else {
    icon = "•"; color = "#888"; label = subtype.toUpperCase();
    summary = JSON.stringify(event).slice(0, 80);
  }

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        marginBottom: 4,
        cursor: "pointer",
        borderRadius: 4,
        background: selected ? "#FFF8DC" : "transparent",
        borderLeft: `3px solid ${color}`,
        paddingLeft: 10,
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#F0F4F8"; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: 10,
        background: color, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>{icon}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color, minWidth: 60, letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {summary}
      </span>
      <span className="muted small" style={{ fontSize: 10 }}>{formatTs(event.timestamp)}</span>
    </div>
  );
}

// ═══ Orchestrator decision chip (between agent cards) ═══
function OrchestratorChip({ event, selected, onClick }) {
  const { phase, from, to, reason } = event;
  const phaseLabel = {
    greeter_handoff: "Greeter handoff",
    intent_pick:     "Intent pick",
    entry_deferred:  "Entry deferred",
    sdoh_gate:       "SDOH gate",
    closer_gate:     "Closer gate",
    end_call:        "End call",
  }[phase] || phase;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
      <div
        onClick={onClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          borderRadius: 20,
          background: selected ? "#FFF8DC" : "#2E4F72",
          color: selected ? "#2E4F72" : "#fff",
          border: selected ? "2px solid #C9A227" : "2px solid #2E4F72",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 500,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          transition: "all 0.1s",
          maxWidth: "90%",
        }}
      >
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8,
          padding: "2px 6px", borderRadius: 3,
          background: selected ? "#2E4F72" : "rgba(255,255,255,0.2)",
          color: selected ? "#fff" : "inherit",
        }}>
          ORCHESTRATOR
        </span>
        <span style={{ fontWeight: 700 }}>{phaseLabel}</span>
        <span style={{ opacity: 0.8 }}>·</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>
          {from || "start"} → {to || "end"}
        </span>
      </div>
    </div>
  );
}

// ═══ Vertical connector arrow between blocks ═══
function TimelineConnector() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}>
      <div style={{
        width: 2, height: 14, background: "#BFBFBF",
      }} />
    </div>
  );
}

// ═══ Details panel — shows the data for whichever block is selected ═══
function ChainDetails({ selected }) {
  const { source, event } = selected;
  let title = "";
  const fields = [];

  if (source === "agent-header") {
    title = `${event.agentId} — activation`;
    if (event.activatingIntent) {
      fields.push(["Activating intent", event.activatingIntent.description]);
      fields.push(["Raised by", event.activatingIntent.sourceAgent || "—"]);
    } else {
      fields.push(["Activation reason", "Orchestration step (no specific patient intent drove this)"]);
    }
    if (event.priorActions?.length) {
      fields.push(["Prior agents completed", (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
          {event.priorActions.map((a, i) => (
            <li key={i}><strong>{a.id}:</strong> {a.summary}</li>
          ))}
        </ul>
      )]);
    }
    if (event.patientContext?.patientName) {
      fields.push(["Patient", `${event.patientContext.patientName} (${event.patientContext.patientId || "?"})`]);
      if (event.patientContext.patientPhysician) fields.push(["Physician", event.patientContext.patientPhysician]);
    }
  } else if (source === "agent-prompt") {
    title = `${event.agentId} — system prompt sent to LLM`;
    fields.push(["Model", event.modelId || "—", "primary"]);
    fields.push(["Prompt size", `${event.promptSize} chars`]);
    fields.push(["Tools available", `${event.toolCount} (${(event.toolNames || []).join(", ")})`]);
    if (Array.isArray(event.contextKeys) && event.contextKeys.length) {
      fields.push(["Context keys substituted", event.contextKeys.join(", ")]);
    }
    if (Array.isArray(event.variables) && event.variables.length) {
      fields.push(["Variables substituted", event.variables.join(", ")]);
    }
    fields.push(["Full rendered prompt", (
      <pre style={{
        fontSize: 11, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
        background: "#FAFAFA", padding: 10, borderRadius: 4,
        maxHeight: 480, overflowY: "auto",
        border: "1px solid #E5E5E5",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}>
        {event.prompt || "(empty)"}
      </pre>
    )]);
    if (Array.isArray(event.tools) && event.tools.length) {
      fields.push(["Tool schemas (full)", (
        <pre style={{
          fontSize: 10, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
          background: "#F5F0FA", padding: 10, borderRadius: 4,
          maxHeight: 320, overflowY: "auto",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}>
          {JSON.stringify(event.tools, null, 2)}
        </pre>
      )]);
    }
    if (Array.isArray(event.toolGates) && event.toolGates.length) {
      fields.push(["Tool gates (per-turn filtering rules)", (
        <pre style={{
          fontSize: 10, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
          background: "#FFF8E1", padding: 10, borderRadius: 4,
          maxHeight: 200, overflowY: "auto",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}>
          {JSON.stringify(event.toolGates, null, 2)}
        </pre>
      )]);
    }
  } else if (source === "agent-complete") {
    title = `${event.agentId} — completed`;
    fields.push(["Summary", event.summary, "primary"]);
    if (event.forced) fields.push(["Forced", "Yes — loop or timeout", "warning"]);
    if (event.needsMore) {
      fields.push(["Signaled needs_more", "Yes"]);
      if (event.followupIntent) fields.push(["Followup intent queued", event.followupIntent]);
    }
  } else if (source === "sub") {
    const { subtype } = event;
    if (subtype === "classify") {
      title = "Intent classifier (LLM)";
      fields.push(["Description", event.description]);
      fields.push(["Source agent", event.sourceAgent || "—"]);
      if (event.result?.agent) fields.push(["LLM classified as", event.result.agent, "primary"]);
      else if (event.result?.clarify) fields.push(["LLM asked for clarification", event.result.clarify, "primary"]);
    } else if (subtype === "enqueue") {
      title = "Intent queued";
      fields.push(["Description", event.description]);
      fields.push(["Resolved agent", event.target, "primary"]);
      fields.push(["Source agent", event.sourceAgent || "—"]);
      fields.push(["Open intents after enqueue", String(event.queueDepth)]);
    } else if (subtype === "drop_intent") {
      title = "Intent dropped";
      fields.push(["Description", event.description]);
      fields.push(["Target (blocked)", event.target]);
      fields.push(["Reason", event.reason, "warning"]);
      fields.push(["Source agent", event.sourceAgent || "—"]);
      if (event.stacksOn) fields.push(["Stacks on", event.stacksOn]);
      if (event.intentCallCount) fields.push(["detect_intent calls this session", String(event.intentCallCount)]);
    }
  } else if (source === "tool") {
    title = `Tool call — ${event.name}`;
    fields.push(["Arguments", (
      <pre style={{ fontSize: 11, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#F5F0FA", padding: 8, borderRadius: 4 }}>
        {JSON.stringify(event.args || {}, null, 2)}
      </pre>
    )]);
    fields.push(["Result", (
      <pre style={{ fontSize: 11, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#F0F7F2", padding: 8, borderRadius: 4 }}>
        {typeof event.result === "object" ? JSON.stringify(event.result, null, 2) : String(event.result ?? "")}
      </pre>
    )]);
    if (typeof event.duration_ms === "number") fields.push(["Duration", `${event.duration_ms} ms`]);
  } else if (source === "decision") {
    title = "Orchestrator decision";
    fields.push(["Phase", event.phase, "primary"]);
    fields.push(["From agent", event.from || "(start)"]);
    fields.push(["To agent", event.to || "(end call)"]);
    fields.push(["Reason", event.reason || "—"]);
    if (event.activatingIntent) {
      fields.push(["Chosen intent", event.activatingIntent.description]);
      fields.push(["Originally raised by", event.activatingIntent.sourceAgent || "—"]);
    }
    if (event.deferredIntent) fields.push(["Deferred (rechecked later)", event.deferredIntent]);
    if (event.queueSnapshot?.length) {
      fields.push(["Intent queue at this moment", (
        <QueueSnapshot snapshot={event.queueSnapshot} />
      )]);
    }
  }

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{
        marginBottom: 12, padding: "6px 10px",
        background: "#EAF2F9", borderRadius: 4,
        fontSize: 12, fontWeight: 700, color: "#2E4F72",
        textTransform: "uppercase", letterSpacing: 0.8,
      }}>
        {title}
      </div>
      {fields.map(([label, value, variant], i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <div className="muted" style={{
            fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3, fontWeight: 600,
          }}>
            {label}
          </div>
          <div style={{
            fontSize: 13,
            fontWeight: variant === "primary" ? 600 : 400,
            color: variant === "warning" ? "#9a6700" : variant === "primary" ? "#2E4F72" : "#1A1A1A",
            wordBreak: "break-word",
            lineHeight: 1.5,
          }}>
            {typeof value === "object" ? value : String(value)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══ Intent queue snapshot block — reused in ChainDetails ═══
function QueueSnapshot({ snapshot }) {
  if (!snapshot?.length) return <span className="muted small">empty</span>;
  return (
    <div style={{ marginTop: 4 }}>
      {snapshot.map((i, idx) => {
        const bg = i.status === "closed" ? "#F4F4F4" : i.status === "in_progress" ? "#FFF8DC" : "#ECF7ED";
        const color = i.status === "closed" ? "#888" : i.status === "in_progress" ? "#8a6d00" : "#2D7A3B";
        const icon = i.status === "closed" ? "✓" : i.status === "in_progress" ? "▶" : "○";
        return (
          <div key={idx} style={{
            display: "flex", alignItems: "baseline", gap: 8,
            padding: "6px 10px", background: bg, borderRadius: 4,
            marginBottom: 4, fontSize: 12, borderLeft: `3px solid ${color}`,
          }}>
            <span style={{ color, fontWeight: 700 }}>{icon}</span>
            <span style={{ color, fontWeight: 600 }}>{i.resolvedAgent}</span>
            <span className="muted" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              — {i.description}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ═══ Agent visual metadata ═══
const CHAIN_AGENT_META = {
  greeter:           { color: "#4A6FA5", accent: "#EAF2F9", icon: "👋" },
  verifier:          { color: "#5A6FA5", accent: "#EEF0F8", icon: "🔐" },
  "referral-intake": { color: "#6E5CA5", accent: "#F1ECF8", icon: "📝" },
  scheduling:        { color: "#4A8FA5", accent: "#E6F4F8", icon: "📅" },
  "appointment-prep":{ color: "#4AA593", accent: "#E6F7F2", icon: "📋" },
  triage:            { color: "#B84A4A", accent: "#F9E6E6", icon: "🩺" },
  sdoh:              { color: "#A57B4A", accent: "#F8EEE6", icon: "💬" },
  closer:            { color: "#555555", accent: "#F0F0F0", icon: "👋" },
};

// ═══ Cost breakdown card — stacked bar + per-service rows ═══
function CostBreakdown({ us }) {
  const cb = us.cost_breakdown || {};
  const rows = [
    { key: "stt",   label: "Speech-to-Text",    color: "#4A8FA5", usd: cb.stt_usd   || 0, detail: `${us.stt_seconds || 0}s audio` },
    { key: "llm",   label: "LLM",               color: "#4A6FA5", usd: cb.llm_usd   || 0, detail: `${us.llm_calls || 0} calls · ${((us.llm_input_tokens || 0) + (us.llm_output_tokens || 0)).toLocaleString()} tokens` },
    { key: "tts",   label: "Text-to-Speech",    color: "#A57B4A", usd: cb.tts_usd   || 0, detail: `${(us.tts_chars || 0).toLocaleString()} chars` },
    { key: "audio", label: "Native audio",      color: "#6E5CA5", usd: cb.audio_usd || 0, detail: us.audio_duration_seconds ? `${us.audio_duration_seconds}s` : "—" },
  ];
  const total = rows.reduce((s, r) => s + r.usd, 0);
  const fmt = (n) => `$${(n || 0).toFixed(4)}`;

  return (
    <div className="card" style={{ marginBottom: 16, padding: 0 }}>
      <div className="card-header" style={{ padding: "10px 16px", display: "flex", alignItems: "baseline", gap: 10 }}>
        <h3 style={{ margin: 0 }}>Cost Breakdown</h3>
        <span className="muted small">Total: <strong style={{ color: "#2E4F72" }}>{fmt(total)}</strong></span>
        {us.duration_seconds != null && (
          <span className="muted small" style={{ marginLeft: "auto" }}>{us.duration_seconds}s call</span>
        )}
      </div>
      <div style={{ padding: "12px 16px" }}>
        {total > 0 ? (
          <div style={{ display: "flex", height: 16, borderRadius: 4, overflow: "hidden", marginBottom: 10, background: "#F0F0F0" }}>
            {rows.map((r) => r.usd > 0 && (
              <div key={r.key}
                style={{ width: `${(r.usd / total) * 100}%`, background: r.color }}
                title={`${r.label}: ${fmt(r.usd)} (${((r.usd / total) * 100).toFixed(1)}%)`}
              />
            ))}
          </div>
        ) : (
          <div className="muted small" style={{ marginBottom: 8 }}>No per-service breakdown recorded.</div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #F0F0F0" }}>
                <td style={{ padding: "6px 0", width: 16 }}>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: r.color, borderRadius: 2 }} />
                </td>
                <td style={{ padding: "6px 8px", fontWeight: 600, color: "#1A1A1A", width: 140 }}>{r.label}</td>
                <td style={{ padding: "6px 8px", color: r.usd > 0 ? "#2E4F72" : "#888", fontVariantNumeric: "tabular-nums" }}>{fmt(r.usd)}</td>
                <td style={{ padding: "6px 8px", color: "#888" }}>
                  {r.usd > 0 ? `${((r.usd / total) * 100).toFixed(1)}%` : "—"}
                </td>
                <td className="muted small" style={{ padding: "6px 8px", textAlign: "right" }}>{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ""; }
}

// ═══════════════════════════════════════════════════════════════════
// Chain Prompts tab — tenant-editable chain framework prompts.
//
// Six prompts per sub-tenant: chain_navigation + chain_greeter/verifier/
// intake/closer/sdoh. Each is versioned (draft + live). Live is read-only;
// edits go to draft; Revisions tab handles publishing via the existing
// publish endpoint (we also expose a per-prompt Publish button here).
// ═══════════════════════════════════════════════════════════════════

const CHAIN_PROMPT_META = {
  chain_navigation: { label: "Navigation framework",  accent: "#4A6FA5", desc: "Shared LLM prompt injected into every task agent — closing sequence, triage preemption, intent routing, queue update logic." },
  chain_greeter:    { label: "Greeter agent",         accent: "#5A8FA5", desc: "Greets the patient and classifies new vs existing." },
  chain_verifier:   { label: "Verifier agent",        accent: "#5A6FA5", desc: "Verifies patient identity (name + DOB) via verify_patient." },
  chain_intake:     { label: "Intake agent",          accent: "#4AA593", desc: "After verifier, asks what the patient needs and captures the first task. Handles resume-from-prior-call flow." },
  chain_closer:     { label: "Closer agent",          accent: "#888888", desc: "Speaks the goodbye and calls end_call. Does NOT ask 'anything else?' — task agents do that." },
  // Outbound chain prompts — only seeded for sub-tenants with outbound mode enabled.
  chain_outbound_navigation: { label: "Navigation framework (outbound)", accent: "#8B5A2B", desc: "Shared LLM prompt injected into every outbound task agent — task dispatch, skip logic, HIPAA guardrails for unverified recipients." },
  chain_outbound_opener:     { label: "Outbound Opener",                 accent: "#A5824A", desc: "First agent on an outbound call — introduces Sandy, states the reason for calling, confirms recipient." },
  chain_recipient_verify:    { label: "Recipient Verify",                accent: "#A5925A", desc: "Lightweight one-question identity confirmation (usually DOB) on an outbound call." },
  chain_outbound_intake:     { label: "Outbound Intake",                 accent: "#A59E5A", desc: "Runs once per outbound call after recipient_verify. Pulls the patient's pending tasks, announces them, and queues them for the task agents." },
  chain_outbound_closer:     { label: "Outbound Closer",                 accent: "#8A8A4A", desc: "Wraps up an outbound call after all tasks are handled. Shorter than the inbound closer — no 'anything else?' prompt." },
};

const CHAIN_PROMPT_KEYS_INBOUND = ["chain_navigation", "chain_greeter", "chain_verifier", "chain_intake", "chain_closer"];
const CHAIN_PROMPT_KEYS_OUTBOUND = ["chain_outbound_navigation", "chain_outbound_opener", "chain_recipient_verify", "chain_outbound_intake", "chain_outbound_closer"];

// ═══════════════════════════════════════════════════════════════════
// Built-in Agents subsection — renders in the Agents tab alongside
// library agents. Lists the 9 chain framework prompts forked from the
// platform-chain library (5 inbound + 4 outbound). Clicking an item
// jumps to Config → Chain Prompts where the editor lives today.
//
// First-class visibility in the Agents list was the main UX ask:
// operators should see chain framework agents as peers of library
// agents, not tucked under a Config subtab. The subtab still owns the
// editor for PR2 — unification comes in PR3.
// ═══════════════════════════════════════════════════════════════════

function BuiltInAgentsSection({ tid, sid, env, isDraft, outboundEnabled }) {
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState(null);
  // Inline-editor state. When set, the table is replaced by the chain-
  // prompt editor focused on the chosen key. Back button returns to the
  // table. Previously this component navigated to Config → Chain Prompts;
  // PR3 folded the editor into the Agents tab so the whole workflow is
  // one-click and the old subtab can be deleted.
  const [editingKey, setEditingKey] = React.useState(null);

  const loadRows = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mode = isDraft ? "draft" : "live";
      const r = await fetch(
        `/api/tenants/${tid}/sub-tenants/${sid}/chain-prompts?mode=${mode}`,
        { credentials: "include" }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.data || d || []);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [tid, sid, isDraft]);

  React.useEffect(() => { loadRows(); }, [loadRows]);

  // ── Inline editor view ───────────────────────────────────────────
  // When a key is selected, render the existing ChainPromptsTab scoped
  // to that key. `initialSelectedKey` flows through so the editor opens
  // on the row the user clicked. Returning from the editor reloads the
  // row list to pick up any version changes.
  if (editingKey) {
    const meta = CHAIN_PROMPT_META[editingKey] || { label: editingKey };
    const direction = CHAIN_PROMPT_KEYS_OUTBOUND.includes(editingKey) ? "outbound" : "inbound";
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <button
            className="btn btn-sm"
            onClick={() => { setEditingKey(null); loadRows(); }}
          >
            ← Back to Built-in Agents
          </button>
          <div className="muted small">
            Editing <strong>{meta.label}</strong> ({editingKey})
          </div>
        </div>
        <ChainPromptsTab
          tid={tid}
          sid={sid}
          mode={isDraft ? "draft" : "live"}
          isLive={!isDraft}
          direction={direction}
          showDirectionPicker={false}
          initialSelectedKey={editingKey}
        />
        {/* Live Tool Gates editor — reads/writes agent_configs for the
            built-in agent this chain prompt key maps to. Platform defaults
            and the current tenant override come from the server at
            mount time via api.getAgentConfig / api.getAgentToolGateDefaults. */}
        <BuiltInAgentGatesEditor
          tid={tid}
          sid={sid}
          chainKey={editingKey}
          isDraft={isDraft}
        />
      </div>
    );
  }

  const inboundKeys = CHAIN_PROMPT_KEYS_INBOUND;
  const outboundKeys = CHAIN_PROMPT_KEYS_OUTBOUND;
  const byKey = Object.fromEntries(rows.map((p) => [p.key, p]));

  const renderRow = (key) => {
    const meta = CHAIN_PROMPT_META[key] || { label: key, desc: "" };
    const p = byKey[key];
    const exists = !!p;
    const diverged = p?.seeded_from?.diverged === true;
    return (
      <tr key={key} style={{ cursor: "pointer" }} onClick={() => setEditingKey(key)}>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 26, height: 26, borderRadius: 6,
              background: meta.accent || "#455A64",
              color: "#fff", fontWeight: 700, fontSize: 11, flexShrink: 0,
            }}>
              BIA
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{meta.label}</div>
              <div className="muted small mono">{key}</div>
            </div>
          </div>
        </td>
        <td>
          <span className="badge badge-neutral" style={{ fontSize: 11 }}>built-in</span>
        </td>
        <td className="muted small" style={{ fontSize: 12 }}>
          {meta.desc}
        </td>
        <td>
          {!exists && <span className="badge" style={{ background: "#ffebee", color: "#c62828", fontSize: 11 }}>not seeded</span>}
          {exists && diverged && <span className="badge" style={{ background: "#fff3e0", color: "#ef6c00", fontSize: 11 }}>customized</span>}
          {exists && !diverged && <span className="badge" style={{ background: "#e8f5e9", color: "#2e7d32", fontSize: 11 }}>library default</span>}
        </td>
        <td style={{ textAlign: "right" }}>
          {exists ? `v${p.version || 1}` : "—"}
        </td>
      </tr>
    );
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <h3>
          Built-in Agents{" "}
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            chain framework
          </span>
        </h3>
      </div>
      <p className="muted small" style={{ marginBottom: 12, lineHeight: 1.5 }}>
        Forked from the platform library at sub-tenant creation. These drive the fixed stages
        of every chain-mode call — greeter, verifier, intake, and closer for inbound,
        plus opener / recipient_verify / closer for outbound. Click any row to edit.
      </p>

      {loading && <div className="muted small">Loading…</div>}
      {error && <div style={{ color: "#c62828", fontSize: 13 }}>Failed to load: {error}</div>}

      {!loading && !error && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "var(--color-text-muted)", marginBottom: 6 }}>
            Inbound
          </div>
          <table className="table" style={{ border: "none", marginBottom: 16 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: 90 }}>Type</th>
                <th>Role</th>
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 70, textAlign: "right" }}>Version</th>
              </tr>
            </thead>
            <tbody>{inboundKeys.map(renderRow)}</tbody>
          </table>

          {outboundEnabled && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "var(--color-text-muted)", marginBottom: 6 }}>
                Outbound
              </div>
              <table className="table" style={{ border: "none" }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ width: 90 }}>Type</th>
                    <th>Role</th>
                    <th style={{ width: 130 }}>Status</th>
                    <th style={{ width: 70, textAlign: "right" }}>Version</th>
                  </tr>
                </thead>
                <tbody>{outboundKeys.map(renderRow)}</tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// BuiltInAgentGatesEditor — wires AgentToolGatesEditor for built-in
// chain framework agents (greeter, verifier, intake, closer, outbound_*).
//
// Chain prompt keys (chain_greeter, chain_intake, ...) map 1:1 to the
// orchestrator's agent ids (greeter, intake, ...). Storage is on the
// standard `agent_configs` collection keyed by the agent id.
// ═══════════════════════════════════════════════════════════════════

const CHAIN_KEY_TO_AGENT_ID = {
  chain_greeter: "greeter",
  chain_verifier: "verifier",
  chain_intake: "intake",
  chain_closer: "closer",
  chain_outbound_opener: "outbound_opener",
  chain_recipient_verify: "recipient_verify",
  chain_outbound_intake: "outbound_intake",
  chain_outbound_closer: "outbound_closer",
};

function BuiltInAgentGatesEditor({ tid, sid, chainKey, isDraft }) {
  const agentId = CHAIN_KEY_TO_AGENT_ID[chainKey];
  const [defaults, setDefaults] = React.useState([]);
  const [override, setOverride] = React.useState(null);   // null = inherit
  const [agentTools, setAgentTools] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [defaultsRes, cfgRes] = await Promise.all([
          api.getToolGateDefaults(agentId),
          api.getAgentConfig(tid, sid, agentId, isDraft ? "draft" : "live").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setDefaults(defaultsRes.data?.tool_gates || []);
        const cfgContent = cfgRes?.data?.content || {};
        setAgentTools(Array.isArray(cfgContent.tools) && cfgContent.tools.length > 0
          ? cfgContent.tools
          // Built-in agents default tools — hard-coded here since the
          // runtime knows them but agent_configs may not populate tools
          // for built-ins.
          : (BUILTIN_AGENT_TOOLS[agentId] || []));
        setOverride(Array.isArray(cfgContent.tool_gates) ? cfgContent.tool_gates : null);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, tid, sid, isDraft]);

  if (!agentId) return null;      // unknown chain key → skip
  if (loading) return null;       // keep silent during initial load

  const handleChange = (newGates) => {
    setOverride(newGates);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Merge with existing agent_config content so we don't clobber other fields.
      const existing = await api.getAgentConfig(tid, sid, agentId, "draft").catch(() => ({ data: null }));
      const content = { ...(existing?.data?.content || {}) };
      if (override === null) {
        delete content.tool_gates;
      } else {
        content.tool_gates = override;
      }
      await api.writeAgentConfig(tid, sid, agentId, {
        content,
        mode: "draft",
        author: "admin",
        commit_msg: `Tool gates updated for ${agentId}`,
      });
      setDirty(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AgentToolGatesEditor
        agentTools={agentTools}
        platformDefaults={defaults}
        value={override}
        onChange={handleChange}
        readOnly={!isDraft}
        agentLabel={agentId}
      />
      {isDraft && dirty && (
        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center", padding: 8, background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4 }}>
          <span className="small" style={{ flex: 1 }}>Unsaved changes.</span>
          <button className="btn btn-sm" onClick={() => { setOverride(null); setDirty(false); }} disabled={saving}>
            Discard
          </button>
          <button className="btn btn-sm primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save to draft"}
          </button>
        </div>
      )}
      {error && (
        <div style={{ marginTop: 8, padding: 8, background: "#ffebee", color: "#c62828", fontSize: 13, borderRadius: 4 }}>
          {error}
        </div>
      )}
    </>
  );
}

// Hard-coded tool lists for built-in chain agents. The runtime maintains
// these in DEFAULT_CHAIN_AGENTS; we mirror the list here so the UI can
// show the gate editor without an extra runtime lookup. Keep in sync with
// configural-runtime/src/chain-agents.js.
const BUILTIN_AGENT_TOOLS = {
  greeter: ["complete_task"],
  verifier: ["verify_patient", "complete_task", "end_call"],
  intake: ["complete_task"],
  closer: ["end_call"],
  outbound_opener: ["complete_task"],
  recipient_verify: ["verify_patient", "complete_task", "end_call"],
  outbound_intake: ["complete_task"],
  outbound_closer: ["end_call"],
};

// ═══════════════════════════════════════════════════════════════════
// ChainRulesPreview — LEGACY STATIC MOCK, replaced by BuiltInAgentGatesEditor.
// Kept only to not break any lingering imports; not rendered anywhere.
//
// Renders the "Chain Rules (tool gating)" panel that would let operators
// control when specific tools become available to each built-in agent
// (e.g. hold back complete_task until the patient has spoken). The
// checkboxes are controlled inputs so the UX feels live, but they DO
// NOT persist and do NOT affect runtime. This is a design preview — a
// full stack impl would add state threading, agent_configs schema, a
// merge path in config-resolver, and pipeline.js runtime enforcement.
// ═══════════════════════════════════════════════════════════════════

// Platform defaults for each built-in agent — shown read-only below the
// override checkboxes so operators see what's already in effect before
// they layer their own rules on top.
const CHAIN_RULES_DEFAULTS = {
  chain_greeter:           { require_patient_input: ["complete_task"] },
  chain_verifier:          {},
  chain_intake:            { require_patient_input: ["complete_task"] },
  chain_closer:            {},
  chain_outbound_opener:   { require_patient_input: ["complete_task"] },
  chain_recipient_verify:  {},
  chain_outbound_intake:   { require_patient_input: ["complete_task"] },
  chain_outbound_closer:   {},
};

function ChainRulesPreview({ chainKey, isDraft }) {
  // Hydrate initial form state from the platform default for this key so
  // the preview looks realistic. All state is ephemeral — nothing persists.
  const defaults = CHAIN_RULES_DEFAULTS[chainKey] || {};
  const defaultGatedTools = defaults.require_patient_input || [];

  const [requirePatientInput, setRequirePatientInput] = React.useState(defaultGatedTools.length > 0);
  const [gatedTools, setGatedTools] = React.useState(defaultGatedTools);
  const [newToolDraft, setNewToolDraft] = React.useState("");
  const [requirePriorTool, setRequirePriorTool] = React.useState(false);
  const [priorTool, setPriorTool] = React.useState("verify_patient");
  const [priorGatedTools, setPriorGatedTools] = React.useState(["complete_task"]);
  const [hideForNTurns, setHideForNTurns] = React.useState(false);
  const [nTurns, setNTurns] = React.useState(1);
  const [nTurnsTools, setNTurnsTools] = React.useState([]);

  // Hide for navigation keys — they're prompt templates, not agents.
  if (chainKey === "chain_navigation" || chainKey === "chain_outbound_navigation") {
    return null;
  }

  const removeTool = (list, setList, t) => setList(list.filter((x) => x !== t));
  const addTool = (list, setList, t) => {
    const trimmed = t.trim();
    if (trimmed && !list.includes(trimmed)) setList([...list, trimmed]);
  };

  const readOnly = !isDraft;

  return (
    <div className="card" style={{ marginTop: 16, borderLeft: "4px solid #6A4C93" }}>
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <h3>
          Chain Rules{" "}
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            tool gating
          </span>
        </h3>
        <span className="badge" style={{ background: "#fff3cd", color: "#856404", fontSize: 11 }}>
          PREVIEW — not wired
        </span>
      </div>

      <p className="muted small" style={{ lineHeight: 1.5, marginBottom: 16 }}>
        Hold back specific tools until a condition is met — prevents the LLM from
        ending the agent prematurely before the patient has responded. Rules stack
        on top of the platform defaults shown at the bottom of this card.
      </p>

      {/* ── Gate #1: require patient input ──────────────────────── */}
      <div style={{ marginBottom: 20, opacity: requirePatientInput ? 1 : 0.7 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={requirePatientInput}
            onChange={(e) => setRequirePatientInput(e.target.checked)}
            disabled={readOnly}
          />
          Require patient input before tool call
        </label>
        <p className="muted small" style={{ margin: "4px 0 8px 24px" }}>
          Listed tools are filtered out of the LLM's tool catalog until the
          patient has spoken at least once in this agent session.
        </p>
        {requirePatientInput && (
          <div style={{ marginLeft: 24 }}>
            <label className="small" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
              Tools held back:
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
              {gatedTools.map((t) => (
                <span
                  key={t}
                  className="badge"
                  style={{ background: "#ede7f6", color: "#4527a0", fontFamily: "var(--font-mono)", fontSize: 11, paddingRight: 4 }}
                >
                  {t}
                  {!readOnly && (
                    <button
                      onClick={() => removeTool(gatedTools, setGatedTools, t)}
                      style={{ marginLeft: 6, border: "none", background: "transparent", color: "#4527a0", cursor: "pointer", fontSize: 13, padding: 0 }}
                      title="Remove"
                    >×</button>
                  )}
                </span>
              ))}
              {gatedTools.length === 0 && (
                <span className="muted small" style={{ fontStyle: "italic" }}>(no tools held back — rule has no effect)</span>
              )}
            </div>
            {!readOnly && (
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  type="text"
                  value={newToolDraft}
                  onChange={(e) => setNewToolDraft(e.target.value)}
                  placeholder="tool name (e.g. complete_task)"
                  style={{ flex: 1, padding: 4, fontSize: 12, fontFamily: "var(--font-mono)", border: "1px solid var(--color-border)", borderRadius: 4 }}
                />
                <button
                  className="btn btn-sm"
                  onClick={() => { addTool(gatedTools, setGatedTools, newToolDraft); setNewToolDraft(""); }}
                >
                  + add
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Gate #2: require prior tool success ─────────────────── */}
      <div style={{ marginBottom: 20, opacity: requirePriorTool ? 1 : 0.7 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={requirePriorTool}
            onChange={(e) => setRequirePriorTool(e.target.checked)}
            disabled={readOnly}
          />
          Require prior tool success
        </label>
        <p className="muted small" style={{ margin: "4px 0 8px 24px" }}>
          Listed tools are held back until another named tool has returned successfully in this session.
          Useful for e.g. holding <code>complete_task</code> on verifier until <code>verify_patient</code> has succeeded.
        </p>
        {requirePriorTool && (
          <div style={{ marginLeft: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="small" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tools held back:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {priorGatedTools.map((t) => (
                  <span key={t} className="badge" style={{ background: "#ede7f6", color: "#4527a0", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <label className="small" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Until this tool succeeds:
              </label>
              <select
                value={priorTool}
                onChange={(e) => setPriorTool(e.target.value)}
                disabled={readOnly}
                style={{ width: "100%", padding: 4, fontSize: 12 }}
              >
                <option value="verify_patient">verify_patient</option>
                <option value="get_available_slots">get_available_slots</option>
                <option value="lookup_appointments">lookup_appointments</option>
                <option value="schedule_appointment">schedule_appointment</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* ── Gate #3: hide for first N turns ─────────────────────── */}
      <div style={{ marginBottom: 16, opacity: hideForNTurns ? 1 : 0.7 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={hideForNTurns}
            onChange={(e) => setHideForNTurns(e.target.checked)}
            disabled={readOnly}
          />
          Hide tools for the first N LLM turns of this session
        </label>
        <p className="muted small" style={{ margin: "4px 0 8px 24px" }}>
          Simpler variant — no dependency on STT or prior tools. Tools become available after N LLM iterations regardless.
        </p>
        {hideForNTurns && (
          <div style={{ marginLeft: 24, display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, alignItems: "end" }}>
            <div>
              <label className="small" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tools held back:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {nTurnsTools.length === 0 && <span className="muted small" style={{ fontStyle: "italic" }}>(none configured)</span>}
                {nTurnsTools.map((t) => (
                  <span key={t} className="badge" style={{ background: "#ede7f6", color: "#4527a0", fontFamily: "var(--font-mono)", fontSize: 11 }}>{t}</span>
                ))}
              </div>
            </div>
            <div>
              <label className="small" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                Turns to hide:
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={nTurns}
                onChange={(e) => setNTurns(parseInt(e.target.value, 10) || 1)}
                disabled={readOnly}
                style={{ width: "100%", padding: 4, fontSize: 12 }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Read-only defaults reference ─────────────────────────── */}
      <div style={{ marginTop: 20, padding: 12, background: "#f5f5f5", borderRadius: 4, fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1, color: "var(--color-text-muted)", fontSize: 11 }}>
          Platform defaults (read-only — from chain framework library)
        </div>
        {Object.entries(CHAIN_RULES_DEFAULTS).map(([k, rules]) => {
          const hasRules = Object.keys(rules).length > 0;
          return (
            <div
              key={k}
              style={{
                display: "flex", gap: 8, alignItems: "baseline",
                padding: "2px 0",
                opacity: k === chainKey ? 1 : 0.55,
                fontWeight: k === chainKey ? 600 : 400,
              }}
            >
              <code style={{ minWidth: 200, fontSize: 11 }}>{k}</code>
              <span className="muted small">
                {hasRules
                  ? `require_patient_input → [${(rules.require_patient_input || []).join(", ")}]`
                  : "(no default gates)"}
                {k === chainKey && <span style={{ color: "#6A4C93", marginLeft: 8 }}>← current</span>}
              </span>
            </div>
          );
        })}
        <div className="muted small" style={{ marginTop: 10, fontStyle: "italic" }}>
          Your overrides (above) stack on top of the defaults for this agent — checkboxes unchecked means use the default as-is;
          checkboxes checked with different tools means replace the default for this agent.
        </div>
      </div>

      <div className="muted small" style={{ marginTop: 12, padding: 8, background: "#fff8e1", borderRadius: 4 }}>
        <strong>Note:</strong> This panel is a UI preview. Changes here are not saved and runtime enforcement is not yet wired.
        Implementation plan: agent_configs schema → config-resolver merge → pipeline.js tool filter at LLM prep time.
      </div>
    </div>
  );
}


function ChainPromptsTab({ tid, sid, mode, isLive, direction: directionProp, showDirectionPicker = true, initialSelectedKey }) {
  const [prompts, setPrompts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // When the parent Config tab already has an Inbound/Outbound pill, it
  // passes `direction` + `showDirectionPicker=false`. Otherwise this tab
  // owns a local direction picker (used by sub-tenants without outbound
  // mode enabled — the picker is hidden and only inbound shows).
  // If an initialSelectedKey is supplied (e.g. the Agents tab clicked
  // into a specific built-in agent), derive the direction from it so
  // the tab starts on the correct direction — clicking an outbound
  // key should open the outbound list, not inbound.
  const initialDirection = initialSelectedKey
    ? (CHAIN_PROMPT_KEYS_OUTBOUND.includes(initialSelectedKey) ? "outbound" : "inbound")
    : "inbound";
  const [localDirection, setLocalDirection] = React.useState(initialDirection);
  const direction = directionProp || localDirection;
  const setDirection = directionProp ? () => {} : setLocalDirection;
  const [selectedKey, setSelectedKey] = React.useState(initialSelectedKey || "chain_navigation");
  const [draftText, setDraftText] = React.useState("");
  const [commitMsg, setCommitMsg] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [banner, setBanner] = React.useState(null);

  // Phase 3 — three-subtab view for migrated agents:
  //   • structured — sectioned authored view (the editable shape)
  //   • combined   — agent's contribution only, scope="agent"
  //   • master     — full LLM view, scope="all" (universal+framework+agent merged)
  // Navigation framework prompts (chain_navigation, chain_outbound_navigation)
  // bypass this entirely and use the prose textarea editor below — they
  // have no structured form by design.
  const [viewMode, setViewMode] = React.useState("structured"); // "structured" | "combined" | "master"
  const [compiledData, setCompiledData] = React.useState(null);
  const [compiledLoading, setCompiledLoading] = React.useState(false);
  const [compiledError, setCompiledError] = React.useState(null);
  const previewAgentId = CHAIN_KEY_TO_AGENT_ID[selectedKey] || null;

  // Lazy-load when an agent is selected — the API returns both the
  // master compiled prompt (scope="all") and the agent-only compiled
  // prompt (scope="agent") in one shot, so all three subtabs render
  // from the same payload.
  React.useEffect(() => {
    if (!previewAgentId) return;
    setCompiledLoading(true);
    setCompiledError(null);
    setCompiledData(null);
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/tenants/${tid}/sub-tenants/${sid}/agents/${previewAgentId}/compiled-prompt?direction=${direction}`,
          { credentials: "include" }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setCompiledData(j.data);
      } catch (e) {
        if (!cancelled) setCompiledError(e.message || String(e));
      } finally {
        if (!cancelled) setCompiledLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, previewAgentId, direction, tid, sid]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/tenants/${tid}/sub-tenants/${sid}/chain-prompts?mode=${mode}`, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || "Failed to load");
      setPrompts(j.data || []);
      const sel = (j.data || []).find(p => p.key === selectedKey);
      if (sel?.current) {
        setDraftText(sel.current.content);
        setCommitMsg("");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tid, sid, mode]);

  // Sync draft text when the user clicks a different prompt in the list.
  function selectPrompt(key) {
    setSelectedKey(key);
    const row = prompts.find(p => p.key === key);
    setDraftText(row?.current?.content ?? row?.default_content ?? "");
    setCommitMsg("");
    setBanner(null);
  }

  async function saveDraft() {
    setBusy(true);
    setBanner(null);
    try {
      const r = await fetch(`/api/tenants/${tid}/sub-tenants/${sid}/chain-prompts/${selectedKey}?mode=draft`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: draftText,
          commit_msg: commitMsg || "Edited via Chain Prompts tab",
          author: "ui",
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || "Save failed");
      setBanner({ kind: "ok", text: `Saved v${j.data.version} (draft)` });
      await load();
    } catch (e) {
      setBanner({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function resetToDefault() {
    if (!confirm(`Reset ${selectedKey} draft to the runtime default? Your current draft will be saved as a new version under the default.`)) return;
    setBusy(true);
    setBanner(null);
    try {
      const r = await fetch(`/api/tenants/${tid}/sub-tenants/${sid}/chain-prompts/${selectedKey}/reset-default`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author: "ui" }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error?.message || "Reset failed");
      setBanner({ kind: "ok", text: `Reset to default (new draft v${j.data.version})` });
      await load();
    } catch (e) {
      setBanner({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  // Filter prompts by direction sub-tab.
  const directionKeys = direction === "outbound" ? CHAIN_PROMPT_KEYS_OUTBOUND : CHAIN_PROMPT_KEYS_INBOUND;
  const visiblePrompts = prompts.filter(p => directionKeys.includes(p.key));
  const hasOutboundPrompts = prompts.some(p => CHAIN_PROMPT_KEYS_OUTBOUND.includes(p.key));

  // When switching direction, if the currently-selected key isn't in the new
  // direction's key set, pick the first visible prompt.
  React.useEffect(() => {
    if (!directionKeys.includes(selectedKey)) {
      const firstKey = visiblePrompts[0]?.key || directionKeys[0];
      if (firstKey) {
        setSelectedKey(firstKey);
        const row = prompts.find(p => p.key === firstKey);
        setDraftText(row?.current?.content ?? row?.default_content ?? "");
        setCommitMsg("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, prompts.length]);

  const selected = prompts.find(p => p.key === selectedKey);
  const selectedMeta = CHAIN_PROMPT_META[selectedKey] || { label: selectedKey, accent: "#888", desc: "" };
  const current = selected?.current;
  const isOverride = !!current?.is_override;
  const hasUnsavedEdits = !!current && draftText !== current.content;
  const editDisabled = isLive;

  const tabBtnStyle = (active, accent) => ({
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid " + (active ? accent : "#D0D0D0"),
    borderRadius: 4,
    background: active ? accent : "#FFF",
    color: active ? "#FFF" : "#555",
    cursor: "pointer",
  });

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-header" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>Chain Prompts</h3>
        <span className="muted small">
          {isLive ? "Live (read-only)" : "Draft (editable)"} · {visiblePrompts.length} prompts ·{" "}
          <span style={{ color: direction === "outbound" ? "#A5824A" : "#4A6FA5", fontWeight: 600 }}>
            {direction}
          </span>
        </span>
        {showDirectionPicker && (
          <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
            <button onClick={() => setDirection("inbound")} style={tabBtnStyle(direction === "inbound", "#4A6FA5")}>
              Inbound
            </button>
            <button
              onClick={() => setDirection("outbound")}
              style={tabBtnStyle(direction === "outbound", "#A5824A")}
              title={hasOutboundPrompts ? "" : "Outbound chain prompts will appear here once outbound mode is enabled."}
            >
              Outbound {!hasOutboundPrompts && <span style={{ opacity: 0.6, marginLeft: 4 }}>·</span>}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 20 }}>Loading chain prompts…</div>
      ) : error ? (
        <div style={{ padding: 20, color: "#c62828" }}>Error: {error}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", minHeight: 520 }}>

          {/* ─── Left rail — prompts filtered by direction ─────────────────── */}
          <div style={{ borderRight: "1px solid #E0E0E0", padding: "10px 0", background: "#FAFAFA" }}>
            {visiblePrompts.length === 0 && (
              <div className="muted small" style={{ padding: "16px 14px", fontSize: 12, lineHeight: 1.5 }}>
                {direction === "outbound"
                  ? "No outbound chain prompts yet. Enable outbound mode on the Overview tab to seed these."
                  : "No inbound chain prompts found."}
              </div>
            )}
            {visiblePrompts.map(p => {
              const meta = CHAIN_PROMPT_META[p.key] || { label: p.key, accent: "#888" };
              const isSel = p.key === selectedKey;
              const isEdited = p.current?.is_override;
              return (
                <div
                  key={p.key}
                  onClick={() => selectPrompt(p.key)}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderLeft: `4px solid ${isSel ? meta.accent : "transparent"}`,
                    background: isSel ? "#FFF" : "transparent",
                    fontWeight: isSel ? 600 : 400,
                  }}
                >
                  <div style={{ fontSize: 13, color: "#1A1A1A" }}>
                    {meta.label}
                    {isEdited && (
                      <span title="This prompt is different from the runtime default" style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", background: "#FFF3E0", color: "#E65100", borderRadius: 3 }}>
                        custom
                      </span>
                    )}
                  </div>
                  <div className="muted small" style={{ fontSize: 10, marginTop: 2 }}>
                    {p.current ? `v${p.current.version}` : "not yet seeded"}
                    {p.current?.is_live && " · live"}
                    {p.current?.is_draft && " · draft"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ─── Right pane — editor for the selected prompt ───── */}
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <h4 style={{ margin: 0, color: selectedMeta.accent }}>{selectedMeta.label}</h4>
                <code className="muted small" style={{ fontSize: 11 }}>{selectedKey}</code>
                {current && (
                  <span className="muted small" style={{ marginLeft: "auto", fontSize: 11 }}>
                    {isOverride ? "customized" : "matches runtime default"}
                  </span>
                )}
              </div>
              <div className="muted small" style={{ fontSize: 12, marginTop: 4 }}>{selectedMeta.desc}</div>
            </div>

            {banner && (
              <div style={{
                padding: "8px 12px", borderRadius: 4,
                background: banner.kind === "ok" ? "#E8F5E9" : "#FFEBEE",
                color: banner.kind === "ok" ? "#2e7d32" : "#c62828",
                fontSize: 13,
              }}>
                {banner.text}
              </div>
            )}

            {/* Phase 3 — selected key is either an AGENT (structured) or
                a NAVIGATION framework prompt (prose). Agents get three
                subtabs (Structured / Combined / Master Combined);
                navigation prompts get the raw editor since they're a
                Framework Rule Set type — no structured form by design. */}
            {previewAgentId ? (
              <>
                <div style={{
                  display: "flex", gap: 6,
                  padding: "8px 0",
                  borderBottom: "1px solid #e5e5e5",
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}>
                  <button
                    className="btn btn-sm"
                    onClick={() => setViewMode("structured")}
                    style={viewMode === "structured" ? { background: "#2E4F72", color: "#fff" } : {}}
                  >
                    Structured editor
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => setViewMode("combined")}
                    style={viewMode === "combined" ? { background: "#2E4F72", color: "#fff" } : {}}
                  >
                    Combined (agent only)
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => setViewMode("master")}
                    style={viewMode === "master" ? { background: "#2E4F72", color: "#fff" } : {}}
                  >
                    Master Combined (LLM view)
                  </button>
                  <div style={{ flex: 1 }} />
                  <span className="muted small" style={{ alignSelf: "center", maxWidth: 360, textAlign: "right" }}>
                    {viewMode === "master"
                      ? "Full prompt to the LLM — universal + framework + agent merged + renumbered"
                      : viewMode === "combined"
                      ? "Agent's contribution only — universal + framework rules excluded for review"
                      : "Sectioned, table-based view of the structured definition"}
                  </span>
                </div>
              </>
            ) : (
              <div style={{
                padding: "8px 12px",
                marginBottom: 12,
                background: "#f0f3f7",
                border: "1px solid #d8dde3",
                borderRadius: 4,
                fontSize: 12,
                color: "#2E4F72",
              }}>
                <strong>Framework Rule Set</strong> — this is a navigation framework
                prompt, not an agent. It has no structured form by design; edit the
                prose directly below. The content here is injected into every task
                agent via <code>buildChainPrompt</code> at runtime.
              </div>
            )}

            {/* Navigation prompts always render the raw textarea.
                Migrated agents render structured/combined; their raw
                prose is available indirectly via Combined view. */}
            {!previewAgentId && (
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                disabled={editDisabled || busy}
                style={{
                  width: "100%",
                  minHeight: 420,
                  fontFamily: "Menlo, Monaco, 'Courier New', monospace",
                  fontSize: 12,
                  lineHeight: 1.5,
                  padding: 10,
                  border: hasUnsavedEdits ? "2px solid #FB8C00" : "1px solid #CCC",
                  borderRadius: 4,
                  background: editDisabled ? "#F5F5F5" : "#FFF",
                  resize: "vertical",
                }}
              />
            )}

            {previewAgentId && (
              <div style={{ minHeight: 420 }}>
                {compiledLoading && <div className="muted">Compiling…</div>}
                {previewAgentId && compiledError && <div style={{ color: "#c62828" }}>Error: {compiledError}</div>}
                {previewAgentId && compiledData?.source === "prose" && (
                  <div style={{
                    padding: 14,
                    background: "#fff8e1",
                    border: "1px solid #ffe0a3",
                    borderRadius: 6,
                    fontSize: 13, color: "#5b4400",
                  }}>
                    <strong>This agent uses the legacy prose prompt path.</strong>
                    <div style={{ marginTop: 6 }}>{compiledData.message}</div>
                    {compiledData.available_structured_agents?.length > 0 && (
                      <div style={{ marginTop: 10, fontSize: 12 }}>
                        Currently migrated to structured authoring:{" "}
                        <strong>{compiledData.available_structured_agents.join(", ")}</strong>
                      </div>
                    )}
                  </div>
                )}
                {previewAgentId && compiledData?.source === "structured" && viewMode === "structured" && (
                  <AuthoredView definition={compiledData.definition} />
                )}
                {previewAgentId && compiledData?.source === "structured" && viewMode === "combined" && (
                  <CompiledView
                    prompt={compiledData.prompt_agent_only}
                    ruleMap={compiledData.rule_map_agent_only}
                    title="Agent contribution only — universal + framework rules excluded"
                  />
                )}
                {previewAgentId && compiledData?.source === "structured" && viewMode === "master" && (
                  <CompiledView
                    prompt={compiledData.prompt}
                    ruleMap={compiledData.rule_map}
                    title="Full prompt sent to LLM — universal + framework + agent merged"
                  />
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Commit message (optional)"
                value={commitMsg}
                onChange={(e) => setCommitMsg(e.target.value)}
                disabled={editDisabled || busy}
                style={{ flex: 1, minWidth: 200, padding: 8, fontSize: 13, border: "1px solid #CCC", borderRadius: 4 }}
              />
              <button
                className="btn sm primary"
                onClick={saveDraft}
                disabled={editDisabled || busy || !hasUnsavedEdits}
                title={editDisabled ? "Switch to draft mode to edit" : "Save changes to draft. Publish from the Revisions tab."}
              >
                {busy ? "Saving…" : "Save draft"}
              </button>
              <button
                className="btn sm"
                onClick={resetToDefault}
                disabled={busy || isLive}
                title="Create a new draft with the runtime default text"
              >
                Reset to default
              </button>
            </div>

            <div className="muted small" style={{ fontSize: 11, marginTop: 4 }}>
              Saves always go to <strong>draft</strong>. To promote drafts to <strong>live</strong> (or discard them), use the <strong>Revisions</strong> tab — that's the single place where publish/discard for all prompts on this sub-tenant happens.
            </div>

            {/* Metadata footer */}
            {current && (
              <div className="muted small" style={{ fontSize: 11, borderTop: "1px solid #EEE", paddingTop: 8, marginTop: 4 }}>
                Version {current.version} · {current.is_live ? "live" : "—"} · {current.is_draft ? "draft" : "—"}
                {current.commit_msg && <> · <em>{current.commit_msg}</em></>}
                {current.created_by && <> · by {current.created_by}</>}
                {current.created_at && <> · {new Date(current.created_at).toLocaleString()}</>}
                {" · "}
                <a href={`#versions-${selectedKey}`} onClick={(e) => { e.preventDefault(); window.open(`/api/tenants/${tid}/sub-tenants/${sid}/chain-prompts/${selectedKey}/versions`, "_blank"); }}>
                  View version history
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Chain Animation tab — read-only demo of how a call progresses
// through the chain. No edit functionality; good for demos.
//
// Plays a scripted scenario: greeter → verifier → intake → scheduling
// (with mid-task triage preemption) → triage → scheduling (resumed) →
// closer. The intent queue builds up as the chain classifies patient
// requests. Autoplay with play/pause/reset and a step-through mode.
// ═══════════════════════════════════════════════════════════════════

const CHAIN_DEMO_AGENT_META = {
  greeter:           { color: "#4A6FA5", accent: "#EAF2F9", icon: "👋" },
  verifier:          { color: "#5A6FA5", accent: "#EEF0F8", icon: "🔐" },
  intake:            { color: "#4AA593", accent: "#E6F7F2", icon: "🎯" },
  scheduling:        { color: "#4A8FA5", accent: "#E6F4F8", icon: "📅" },
  triage:            { color: "#B84A4A", accent: "#F9E6E6", icon: "🩺" },
  "appointment-prep":{ color: "#4AA593", accent: "#E6F7F2", icon: "📋" },
  "referral-intake": { color: "#6E5CA5", accent: "#F1ECF8", icon: "📝" },
  closer:            { color: "#555555", accent: "#F0F0F0", icon: "👋" },
};

// Each step is one tick in the demo. Types:
//   speak      — agent says something (patient or agent)
//   activate   — new agent activates (becomes the current card)
//   enqueue    — orchestrator adds an intent to the queue
//   dispatch   — orchestrator decides what runs next (chip between cards)
//   complete   — current agent completes
//   tool_call  — agent calls a tool (inline in the agent card)
//   defer      — current agent defers (triage preemption)
//   resume     — deferred intent re-opens and re-activates
const CHAIN_DEMO_SCENARIO = [
  { t: "activate",  agent: "greeter", intent: null },
  { t: "speak",     role: "agent", agent: "greeter", text: "Hello, this is your care coordinator at NY Oncology. Are you an existing patient or calling as new or referral?" },
  { t: "speak",     role: "patient", text: "I'm an existing patient." },
  { t: "complete",  agent: "greeter", summary: "patient_type: existing" },
  { t: "dispatch",  from: "greeter", to: "verifier", reason: "existing patient" },
  { t: "activate",  agent: "verifier", intent: null },
  { t: "speak",     role: "agent", agent: "verifier", text: "I'll need to verify your identity. Your full name and date of birth?" },
  { t: "speak",     role: "patient", text: "James Smith, September 8, 1969." },
  { t: "tool_call", agent: "verifier", name: "verify_patient", result: "verified · PAT-002 · Dr. Rebecca Morrison" },
  { t: "complete",  agent: "verifier", summary: "Verified James Smith" },
  { t: "dispatch",  from: "verifier", to: "intake", reason: "queue empty, post-verify intake" },
  { t: "activate",  agent: "intake", intent: null },
  { t: "speak",     role: "agent", agent: "intake", text: "Welcome back, James. What can I help you with today?" },
  { t: "speak",     role: "patient", text: "I need to schedule a treatment visit." },
  { t: "enqueue",   description: "schedule a treatment visit", target: "scheduling", priority: 3 },
  { t: "complete",  agent: "intake", summary: "captured: schedule a treatment visit" },
  { t: "dispatch",  from: "intake", to: "scheduling", reason: "priority=3 pick" },
  { t: "activate",  agent: "scheduling", intent: "schedule a treatment visit" },
  { t: "speak",     role: "agent", agent: "scheduling", text: "I see you have Dr. Rebecca Morrison as part of your care team. Schedule with her?" },
  { t: "speak",     role: "patient", text: "Yes. Actually, I've been feeling nauseous since yesterday." },
  // Triage preemption
  { t: "speak",     role: "agent", agent: "scheduling", text: "Oh, I'm sorry you're feeling nauseous. Let's address that first — we can book after. Okay?" },
  { t: "speak",     role: "patient", text: "Yes, please." },
  { t: "enqueue",   description: "nausea since yesterday", target: "triage", priority: 1 },
  { t: "defer",     agent: "scheduling", reason: "triage preemption — scheduling re-opened" },
  { t: "dispatch",  from: "scheduling", to: "triage", reason: "priority=1 jumps the queue" },
  { t: "activate",  agent: "triage", intent: "nausea since yesterday" },
  { t: "speak",     role: "agent", agent: "triage", text: "Let me screen for emergency symptoms. Any chest pain, severe headache, or vomiting blood?" },
  { t: "speak",     role: "patient", text: "No, none of that." },
  { t: "tool_call", agent: "triage", name: "notify_practice", result: "practice notified" },
  { t: "speak",     role: "agent", agent: "triage", text: "I've notified the practice. Is there anything else?" },
  { t: "speak",     role: "patient", text: "No, that's all for the nausea." },
  { t: "complete",  agent: "triage", summary: "nausea assessed, practice notified" },
  { t: "resume",    agent: "scheduling", reason: "deferred intent re-opens" },
  { t: "dispatch",  from: "triage", to: "scheduling", reason: "resume deferred scheduling" },
  { t: "activate",  agent: "scheduling", intent: "schedule a treatment visit (resumed)" },
  { t: "tool_call", agent: "scheduling", name: "get_available_slots", result: "3 openings April 27" },
  { t: "speak",     role: "agent", agent: "scheduling", text: "Dr. Morrison has openings on April 27th at 8, 9, or 10 AM. Which works?" },
  { t: "speak",     role: "patient", text: "10 AM." },
  { t: "tool_call", agent: "scheduling", name: "schedule_appointment", result: "booked · Apr 27 10:00 AM" },
  { t: "speak",     role: "agent", agent: "scheduling", text: "You're all set — Treatment Visit with Dr. Morrison on April 27th at 10 AM. Is there anything else?" },
  { t: "speak",     role: "patient", text: "No, I'm good." },
  { t: "complete",  agent: "scheduling", summary: "booked Apr 27 10 AM" },
  { t: "dispatch",  from: "scheduling", to: "closer", reason: "queue empty" },
  { t: "activate",  agent: "closer", intent: null },
  { t: "speak",     role: "agent", agent: "closer", text: "Take care, James. Don't hesitate to call us. Goodbye." },
  { t: "tool_call", agent: "closer", name: "end_call", result: "completed" },
  { t: "complete",  agent: "closer", summary: "call ended normally" },
];



// ═══════════════════════════════════════════════════════════════════
// Chain Animation tab — rich swim-lane flow chart (read-only demo).
//
// Layout:
//   ┌──────────── PHASE BAR (HTML overlay) ───────────────┐
//   │  GREETING  →  VERIFY  →  INTAKE  →  TASKS  →  CLOSE │
//   └─────────────────────────────────────────────────────┘
//   Row 1 (y=0)    agent nodes (main flow)  ◯ → ◯ → ◯ → ◯
//   Row 2 (y=200)  tool + intent sub-nodes   ⚙  +"intent"
//
// Deferred agents go dashed amber; triage preemption shows as an offset
// agent with a yellow "preempt" edge in and a green "resume" edge out.
// ═══════════════════════════════════════════════════════════════════

import { ReactFlow, Background, Controls, MarkerType, Handle, Position } from "@xyflow/react";

// ─── Custom node types ────────────────────────────────────────────

const AgentFlowNode = React.memo(({ data, selected }) => {
  const color = data.color || "#666";
  const statusBg = data.status === "completed" ? "#E8F5E9"
                 : data.status === "deferred" ? "#FFF4E5"
                 : "#EAF2F9";
  const statusFg = data.status === "completed" ? "#2D7A3B"
                 : data.status === "deferred" ? "#9a6700"
                 : "#2E4F72";
  const borderStyle = data.status === "deferred" ? "dashed" : "solid";
  const isActive = data.status === "active";
  return (
    <div style={{
      background: "#fff",
      border: `2px ${borderStyle} ${selected ? "#1a1a1a" : color}`,
      borderRadius: 10,
      padding: "10px 14px",
      minWidth: 180,
      maxWidth: 220,
      boxShadow: isActive ? `0 0 0 4px ${color}33` : "0 1px 3px rgba(0,0,0,0.08)",
      transition: "box-shadow 0.25s, border-color 0.25s",
    }}>
      <Handle type="target" position={Position.Left} style={{ background: color, width: 8, height: 8 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: 26, height: 26, borderRadius: 13,
          background: color, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13,
        }}>{data.icon || "●"}</span>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a1a", textTransform: "capitalize" }}>
          {data.label}
        </div>
        <span style={{
          marginLeft: "auto",
          fontSize: 9, fontWeight: 700,
          padding: "2px 6px", borderRadius: 3,
          background: statusBg, color: statusFg,
          textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          {data.status || "pending"}
        </span>
      </div>
      {data.activatingIntent && (
        <div style={{ fontSize: 10, color: "#666", marginTop: 6, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          "{data.activatingIntent}"
        </div>
      )}
      {data.summary && (
        <div style={{ fontSize: 10, color: "#2D7A3B", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          ✓ {data.summary}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: color, width: 8, height: 8 }} />
      <Handle id="bottom" type="source" position={Position.Bottom} style={{ background: color, width: 8, height: 8, opacity: 0.5 }} />
    </div>
  );
});
AgentFlowNode.displayName = "AgentFlowNode";

const ToolFlowNode = React.memo(({ data }) => {
  return (
    <div style={{
      background: "#F5F0FA",
      border: "1.5px solid #7B4FA5",
      borderRadius: 6,
      padding: "5px 9px",
      minWidth: 140,
      fontSize: 11,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    }}>
      <Handle type="target" position={Position.Top} style={{ background: "#7B4FA5", width: 6, height: 6 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          width: 16, height: 16, borderRadius: 8,
          background: "#7B4FA5", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 700,
        }}>⚙</span>
        <span style={{ fontWeight: 600, color: "#4A2E72" }}>{data.name}</span>
      </div>
      {data.result && (
        <div style={{ fontSize: 9, color: "#7B4FA5", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          → {data.result}
        </div>
      )}
    </div>
  );
});
ToolFlowNode.displayName = "ToolFlowNode";

const IntentFlowNode = React.memo(({ data }) => {
  const targetColor = CHAIN_DEMO_AGENT_META[data.target]?.color || "#2D7A3B";
  return (
    <div style={{
      background: "#ECF7ED",
      border: `1.5px solid ${targetColor}`,
      borderRadius: 999,
      padding: "4px 10px",
      fontSize: 10,
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      maxWidth: 220,
    }}>
      <Handle type="target" position={Position.Top} style={{ background: targetColor, width: 6, height: 6 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          fontWeight: 700, color: targetColor,
          fontSize: 10, letterSpacing: 0.5,
        }}>+ {data.target}</span>
        <span style={{
          fontSize: 9, background: "#fff", color: "#666",
          padding: "1px 5px", borderRadius: 3, border: `1px solid ${targetColor}`,
        }}>p={data.priority}</span>
      </div>
      <div style={{ fontSize: 10, color: "#1a1a1a", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        "{data.description}"
      </div>
    </div>
  );
});
IntentFlowNode.displayName = "IntentFlowNode";

const PhaseMarker = React.memo(({ data }) => {
  return (
    <div style={{
      background: data.active ? data.color : "#FAFAFA",
      color: data.active ? "#fff" : "#888",
      padding: "6px 14px",
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 1,
      border: `1px solid ${data.active ? data.color : "#DDD"}`,
      transition: "all 0.3s",
    }}>
      {data.label}
    </div>
  );
});
PhaseMarker.displayName = "PhaseMarker";

const NODE_TYPES = {
  agentFlowNode:  AgentFlowNode,
  toolFlowNode:   ToolFlowNode,
  intentFlowNode: IntentFlowNode,
  phaseMarker:    PhaseMarker,
};

// ─── Tab component ────────────────────────────────────────────────

function ChainAnimationTab() {
  const [stepIdx, setStepIdx] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [speed, setSpeed] = React.useState(1.5);
  const [selectedNodeId, setSelectedNodeId] = React.useState(null);

  const { nodes, edges, queue, completedAgents, narration, phase } = React.useMemo(
    () => buildChainDemoFlow(CHAIN_DEMO_SCENARIO.slice(0, stepIdx)),
    [stepIdx]
  );

  React.useEffect(() => {
    if (!playing) return;
    if (stepIdx >= CHAIN_DEMO_SCENARIO.length) { setPlaying(false); return; }
    const h = setTimeout(() => setStepIdx(i => i + 1), speed * 1000);
    return () => clearTimeout(h);
  }, [playing, stepIdx, speed]);

  function reset() { setPlaying(false); setStepIdx(0); setSelectedNodeId(null); }
  function play()  { if (stepIdx >= CHAIN_DEMO_SCENARIO.length) setStepIdx(0); setPlaying(true); }
  function pause() { setPlaying(false); }
  function next()  { setPlaying(false); setStepIdx(i => Math.min(i + 1, CHAIN_DEMO_SCENARIO.length)); }
  function prev()  { setPlaying(false); setStepIdx(i => Math.max(i - 1, 0)); }

  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 16 }}>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="card-header" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <h3 style={{ margin: 0 }}>View Chain</h3>
          <span className="muted small">Flow-chart demo · read-only</span>
          <span className="muted small" style={{ marginLeft: "auto" }}>step {stepIdx} / {CHAIN_DEMO_SCENARIO.length}</span>
        </div>

        {/* Player controls */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #EEE", display: "flex", alignItems: "center", gap: 8, background: "#FAFAFA" }}>
          <button className="btn sm" onClick={reset} title="Reset">⏮</button>
          <button className="btn sm" onClick={prev} disabled={stepIdx === 0}>◀</button>
          {playing
            ? <button className="btn sm primary" onClick={pause}>⏸ Pause</button>
            : <button className="btn sm primary" onClick={play}>▶ Play</button>}
          <button className="btn sm" onClick={next} disabled={stepIdx >= CHAIN_DEMO_SCENARIO.length}>▶</button>
          <span className="muted small" style={{ marginLeft: 12 }}>Speed:</span>
          <select value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))} style={{ padding: "2px 6px", fontSize: 12 }}>
            <option value="0.5">0.5s</option>
            <option value="1">1s</option>
            <option value="1.5">1.5s</option>
            <option value="2">2s</option>
            <option value="3">3s</option>
          </select>
          <span className="muted small" style={{ marginLeft: "auto", flex: 1, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {narration || "Click ▶ Play to begin."}
          </span>
        </div>

        {/* Phase bar */}
        <div style={{ display: "flex", gap: 6, padding: "10px 16px", borderBottom: "1px solid #EEE", background: "#fff", flexWrap: "wrap" }}>
          {["greeting", "verify", "intake", "tasks", "close"].map((p, i, arr) => {
            const meta = CHAIN_PHASE_META[p];
            const isActive = phase === p;
            const isPast = arr.indexOf(phase) > i;
            return (
              <React.Fragment key={p}>
                <div style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  background: isActive ? meta.color : isPast ? meta.accent : "#F5F5F5",
                  color: isActive ? "#fff" : isPast ? meta.color : "#999",
                  border: `1px solid ${isActive ? meta.color : isPast ? meta.color : "#E0E0E0"}`,
                  transition: "all 0.3s",
                }}>
                  {meta.label}
                </div>
                {i < arr.length - 1 && (
                  <span style={{ color: isPast || isActive ? "#888" : "#D0D0D0", fontSize: 14, alignSelf: "center" }}>→</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ height: 640, background: "#FAFAFA" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.15, duration: 350 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            onNodeClick={(_e, n) => setSelectedNodeId(n.id)}
            onPaneClick={() => setSelectedNodeId(null)}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#E0E0E0" gap={16} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>

      {/* ── Side panel ─────────────────────────────────────── */}
      <div className="card" style={{ padding: 0, position: "sticky", top: 16, alignSelf: "flex-start", maxHeight: "calc(100vh - 80px)", overflow: "auto" }}>
        <div className="card-header" style={{ padding: "12px 16px" }}>
          <h3 style={{ margin: 0 }}>Task Queue</h3>
          <span className="muted small">updates live</span>
        </div>
        <div style={{ padding: 12 }}>
          {queue.length === 0 ? (
            <div className="muted small">empty</div>
          ) : queue.map((q, idx) => {
            const meta = CHAIN_DEMO_AGENT_META[q.target] || { color: "#888" };
            const bg = q.status === "closed" ? "#F4F4F4"
              : q.status === "in_progress" ? "#FFF8DC" : "#ECF7ED";
            const icon = q.status === "closed" ? "✓" : q.status === "in_progress" ? "▶" : "○";
            return (
              <div key={idx} style={{
                display: "flex", alignItems: "baseline", gap: 8,
                padding: "6px 10px", background: bg,
                borderLeft: `3px solid ${meta.color}`, borderRadius: 4,
                marginBottom: 4, fontSize: 12,
                opacity: q.status === "closed" ? 0.65 : 1,
                transition: "all 0.3s",
              }}>
                <span style={{ color: meta.color, fontWeight: 700 }}>{icon}</span>
                <span style={{ fontWeight: 600 }}>{q.target}</span>
                <span className="muted" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>— {q.description}</span>
                {q.priority != null && (
                  <span className="muted" style={{ marginLeft: "auto", fontSize: 10 }}>p={q.priority}</span>
                )}
              </div>
            );
          })}
        </div>

        {selectedNode?.data && (
          <>
            <div className="card-header" style={{ padding: "12px 16px", borderTop: "1px solid #EEE" }}>
              <h3 style={{ margin: 0, fontSize: 13 }}>Selected</h3>
            </div>
            <div style={{ padding: 12, fontSize: 12 }}>
              <div><strong>{selectedNode.data.label || selectedNode.id}</strong></div>
              {selectedNode.data.agentId && <div className="muted small">agent: {selectedNode.data.agentId}</div>}
              {selectedNode.data.activatingIntent && <div className="muted small" style={{ marginTop: 4 }}>activating: "{selectedNode.data.activatingIntent}"</div>}
              {selectedNode.data.summary && <div style={{ marginTop: 4 }}>{selectedNode.data.summary}</div>}
              {selectedNode.data.description && <div style={{ marginTop: 4 }}>"{selectedNode.data.description}"</div>}
              {selectedNode.data.result && <div style={{ marginTop: 4 }}>result: {selectedNode.data.result}</div>}
            </div>
          </>
        )}

        <div className="card-header" style={{ padding: "12px 16px", borderTop: "1px solid #EEE" }}>
          <h3 style={{ margin: 0, fontSize: 13 }}>Completed agents</h3>
        </div>
        <div style={{ padding: 12 }}>
          {completedAgents.length === 0 ? (
            <div className="muted small">none yet</div>
          ) : completedAgents.map((a, idx) => {
            const meta = CHAIN_DEMO_AGENT_META[a.id] || { color: "#888", icon: "●" };
            return (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12 }}>
                <span style={{ fontSize: 14 }}>{meta.icon}</span>
                <span style={{ fontWeight: 600, color: meta.color }}>{a.id}</span>
                <span className="muted" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.summary}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Phase color bar metadata.
const CHAIN_PHASE_META = {
  greeting: { label: "Greeting",  color: "#4A6FA5", accent: "#EAF2F9" },
  verify:   { label: "Verify",    color: "#5A6FA5", accent: "#EEF0F8" },
  intake:   { label: "Intake",    color: "#4AA593", accent: "#E6F7F2" },
  tasks:    { label: "Tasks",     color: "#4A8FA5", accent: "#E6F4F8" },
  close:    { label: "Close",     color: "#555555", accent: "#F0F0F0" },
};

function agentToPhase(agentId) {
  if (agentId === "greeter") return "greeting";
  if (agentId === "verifier") return "verify";
  if (agentId === "intake") return "intake";
  if (agentId === "closer") return "close";
  return "tasks";
}

/**
 * Reduce scenario steps into React Flow custom nodes + edges.
 *
 * Layout strategy:
 *   Main agent row at y=0 (COL_W spacing).
 *   A preempting agent (triage) shifts to y=-160 (above the deferred row)
 *   so the preemption is visually a detour above the main line.
 *   Tool-call sub-nodes stack below their parent agent (y = parentY + 160).
 *   Intent pills hang further below (y = parentY + 240).
 */
function buildChainDemoFlow(steps) {
  const nodes = [];
  const edges = [];
  const queue = [];
  const completedAgents = [];
  let narration = "";
  let phase = "greeting";

  const COL_W = 260;
  const AGENT_Y = 0;
  const PREEMPT_Y = -180;
  const TOOL_Y_OFFSET = 120;
  const INTENT_Y_OFFSET = 210;

  let mainCol = 0;             // x-column counter for main-row nodes
  let preemptCol = 0;           // separate counter so preempt row advances independently
  let lastMainAgentNodeId = null;
  let activeNodeId = null;
  let deferredNodeId = null;    // the deferred scheduling node we need to resume later
  let inPreemptBranch = false;  // are we currently on the preempt row?

  const agentNodeCounts = {};   // per-agent tool sub-node counter (for stacking)

  function addAgentNode(agentId, activatingIntent, { x, y }, status) {
    const meta = CHAIN_DEMO_AGENT_META[agentId] || { color: "#888", icon: "●" };
    const id = `agent-${nodes.length}-${agentId}`;
    nodes.push({
      id,
      type: "agentFlowNode",
      position: { x, y },
      data: {
        label: agentId,
        agentId,
        activatingIntent,
        color: meta.color,
        icon: meta.icon,
        status,
      },
    });
    return id;
  }

  function addToolNode(parentId, parentPos, name, result) {
    agentNodeCounts[parentId] = (agentNodeCounts[parentId] || 0) + 1;
    const offsetY = TOOL_Y_OFFSET + (agentNodeCounts[parentId] - 1) * 46;
    const id = `tool-${nodes.length}`;
    nodes.push({
      id,
      type: "toolFlowNode",
      position: { x: parentPos.x + 20, y: parentPos.y + offsetY },
      data: { name, result },
    });
    edges.push({
      id: `e-${edges.length}`,
      source: parentId, sourceHandle: "bottom",
      target: id,
      style: { stroke: "#7B4FA5", strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#7B4FA5" },
    });
  }

  function addIntentNode(parentId, parentPos, description, target, priority) {
    const id = `intent-${nodes.length}`;
    nodes.push({
      id,
      type: "intentFlowNode",
      position: { x: parentPos.x + 30, y: parentPos.y + INTENT_Y_OFFSET + (agentNodeCounts[parentId] || 0) * 40 },
      data: { description, target, priority },
    });
    edges.push({
      id: `e-${edges.length}`,
      source: parentId, sourceHandle: "bottom",
      target: id,
      style: { stroke: "#2D7A3B", strokeWidth: 1.5, strokeDasharray: "4 3" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#2D7A3B" },
    });
  }

  function addDispatchEdge(fromId, toId, reason, kind = "normal") {
    const palette = {
      normal:  { stroke: "#2E4F72", label: "#2E4F72" },
      preempt: { stroke: "#B84A4A", label: "#B84A4A" },
      resume:  { stroke: "#2D7A3B", label: "#2D7A3B" },
    }[kind] || { stroke: "#2E4F72", label: "#2E4F72" };
    edges.push({
      id: `e-${edges.length}`,
      source: fromId,
      target: toId,
      label: reason,
      labelStyle: { fontSize: 10, fill: palette.label, fontWeight: 600 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.95 },
      style: {
        stroke: palette.stroke,
        strokeWidth: 2,
        ...(kind !== "normal" ? { strokeDasharray: "6 4" } : {}),
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: palette.stroke },
      animated: kind !== "normal",
    });
  }

  // Track last-node-per-agent (for finding targets during resume).
  const lastByAgent = {};
  // Remember which preempting branch the active node is on (for resume edge).
  let lastPreemptNodeId = null;

  for (const ev of steps) {
    if (ev.t === "activate") {
      phase = agentToPhase(ev.agent);

      let pos, newId;
      if (inPreemptBranch) {
        // A new agent activates WHILE in the preempt branch (e.g. triage).
        pos = { x: preemptCol * COL_W, y: PREEMPT_Y };
        newId = addAgentNode(ev.agent, ev.intent, pos, "active");
        // Edge from the deferred main-row agent to this preempt-row agent.
        if (deferredNodeId) {
          addDispatchEdge(deferredNodeId, newId, "preempt", "preempt");
        }
        preemptCol++;
        lastPreemptNodeId = newId;
      } else if (deferredNodeId && ev.agent === nodes.find(n => n.id === deferredNodeId)?.data.agentId) {
        // Resume of the deferred agent — create a NEW node (new session)
        // to the right of the last preempt branch, back on the main row.
        pos = { x: mainCol * COL_W, y: AGENT_Y };
        newId = addAgentNode(ev.agent, ev.intent, pos, "active");
        // Resume edge from the last preempt node back down.
        if (lastPreemptNodeId) {
          addDispatchEdge(lastPreemptNodeId, newId, "resume", "resume");
        } else if (lastMainAgentNodeId) {
          addDispatchEdge(lastMainAgentNodeId, newId, "→", "normal");
        }
        mainCol++;
        lastMainAgentNodeId = newId;
        deferredNodeId = null;
        lastPreemptNodeId = null;
      } else {
        // Normal next agent on the main row.
        pos = { x: mainCol * COL_W, y: AGENT_Y };
        newId = addAgentNode(ev.agent, ev.intent, pos, "active");
        if (lastMainAgentNodeId) {
          addDispatchEdge(lastMainAgentNodeId, newId, "→", "normal");
        }
        mainCol++;
        lastMainAgentNodeId = newId;
      }

      // Mark active / queue state
      activeNodeId = newId;
      lastByAgent[ev.agent] = newId;
      for (const q of queue) {
        if (q.target === ev.agent && q.status === "open") { q.status = "in_progress"; break; }
      }
      narration = `→ ${ev.agent} activated${ev.intent ? ` for "${ev.intent}"` : ""}`;
    } else if (ev.t === "speak") {
      narration = `${ev.role === "patient" ? "Patient" : ev.agent || "Agent"}: "${(ev.text || "").slice(0, 90)}${(ev.text || "").length > 90 ? "…" : ""}"`;
    } else if (ev.t === "tool_call") {
      const parent = nodes.find(n => n.id === activeNodeId);
      if (parent) addToolNode(activeNodeId, parent.position, ev.name, ev.result);
      narration = `${ev.agent} ⚙ ${ev.name} → ${ev.result}`;
    } else if (ev.t === "enqueue") {
      queue.push({ description: ev.description, target: ev.target, priority: ev.priority, status: "open" });
      const parent = nodes.find(n => n.id === activeNodeId);
      if (parent) addIntentNode(activeNodeId, parent.position, ev.description, ev.target, ev.priority);
      narration = `+ queued "${ev.description}" → ${ev.target} (p=${ev.priority})`;
    } else if (ev.t === "defer") {
      // The currently-active agent (on the main row) becomes deferred.
      const node = nodes.find(n => n.id === activeNodeId);
      if (node) {
        node.data = { ...node.data, status: "deferred" };
        deferredNodeId = node.id;
      }
      for (const q of queue) {
        if (q.target === ev.agent && q.status === "in_progress") { q.status = "open"; break; }
      }
      inPreemptBranch = true;
      narration = `⏸ ${ev.agent} deferred (${ev.reason})`;
    } else if (ev.t === "resume") {
      inPreemptBranch = false;
      narration = `↻ resuming ${ev.agent}`;
    } else if (ev.t === "dispatch") {
      narration = `orchestrator: ${ev.from} → ${ev.to} (${ev.reason})`;
    } else if (ev.t === "complete") {
      const id = lastByAgent[ev.agent];
      const node = nodes.find(n => n.id === id);
      if (node) {
        node.data = { ...node.data, status: "completed", summary: ev.summary };
      }
      completedAgents.push({ id: ev.agent, summary: ev.summary });
      for (const q of queue) {
        if (q.target === ev.agent && q.status === "in_progress") { q.status = "closed"; break; }
      }
      narration = `✓ ${ev.agent} completed`;
    }
  }

  return { nodes, edges, queue, completedAgents, narration, phase };
}
